import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import SearchSelect from "./SearchSelect";
import DraggableModal from "./DraggableModal";
import FileDropZone from "./FileDropZone";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import { printShipmentDocs, buildShipmentDocPdf } from "./ShipmentRegistry";
import { QualityReportPreview } from "./qualityReport";
import EmailDialog from "./EmailDialog";
import { loadCompanyProfile } from "./CompanyProfile";
import { computeCertUsage, nrmCertNum, extractCommodity } from "./exportCertUsage";
import { loadFromSupabase, saveToSupabase, deleteFromSupabase, getCached } from "../supabaseDB";
import { supabase } from "../supabaseClient";
import { uploadFileToStorage, removeFromStorage } from "../storageImages";
import TrackingLinkModal from "./TrackingLinkModal";
import JSZip from "jszip";

/* Bucket where ALL shipper-portal upload slots live (embarque invoice
   PDF, export cert PDF, "other" docs, per-proforma pallet photos and
   docs). Provisioned via SQL — see the comment block at the top of
   src/supabaseSchema.sql for the bucket-create + RLS statements. Files
   never round-trip as base64 through `shipper_shipments`; we store
   only the storage `path` + a resolved `publicUrl` on the embarque /
   proforma object. That keeps row payloads small (a few hundred
   bytes per file regardless of the underlying PDF size), bypasses
   the stripBlobs JSONB pipeline entirely, and ships through the same
   Storage architecture already proven on `product-labels`. */
const SHIPMENT_DOCS_BUCKET = "shipment-docs";

/* Sanitize a free-text id (supplier code, embarque id) for use as a
   Storage path segment. Storage keys must be ascii-safe slashes-and-
   alphanumeric; embarque ids are usually Date.now() integers (safe)
   but supplier `shortName` / legacy ids can carry spaces or accents. */
function safePathSegment(s) {
  return String(s == null ? "" : s)
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/^_+|_+$/g, "")
    || "_";
}

/* Pick a file extension from a File's mime type / filename. Storage
   doesn't care about the extension semantically but a friendly one
   makes the browser's Content-Disposition work without surprises. */
function extFromFile(file) {
  var t = (file && file.type ? String(file.type) : "").toLowerCase();
  if (t.includes("pdf"))  return "pdf";
  if (t.includes("png"))  return "png";
  if (t.includes("jpeg") || t.includes("jpg")) return "jpg";
  if (t.includes("webp")) return "webp";
  if (t.includes("gif"))  return "gif";
  if (t.includes("msword") || t.includes("wordprocessingml")) return "docx";
  if (t.includes("excel") || t.includes("spreadsheetml")) return "xlsx";
  if (t.includes("plain")) return "txt";
  if (t.includes("csv"))   return "csv";
  /* Fall back to the original filename's extension if mime is missing
     or generic (e.g. "application/octet-stream"). */
  var name = file && file.name ? String(file.name) : "";
  var m = name.match(/\.([a-zA-Z0-9]{1,8})$/);
  return m ? m[1].toLowerCase() : "bin";
}

/* ============================================================
   SHIPPER PORTAL — Shipments tab

   Data model (per spec): one row in gfi-shipper-shipments
   represents an EMBARQUE (a shipper invoice). Each embarque can
   carry multiple proformas; each proforma is what ultimately
   becomes a GFI shipment with its own lot.

     embarque  ──┬─ proforma A → accepted → gfi-shipments LOT 58030
                 ├─ proforma B → submitted (pending Arturo)
                 └─ proforma C → draft

   Proforma state machine: draft → submitted → accepted | rejected
   (rejected can be edited and resubmitted by the shipper).

   The "logged-in" shipper is simulated as the first supplier
   whose supplierType is "shipper" (per spec).
   ============================================================ */

const STORAGE_KEY = "gfi-shipper-shipments";
const MAX_PALLETS = 30;
const MAX_LINES = 4;
const STATUSES = ["draft", "submitted", "accepted", "rejected"];

const STATUS_COLORS = {
  draft:     { bg: "rgba(107,114,128,0.12)", text: "#374151", border: "rgba(107,114,128,0.3)" },
  submitted: { bg: "rgba(59,130,246,0.12)",  text: "#1D4ED8", border: "rgba(59,130,246,0.3)" },
  accepted:  { bg: "rgba(16,185,129,0.12)",  text: "#047857", border: "rgba(16,185,129,0.3)" },
  rejected:  { bg: "rgba(239,68,68,0.12)",   text: "#B91C1C", border: "rgba(239,68,68,0.3)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to this
   file's own submission-status badge and its read-only Texas QC tier
   mirror. */
const STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";

const T = {
  es: {
    title: "Embarques · Portal del Exportador", subtitle: "Captura embarques y proformas para enviar a GFI",
    loggedAs: "Conectado como", newSubmission: "Nuevo Embarque",
    cardDraft: "Borradores", cardSubmitted: "Enviadas", cardAccepted: "Aceptadas", cardRejected: "Rechazadas",
    search: "Buscar por factura, consecutivo, lote...", filterStatus: "Todos los estados", allCerts: "Todos los certificados",
    colEmb: "Embarque", colInvoice: "Factura del Exportador", colProformas: "Proformas", colProducts: "Productos",
    colPallets: "Pallets", colCases: "Cajas", colStatus: "Estado", colDate: "Fecha",
    colProforma: "Proforma", colDeparture: "Salida", colEta: "ETA", colWarehouse: "Bodega", colGfiLot: "Lote GFI", colEntry: "Entry #",
    noResults: "Sin embarques aún. Crea el primero con el botón superior.",
    noResultsFiltered: "Ninguna proforma coincide con los filtros.",
    statusDraft: "Borrador", statusSubmitted: "Enviada", statusAccepted: "Aceptada", statusRejected: "Rechazada",
    /* Embarque modal */
    embTitle: "Embarque", consecEmb: "Consec. Embarque", shipperInvoice: "Factura del Exportador", shipperInvoiceReq: "Factura del Exportador *",
    invoiceFile: "Archivo de Factura (PDF/imagen) *", invoiceFileHint: "Obligatorio para enviar cualquier proforma",
    exportCertFile: "Certificado de Exportación (archivo)", exportCertFileHint: "Un archivo para todo el embarque",
    otherDocs: "Otros Documentos", uploadBtn: "Subir...", uploadedFiles: "Archivos subidos",
    otherDocsHint: "Certificados, fotos extra, comprobantes — múltiples archivos.",
    shipmentDocsSub: "Documentos del Embarque", shipmentDocsHint: "Compartidos · visibles en todas las proformas",
    proformaDocsSub: "Documentos de la Proforma", proformaDocsHint: "Solo para esta proforma",
    shipmentDocBadge: "Doc. del embarque", proformaDocBadge: "Doc. de la proforma",
    consignee: "Consignatario", consigneeHint: "Datos fijos asignados por GFI · solo lectura",
    consigneeMissing: "Consignatario no configurado. Contacta a GFI.",
    brokerUSLocked: "Default asignado por GFI · solo lectura",
    brokerUSMissing: "GFI no ha asignado un agente US default. Contacta a GFI.",
    brokerMXNone: "GFI no te ha asignado agentes MX. Contacta a GFI o crea uno propio en Customs Brokers.",
    productsRestricted: "Solo productos autorizados por GFI están disponibles.",
    productsUnrestricted: "(GFI aún no ha definido productos autorizados — todos están disponibles)",
    noCertsAvailable: "Sin certificados disponibles",
    declaredValue: "Valor Declarado", declaredValueHint: "Solo para documentos de exportación (no es precio de venta)",
    proformasIn: "Proformas en este embarque", addProforma: "+ Agregar Proforma",
    /* Proforma form */
    proforma: "Proforma", consec: "Consec. Proforma", proformaNumber: "Nº Proforma",
    departureDate: "Fecha de Salida", arrivalDate: "Fecha Estimada de Llegada (ETA)",
    warehouse: "Bodega Destino", brokerMX: "Agente Aduanal MX", brokerUS: "Agente Aduanal US",
    selectBroker: "Seleccionar agente...", selectWarehouse: "Seleccionar bodega...",
    selectCert: "Seleccionar certificado...", selectProduct: "Seleccionar producto...",
    selectPresentation: "Seleccionar descripción...",
    productLines: "Líneas de Producto", productLinesHint: "Hasta " + MAX_LINES + " productos",
    product: "Producto", presentation: "Descripción del Producto", category: "Cat",
    pallets: "Pallets", cases: "Cajas", certShort: "Cert #", exportCert: "Certificado de Exportación",
    certManualPlaceholder: "Número manual...",
    palletManifest: "Manifiesto de Pallets", palletManifestHint: "Máx " + MAX_PALLETS,
    palletNum: "Pallet #", tagId: "Tag ID", casesPallet: "Cajas/Pallet", julianCode: "Trazabilidad (Julian)", growerCode: "Cód. Productor",
    palletCondition: "Condición", palletPack: "Empaque", palletPresentation: "Descripción del Producto", palletUnitsCase: "Unid/Caja",
    palletLabel: "Etiqueta", palletVariety: "Variedad", palletWeight: "Peso",
    texasQcHeader: "Reporte QC Texas", texasQuality: "Calidad", texasCondition: "Condición", texasSevereCondition: "Condición Severa",
    texasQcExcellent: "Excelente", texasQcVeryGood: "Muy Bueno", texasQcGoodQuality: "Buena Calidad", texasQcFairQuality: "Calidad Aceptable", texasQcGood: "Bueno", texasQcRegular: "Regular", texasQcBadQuality: "Mala Calidad", texasQcPoor: "Malo", texasQcPoorQuality: "Calidad Pobre",
    qcDefects: "Defectos",
    palletCondFresh: "Fresco", palletCondFreshCut: "Cortado", palletCondFrozen: "Congelado",
    packClamshell: "Clamshell", packBulk: "Granel", packBag: "Bolsa", packCasePack: "Caja",
    addPallet: "+ Agregar pallet",
    /* Transport labels MUST match ShipmentRegistry's keys exactly
       so data submitted from the portal maps 1:1 into Arturo's
       shipment record. Field keys: carrier / driver / phone /
       econTruck / platesTruck / econTrailer / platesTrailer / caat
       / codigoAlpha / sealNumber / thermograph. */
    transport: "Datos del Transportista", transportSub: "Transportista y datos del camión",
    carrier: "Línea transportista", driver: "Nombre del chofer",
    platesTruck: "Placas del Tractor", econTruck: "No. Económico Tractor",
    platesTrailer: "Placas de la Caja", econTrailer: "No. Económico Caja",
    caat: "CAAT", codigoAlpha: "Código Alfa", phone: "📱 Celular",
    sealNumber: "No. de Candado", thermograph: "No. de Termógrafo",
    truckInfo: "Datos del Tracto", trailerInfo: "Datos de la Caja",
    needTransport: "Faltan datos de transporte obligatorios: Chofer, Placas Tracto, No. Económico Tracto, Código Alfa.",
    needManifest: "Captura al menos 1 pallet en el Manifiesto de Pallets.",
    needManifestMatch: "Las cajas del Manifiesto no coinciden con las líneas de producto:",
    printDocs: "Imprimir Documentos", printCartaPorte: "Bill of Lading", printCartaInstr: "Instrucciones de Embarque",
    sendTrackingLink: "Enviar Link de Rastreo",
    printNeedsData: "Captura productos y datos de transporte antes de imprimir.",
    printPopupBlocked: "El navegador bloqueó la ventana de impresión. Permítela y reintenta.",
    selected: "seleccionadas", clearSel: "Limpiar selección",
    bulkPrintNone: "Ninguna de las proformas seleccionadas tiene datos suficientes para imprimir.",
    /* Collapsible-section titles mirror ShipmentRegistry's labels. */
    secHeader: "Datos del Embarque", secTransport: "Datos del Transportista", secProducts: "Productos",
    secPallets: "Manifiesto de Pallets", secDocs: "Documentos", secNotes: "Notas",
    weekShort: "Sem",
    clearForm: "Limpiar Formulario",
    confirmClear: "¿Limpiar todos los campos del formulario? Los datos no guardados se perderán.",
    confirmLoseManifestRows: "Se eliminarán {n} fila(s) del Manifiesto de Pallets que ya tienen datos capturados. ¿Continuar?",
    palletCountMismatch: "Los pallets del Manifiesto ({m}) no coinciden con los pallets en Líneas de Producto ({l}).",
    duplicatePalletNum: "Pallet # duplicado",
    palletPhotos: "Fotos de Pallets Cargados",
    notes: "Notas", notesPlaceholder: "Notas para GFI...",
    rejectionReason: "Razón del Rechazo",
    /* actions */
    save: "Guardar", submit: "Enviar Proforma a GFI", resubmit: "Reenviar Proforma",
    cancel: "Cancelar", close: "Cerrar", del: "Eliminar Embarque", delProforma: "Eliminar Proforma",
    gfiLotAssigned: "Lote GFI",
    confirmDeleteEmb: "¿Eliminar este embarque y TODAS sus proformas?",
    confirmDeleteProforma: "¿Eliminar esta proforma?",
    confirmSubmit: "Una vez enviada no podrás editar esta proforma. ¿Continuar?",
    needInvoice: "Sube el archivo de la factura del shipper (a nivel de embarque) para poder enviar.",
    needLine: "Captura al menos 1 línea de producto con descripción y cantidades en esta proforma.",
    needInvoiceNumber: "Captura el número de factura del shipper.",
    submitted: "Cambios guardados", sent: "Proforma enviada a GFI", deleted: "Eliminado",
    readonlyPro: "Solo lectura — esperando revisión de GFI", readonlyAcc: "Solo lectura — proforma aceptada",
    fixAndResubmit: "Corrige y reenvía para que GFI revise nuevamente.",
    incomplete: "INCOMPLETO", missingDocs: "Falta factura",
    embCantDelete: "No puedes eliminar — hay proformas aceptadas o enviadas.",
    emailBol: "Email BOL",
    emailBolPickerTitle: "Selecciona embarque para enviar BOL",
    emailBolPickerEmpty: "No hay embarques disponibles.",
    emailBolPickerSearch: "Buscar factura, proforma...",
    emailBolPickerCancel: "Cancelar",
    emailBolTitle: "Enviar Bill of Lading",
    emailBolSubjectShipmentPrefix: "Embarque Ground-Fresh",
    emailBolDocLabel: "Bill of Lading — Proforma",
    emailBolGreeting: "Estimados,",
    emailBolIntro: "adjunto Bill of Lading del embarque",
    emailBolSignoff: "Saludos",
    emailBolPallets: "pallets",
    emailBolCases: "cajas",
    emailBolRecipientAdmin: "Ground Fresh (Admin)",
    emailBolRecipientUSBroker: "Agente Aduanal US",
    emailBolRecipientMXBroker: "Agente Aduanal MX",
    emailBolNoReady: "Ninguna proforma del embarque tiene datos suficientes para generar el BOL.",
  },
  en: {
    title: "Shipments · Shipper Portal", subtitle: "Capture shipments and proformas to submit to GFI",
    loggedAs: "Logged in as", newSubmission: "New Shipment",
    cardDraft: "Drafts", cardSubmitted: "Submitted", cardAccepted: "Accepted", cardRejected: "Rejected",
    search: "Search by invoice, consec, lot...", filterStatus: "All statuses", allCerts: "All certificates",
    colEmb: "Shipment", colInvoice: "Shipper Invoice", colProformas: "Proformas", colProducts: "Products",
    colPallets: "Pallets", colCases: "Cases", colStatus: "Status", colDate: "Date",
    colProforma: "Proforma", colDeparture: "Departure", colEta: "ETA", colWarehouse: "Warehouse", colGfiLot: "GFI Lot", colEntry: "Entry #",
    noResults: "No shipments yet. Create the first with the button above.",
    noResultsFiltered: "No proforma matches the filters.",
    statusDraft: "Draft", statusSubmitted: "Submitted", statusAccepted: "Accepted", statusRejected: "Rejected",
    embTitle: "Shipment", consecEmb: "Shipment #", shipperInvoice: "Shipper Invoice", shipperInvoiceReq: "Shipper Invoice *",
    invoiceFile: "Invoice File (PDF/image) *", invoiceFileHint: "Required to submit any proforma",
    exportCertFile: "Export Certificate (file)", exportCertFileHint: "One file for the whole shipment",
    otherDocs: "Other Documents", uploadBtn: "Upload...", uploadedFiles: "Uploaded files",
    otherDocsHint: "Certificates, extra photos, receipts — multiple files.",
    shipmentDocsSub: "Shipment Documents", shipmentDocsHint: "Shared · visible across all proformas",
    proformaDocsSub: "Proforma Documents", proformaDocsHint: "Only for this proforma",
    shipmentDocBadge: "Shipment doc", proformaDocBadge: "Proforma doc",
    consignee: "Consignee", consigneeHint: "Fixed values assigned by GFI · read-only",
    consigneeMissing: "Consignee not configured. Contact GFI.",
    brokerUSLocked: "Default set by GFI · read-only",
    brokerUSMissing: "GFI hasn't set a default US broker. Contact GFI.",
    brokerMXNone: "GFI hasn't assigned MX brokers to you. Contact GFI or create one in Customs Brokers.",
    productsRestricted: "Only products authorized by GFI are available.",
    productsUnrestricted: "(GFI hasn't defined authorized products yet — all are available)",
    noCertsAvailable: "No certificates available",
    declaredValue: "Declared Value", declaredValueHint: "Export documents only (not a sale price)",
    proformasIn: "Proformas in this shipment", addProforma: "+ Add Proforma",
    proforma: "Proforma", consec: "Proforma #", proformaNumber: "Proforma No.",
    departureDate: "Departure Date", arrivalDate: "Estimated Arrival (ETA)",
    warehouse: "Destination Warehouse", brokerMX: "Mexican Customs Broker", brokerUS: "US Customs Broker",
    selectBroker: "Select broker...", selectWarehouse: "Select warehouse...",
    selectCert: "Select certificate...", selectProduct: "Select product...",
    selectPresentation: "Select description...",
    productLines: "Product Lines", productLinesHint: "Up to " + MAX_LINES,
    product: "Product", presentation: "Product Description", category: "Cat",
    pallets: "Pallets", cases: "Cases", certShort: "Cert #", exportCert: "Export Certificate",
    certManualPlaceholder: "Manual number...",
    palletManifest: "Pallet Manifest", palletManifestHint: "Max " + MAX_PALLETS,
    palletNum: "Pallet #", tagId: "Tag ID", casesPallet: "Cases/Pallet", julianCode: "Traceability (Julian)", growerCode: "Grower Code",
    palletCondition: "Condition", palletPack: "Pack", palletPresentation: "Product Description", palletUnitsCase: "Units/Case",
    palletLabel: "Label", palletVariety: "Variety", palletWeight: "Weight",
    texasQcHeader: "Texas QC Report", texasQuality: "Quality", texasCondition: "Condition", texasSevereCondition: "Severe Condition",
    texasQcExcellent: "Excellent", texasQcVeryGood: "Very Good", texasQcGoodQuality: "Good Quality", texasQcFairQuality: "Fair Quality", texasQcGood: "Good", texasQcRegular: "Regular", texasQcBadQuality: "Bad Quality", texasQcPoor: "Poor", texasQcPoorQuality: "Poor Quality",
    qcDefects: "Defects",
    palletCondFresh: "Fresh", palletCondFreshCut: "Fresh Cut", palletCondFrozen: "Frozen",
    packClamshell: "Clamshell", packBulk: "Bulk", packBag: "Bag", packCasePack: "Case Pack",
    addPallet: "+ Add pallet",
    transport: "Transport Details", transportSub: "Required", carrier: "Carrier",
    driver: "Driver Name", platesTruck: "Truck Plates", econTruck: "Truck Number",
    platesTrailer: "Trailer Plates", econTrailer: "Trailer Number", caat: "CAAT", codigoAlpha: "Alpha Code",
    phone: "📱 Cell Phone", sealNumber: "Seal", thermograph: "Temp Recorder #",
    needTransport: "Missing required transport fields: Driver Name, Truck Plates, Truck Econ #, Alpha Code.",
    needManifest: "Add at least 1 pallet in the Pallet Manifest.",
    needManifestMatch: "Pallet Manifest cases don't match the product lines:",
    printDocs: "Print Documents", printCartaPorte: "Bill of Lading", printCartaInstr: "Shipping Instructions",
    sendTrackingLink: "Send Tracking Link",
    printNeedsData: "Add products and transport details before printing.",
    printPopupBlocked: "The browser blocked the print window. Allow popups and try again.",
    selected: "selected", clearSel: "Clear selection",
    bulkPrintNone: "None of the selected proformas have enough data to print.",
    secHeader: "Shipment Header", secTransport: "Carrier Info", secProducts: "Products",
    secPallets: "Pallet Manifest", secDocs: "Documents", secNotes: "Notes",
    weekShort: "W",
    clearForm: "Clear Form",
    confirmClear: "Clear all form fields? Unsaved data will be lost.",
    confirmLoseManifestRows: "{n} Pallet Manifest row(s) with captured data will be removed. Continue?",
    palletCountMismatch: "Pallet Manifest ({m}) doesn't match the pallet count in Product Lines ({l}).",
    duplicatePalletNum: "Duplicate Pallet #",
    palletPhotos: "Loaded Pallet Photos",
    notes: "Notes", notesPlaceholder: "Notes to GFI...",
    rejectionReason: "Rejection Reason",
    save: "Save", submit: "Submit Proforma to GFI", resubmit: "Resubmit Proforma",
    cancel: "Cancel", close: "Close", del: "Delete Shipment", delProforma: "Delete Proforma",
    gfiLotAssigned: "GFI Lot",
    confirmDeleteEmb: "Delete this shipment and ALL its proformas?",
    confirmDeleteProforma: "Delete this proforma?",
    confirmSubmit: "Once submitted you can't edit this proforma. Continue?",
    needInvoice: "Upload the shipper invoice file (shipment-level) to submit.",
    needLine: "Capture at least 1 product line with description and quantities in this proforma.",
    needInvoiceNumber: "Enter the shipper invoice number.",
    submitted: "Changes saved", sent: "Proforma submitted to GFI", deleted: "Deleted",
    readonlyPro: "Read-only — awaiting GFI review", readonlyAcc: "Read-only — proforma accepted",
    fixAndResubmit: "Fix and resubmit so GFI reviews again.",
    incomplete: "INCOMPLETE", missingDocs: "Invoice missing",
    embCantDelete: "Can't delete — there are accepted or submitted proformas.",
    emailBol: "Email BOL",
    emailBolPickerTitle: "Pick a shipment to email its BOL",
    emailBolPickerEmpty: "No shipments available.",
    emailBolPickerSearch: "Search invoice, proforma...",
    emailBolPickerCancel: "Cancel",
    emailBolTitle: "Send Bill of Lading",
    emailBolSubjectShipmentPrefix: "Ground-Fresh Shipment",
    emailBolDocLabel: "Bill of Lading — Proforma",
    emailBolGreeting: "Hi,",
    emailBolIntro: "attached is the Bill of Lading for shipment",
    emailBolSignoff: "Regards",
    emailBolPallets: "pallets",
    emailBolCases: "cases",
    emailBolRecipientAdmin: "Ground Fresh (Admin)",
    emailBolRecipientUSBroker: "US Customs Broker",
    emailBolRecipientMXBroker: "MX Customs Broker",
    emailBolNoReady: "No proforma in this shipment has enough data to generate the BOL.",
  },
};

/* ---------- BOL bulk-download helpers ---------- */
/* Strip everything Windows / macOS / Linux file systems refuse,
   collapse whitespace, cap length so the zip name stays readable. */
function sanitizeBolFilename(name) {
  return String(name || "")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .substring(0, 120) || "BOLs";
}
/* Build each row's BOL PDF, bundle into a single ZIP, and trigger
   a download. `rows` is an array of { embarque, pro } shapes (the
   same shape the table's filteredRows produces, and what the modal
   passes after mapping its allReady proformas through f). Each PDF
   is named after the proforma's consec to avoid collisions when
   multiple proformas share an invoice. */
async function downloadBolsAsZip(rows, ctx, zipBaseName) {
  var zip = new JSZip();
  var seen = {};
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i];
    var synth = buildSyntheticShipment(row.embarque, row.pro);
    var att = await buildShipmentDocPdf(synth, "carta-porte", ctx);
    var fname = sanitizeBolFilename(att && att.filename ? att.filename : ("BOL_" + (row.pro.consec || (i + 1))));
    if (!/\.pdf$/i.test(fname)) fname += ".pdf";
    /* De-dupe by suffixing when two PDFs would collide on filename. */
    if (seen[fname]) {
      var stem = fname.replace(/\.pdf$/i, "");
      var n = 2;
      while (seen[stem + "_" + n + ".pdf"]) n++;
      fname = stem + "_" + n + ".pdf";
    }
    seen[fname] = true;
    zip.file(fname, att.blob);
  }
  var blob = await zip.generateAsync({ type: "blob" });
  var url = URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = sanitizeBolFilename(zipBaseName) + ".zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
}

/* ---------- Storage helpers ---------- */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function getSuppliers()  { return loadLS("gfi-suppliers", []); }
function getProducts()   { return loadLS("gfi-products", []); }
/* Arturo, 2026-09-16: "tampoco muestra el MC custom broker" (and,
   before that, the consignee below) — both came up empty specifically
   when the Shipper Portal is opened via GFI's own admin mirror.
   loadFromSupabase() (supabaseDB.js) never writes its result to
   localStorage — it only fills an in-memory cache — so these two
   helpers, which read the localStorage mirror ONLY, stayed empty in
   any tab where nothing else had ever separately written
   gfi-warehouses/gfi-brokers there. loadCompanyProfile() (already
   used elsewhere in this file) already solves this the right way:
   prefer the fresh in-memory Supabase cache (getCached), fall back to
   the localStorage mirror only when that cache hasn't been primed
   yet. Applying the same pattern here so every caller of
   getWarehouses()/getBrokers() in this file benefits, not just the
   two spots that were reported broken. */
function getWarehouses() {
  var cached = getCached("warehouses");
  if (cached && cached.length > 0) return cached;
  return loadLS("gfi-warehouses", []);
}
function getBrokers() {
  var cached = getCached("brokers");
  if (cached && cached.length > 0) return cached;
  return loadLS("gfi-brokers", []);
}
function getCerts()      { return loadLS("gfi-export-certs", []); }
function getSeasons()    { return loadLS("gfi-seasons", []); }

function getCurrentShipper() {
  var sups = getSuppliers();
  return sups.find(function (s) { return s.supplierType === "shipper"; }) || sups[0] || null;
}

function seasonForDate(dateStr) {
  if (!dateStr) return null;
  var d = new Date(dateStr + "T00:00:00");
  var ss = getSeasons();
  return ss.find(function (s) { return s.startDate && s.endEstimate && new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); }) || null;
}
function activeSeason() {
  var today = new Date().toISOString().substring(0, 10);
  return seasonForDate(today);
}
function seasonLabel(s) {
  if (!s) return "—";
  return s.seasonName || (s.startDate || "").substring(0, 4) + "-" + (s.endEstimate || "").substring(2, 4);
}

/* ISO-8601 week number — same algorithm Arturo's ShipmentRegistry
   uses for the Week column / W## badge. */
function getISOWeekNum(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}

/* Embarque (invoice-level) consecutive — "{INITIALS}-E001". Resets
   per season for the same shipper. */
function nextEmbConsec(embarques, shipper, season) {
  if (!shipper) return "??-E001";
  var initials = (shipper.initials || (shipper.shortName || "S").substring(0, 2)).toUpperCase();
  if (!season) return initials + "-E001";
  var sid = String(season.id);
  var used = (embarques || [])
    .filter(function (e) { return String(e.supplierId) === String(shipper.id) && String(e.seasonId) === sid; })
    .map(function (e) { var m = /-E(\d+)$/.exec(String(e.consecEmbarque || "")); return m ? parseInt(m[1], 10) : 0; });
  var next = (used.length === 0 ? 0 : Math.max.apply(null, used)) + 1;
  return initials + "-E" + String(next).padStart(3, "0");
}

/* Proforma consecutive — "{INITIALS}-001". Counts ALL proformas
   across ALL embarques of this shipper in the current season so
   numbering is sequential across the whole season. */
function nextProConsec(embarques, shipper, season) {
  if (!shipper) return "??-001";
  var initials = (shipper.initials || (shipper.shortName || "S").substring(0, 2)).toUpperCase();
  if (!season) return initials + "-001";
  var sid = String(season.id);
  var used = [];
  (embarques || []).forEach(function (e) {
    if (String(e.supplierId) !== String(shipper.id)) return;
    (e.proformas || []).forEach(function (p) {
      if (String(p.seasonId) !== sid) return;
      var m = /-(\d+)$/.exec(String(p.consec || ""));
      if (m) used.push(parseInt(m[1], 10));
    });
  });
  var next = (used.length === 0 ? 0 : Math.max.apply(null, used)) + 1;
  return initials + "-" + String(next).padStart(3, "0");
}

/* Per spec: dropdowns do NOT filter brokers by country — all
   brokers from gfi-brokers are shown so the shipper can find any
   broker they registered or that Arturo registered. */
function allBrokers() { return getBrokers(); }

/* Per-shipper settings (gfi-shipper-settings keyed by supplierId).
   Used to enforce: authorized products, assigned MX brokers,
   default US broker. Returns an empty record if nothing configured
   so callers can safely read defaultUSBroker / assignedBrokers /
   authorizedProducts without null checks. */
function getShipperSettings(supplierId) {
  if (!supplierId) return { authorizedProducts: [], assignedBrokers: [], defaultUSBroker: null, permissions: {} };
  var all = loadLS("gfi-shipper-settings", {}) || {};
  /* Direct hit — the common path when the caller uses the same id
     shape ShipperPortalShell wrote with. */
  var rec = all[supplierId];
  /* Fallback: scan every entry and match by any embedded id field.
     ShipperPortalShell indexes the map under multiple keys (uuid,
     supplier_id, legacy_id, etc.) but a caller asking with the
     "other" id shape would still miss when those keys weren't all
     populated. Walking the values catches the remaining cases —
     e.g. a portal-promoted shipper carries _uuid in the record body
     but the caller passes the legacy id. */
  if (!rec) {
    var target = String(supplierId);
    Object.keys(all).forEach(function (k) {
      var entry = all[k];
      if (!rec && entry && (
        String(entry._uuid) === target
        || String(entry.supplierId) === target
        || String(entry.supplier_id) === target
        || String(entry.supplierLegacyId) === target
      )) {
        rec = entry;
      }
    });
  }
  return rec || { authorizedProducts: [], assignedBrokers: [], defaultUSBroker: null, permissions: {} };
}

/* MX brokers visible to this shipper = brokers they created
   themselves (createdBy match) ∪ brokers Arturo assigned via
   Settings → Shipper Settings → Customs Brokers. Filtered to
   agentType === "mexican" so American-typed entries don't bleed in. */
function visibleMxBrokers(supplierId) {
  var settings = getShipperSettings(supplierId);
  /* assignedBrokers and defaultUSBroker store the broker's Supabase
     uuid (the FK type on shipper_settings). The brokers cache
     presents records as `b.id` = legacy_id + `b._uuid` = uuid.
     Match against BOTH so admin-assigned brokers actually surface;
     the previous check only compared legacy ids and missed every
     assignment that came through the admin's ShipperSettings UI. */
  var aSet = {}; (settings.assignedBrokers || []).forEach(function (id) { aSet[String(id)] = true; });
  return getBrokers().filter(function (b) {
    if (b.agentType !== "mexican") return false;
    if (String(b.createdBy) === String(supplierId)) return true;
    return !!aSet[String(b.id)] || !!aSet[String(b._uuid)];
  });
}

/* Resolved default US broker for this shipper (or null when Arturo
   hasn't picked one). The form displays this as locked read-only;
   it's also stamped onto the proforma at submission time so the
   stored value reflects the default at that moment. */
function resolveDefaultUSBroker(supplierId) {
  var settings = getShipperSettings(supplierId);
  if (!settings.defaultUSBroker) return null;
  var target = String(settings.defaultUSBroker);
  return getBrokers().find(function (b) {
    return String(b.id) === target || String(b._uuid) === target;
  }) || null;
}

/* Configured invoice prefix for this shipper. Empty string means
   "no prefix" — the Shipper Invoice field becomes a free input. */
function invoicePrefixFor(supplierId) {
  var s = getShipperSettings(supplierId);
  return s.invoicePrefix || "";
}

/* Reserve the next proforma consecutive for this shipper. Reads
   gfi-shipper-settings fresh from localStorage on every call so a
   change Arturo just made in Shipper Settings is honored on the
   next "New Embarque" click without any refresh.

   Rules:
     - If nextProformaNumber is configured (>0), always use it.
       The prefix is what Arturo set OR the shipper's initials as
       a sensible default OR empty string — but the NUMBER wins.
     - Atomically increment the counter so subsequent reservations
       advance even when this proforma stays as a draft forever.
     - Only fall back to the legacy date-range scan when no
       nextProformaNumber is configured at all. */
/* Reserve the next shipment (embarque) consecutive. Same shape as
   reserveProformaConsec but for the embarque-level counter. Reads
   gfi-shipper-settings fresh on every call so a change Arturo just
   made in Shipper Settings is honored on the next "New Embarque"
   click without any refresh.

   Falls back to the legacy max-scan (`nextEmbConsec`) when no
   counter is configured at all. */
/* Preview the next embarque consecutive WITHOUT advancing the
   counter. The counter only advances when handleSave / handleSubmit
   actually persists the embarque — see commitEmbConsec below.
   Previously this function incremented eagerly which left "holes"
   in the numbering whenever the shipper canceled a new-embarque
   draft. */
function reserveEmbConsec(shipper, embarques, season) {
  if (!shipper) return "??-E001";
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[shipper.id] || {};
  var prefix = rec.embarquePrefix;
  var nextNum = parseInt(rec.nextEmbarqueNumber, 10);
  if (!isNaN(nextNum) && nextNum > 0) {
    var effectivePrefix = (prefix != null && prefix !== "") ? prefix
      : (shipper.initials ? String(shipper.initials).toUpperCase() + "-E" : "");
    return effectivePrefix + String(nextNum).padStart(3, "0");
  }
  return nextEmbConsec(embarques, shipper, season);
}

/* Same preview-only behavior for proformas. */
function reserveProformaConsec(shipper, embarques, season) {
  if (!shipper) return "??-001";
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[shipper.id] || {};
  var prefix = rec.proformaPrefix;
  var nextNum = parseInt(rec.nextProformaNumber, 10);
  if (!isNaN(nextNum) && nextNum > 0) {
    var effectivePrefix = (prefix != null && prefix !== "") ? prefix
      : (shipper.initials ? String(shipper.initials).toUpperCase() + "-" : "");
    return effectivePrefix + String(nextNum).padStart(3, "0");
  }
  return nextProConsec(embarques, shipper, season);
}

/* Bump the saved counter past whatever consec the user actually
   used. Idempotent: re-saving the same embarque doesn't re-advance.
   Called from handleSave / handleSubmitProforma so canceled drafts
   never burn a counter slot. */
function commitEmbConsec(shipper, consec) {
  if (!shipper || !consec) return;
  var m = /(\d+)$/.exec(String(consec));
  if (!m) return;
  var used = parseInt(m[1], 10);
  if (isNaN(used)) return;
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[shipper.id] || {};
  var current = parseInt(rec.nextEmbarqueNumber, 10);
  if (isNaN(current) || used + 1 > current) {
    all[shipper.id] = Object.assign({}, rec, { nextEmbarqueNumber: used + 1 });
    try { localStorage.setItem("gfi-shipper-settings", JSON.stringify(all)); } catch (e) {}
  }
}
function commitProformaConsec(shipper, consec) {
  if (!shipper || !consec) return;
  var m = /(\d+)$/.exec(String(consec));
  if (!m) return;
  var used = parseInt(m[1], 10);
  if (isNaN(used)) return;
  var all = loadLS("gfi-shipper-settings", {}) || {};
  var rec = all[shipper.id] || {};
  var current = parseInt(rec.nextProformaNumber, 10);
  if (isNaN(current) || used + 1 > current) {
    all[shipper.id] = Object.assign({}, rec, { nextProformaNumber: used + 1 });
    try { localStorage.setItem("gfi-shipper-settings", JSON.stringify(all)); } catch (e) {}
    /* Also persist to Supabase so a new session doesn't reload the
       stale counter and reuse a number. The shipper_settings row is
       keyed by the supplier UUID (`supplier_id`); prefer
       shipper._uuid (set by rowToRecord) and fall back to shipper.id
       when the record came from legacy LS without a UUID. */
    var supplierId = shipper._uuid || shipper.id;
    if (supplierId) {
      supabase
        .from("shipper_settings")
        .update({ next_proforma_number: used + 1 })
        .eq("supplier_id", supplierId)
        .then(function () {}, function (err) {
          console.warn("[shipper-portal] failed to persist next_proforma_number to Supabase:", err && err.message);
        });
    }
  }
}
function stripInvoicePrefix(value, prefix) {
  if (!prefix) return value || "";
  var v = String(value || "");
  return v.indexOf(prefix) === 0 ? v.slice(prefix.length) : v;
}

/* Build the synthetic shipment shape the ShipmentRegistry print
   templates expect, from a {embarque, proforma} pair. Enriches
   lines and pallets with catalog data (size / units / label /
   packType / category) so Carta Porte and Shipping Instructions
   render full presentation strings + correct kg weights even from
   the shipper side, before Arturo accepts. Also maps each line's
   declaredValue → price so the value column has something to read. */
function buildSyntheticShipment(embarque, pro) {
  var catalog = getProducts();
  var productById = {};
  catalog.forEach(function (p) { if (p.id != null) productById[String(p.id)] = p; });
  /* Filter BEFORE enrichment so phantom rows never leave the
     portal. A real product line needs (productId or productBase) +
     a positive case count; a real pallet needs productBase + a
     positive casesPallet count. This guarantees print docs for a
     single proforma never carry empty/zero rows from the other
     proformas of the same embarque (they shouldn't anyway since
     buildSyntheticShipment reads ONLY this proforma — but the
     proforma's own form keeps an empty trailing row for the user
     to add the next entry, and that row was bleeding into prints). */
  var realLines = (pro.lines || []).filter(function (l) { return (l.productId || l.productBase) && (parseInt(l.cases, 10) || 0) > 0; });
  var enrichedLines = realLines.map(function (l) {
    var prod = l.productId ? productById[String(l.productId)] : null;
    var cat = l.category || (prod ? (String(prod.category || "").toLowerCase().indexOf("org") >= 0 ? "OG" : "CV") : "");
    var price = l.declaredValue != null && l.declaredValue !== "" ? l.declaredValue : (l.price || "");
    return {
      ...l,
      productBase: l.productBase || l.productName || (prod ? prod.product : "") || "",
      productName: l.productName || l.productBase || (prod ? prod.product : "") || "",
      productDescription: l.productDescription || (prod ? (prod.description || "") : ""),
      category: cat,
      size: l.size || (prod ? prod.size : "") || "",
      unitsPerCase: l.unitsPerCase || (prod ? (prod.unitsPerCase || prod.unitsCase) : "") || "",
      unitsCase: l.unitsCase || (prod ? (prod.unitsCase || prod.unitsPerCase) : "") || "",
      labelInitials: l.labelInitials || (prod ? prod.labelInitials : "") || "",
      packType: l.packType || (prod ? prod.packType : "") || "",
      price: price,
    };
  });
  var enrichedPallets = (pro.palletManifest || []).filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; }).map(function (p) {
    var match = enrichedLines.find(function (l) { return String(l.productBase || "").trim().toLowerCase() === String(p.productBase || "").trim().toLowerCase(); });
    var prod = match && match.productId ? productById[String(match.productId)] : null;
    return {
      ...p,
      category: p.category || (match ? match.category : "") || "",
      size: p.size || (match ? match.size : "") || (prod ? prod.size : "") || "",
      unitsCase: p.unitsCase || (match ? (match.unitsCase || match.unitsPerCase) : "") || (prod ? (prod.unitsCase || prod.unitsPerCase) : "") || "",
      labelInitials: p.labelInitials || (match ? match.labelInitials : "") || (prod ? prod.labelInitials : "") || "",
      packType: p.packType || (match ? match.packType : "") || (prod ? prod.packType : "") || "",
      condition: p.condition || (prod ? prod.condition : "") || "",
      traceability: p.traceability || p.julianCode || "",
    };
  });
  return {
    ...pro,
    supplierId: embarque.supplierId,
    shipperName: embarque.shipperName,
    shipperInvoice: embarque.shipperInvoice,
    proforma: pro.consec || embarque.consecEmbarque || "",
    lot: pro.gfiLot || "",
    lines: enrichedLines,
    palletManifest: enrichedPallets,
    /* Stamp the live US broker default the same way doSubmitProforma
       does — Draft proformas never write brokerUSId/Name to the
       stored shape (the form renders the broker as a locked read-
       only field that pulls from gfi-shipper-settings), so without
       this the Shipping Instructions print would show "—" for the
       US broker on any Draft preview. Fallback to whatever the
       proforma carries (Submitted / Accepted proformas already
       have these stamped). */
    brokerUSId:   (function () { if (pro.brokerUSId) return pro.brokerUSId; var u = resolveDefaultUSBroker(embarque.supplierId); return u ? u.id : ""; })(),
    brokerUSName: (function () { if (pro.brokerUSName) return pro.brokerUSName; var u = resolveDefaultUSBroker(embarque.supplierId); return u ? (u.shortName || u.fullName || "") : ""; })(),
  };
}

/* Whether the proforma has enough data to generate a print doc.
   Mirrors the spec: only available when products + transport are
   captured (lot + invoice handled at embarque level). */
function proformaReadyToPrint(embarque, pro) {
  var hasLine = (pro.lines || []).some(function (l) { return l.productId && (parseInt(l.cases, 10) > 0 || parseInt(l.pallets, 10) > 0); });
  if (!hasLine) return false;
  var trp = pro.transport || {};
  var hasTrp = ["driver", "platesTruck", "econTruck"].some(function (k) { return String(trp[k] || "").trim(); });
  if (!hasTrp) return false;
  return true;
}

/* Certificates visible to this shipper in the line-level dropdown.
   Two filters combined: belongs to shipper + currently active +
   not past its periodTo date. Depleted certs (used >= total) are
   intentionally NOT filtered out here — they're shown in the
   dropdown with an "(Agotado / Depleted)" suffix and a visual
   greying so the shipper sees why a cert is unavailable, and they
   can't be selected (see updateLine's guard). */
function certsForShipper(supplierId) {
  var todayStr = new Date().toISOString().substring(0, 10);
  return getCerts().filter(function (c) {
    if (String(c.supplierId) !== String(supplierId)) return false;
    var st = String(c.status || "").toLowerCase();
    if (st === "expired" || st === "inactive") return false;
    var endDate = c.periodTo || c.expirationDate || "";
    if (endDate && String(endDate) < todayStr) return false;
    return true;
  });
}

/* Per-certificate usage breakdown for the dropdown label. The Used
   count is NEVER stored on the cert record — it's always derived
   from gfi-shipments via computeCertUsage (the same helper the
   ExportCertificateRegistry uses). Pass the shipments array in so
   the caller can load it once and reuse across all certs in the
   dropdown without redundant localStorage reads.
   When no totalInvoices cap is set, treat the cert as having
   unlimited uses (depleted always false, no remaining count). */
function certUsage(c, allShipments, allBills, portalEmbarques) {
  var cap = parseInt(c.totalInvoices, 10);
  var hasCap = !isNaN(cap) && cap > 0;
  var used = computeCertUsage(c, allShipments || [], allBills || [], portalEmbarques || []).length;
  var remaining = hasCap ? Math.max(0, cap - used) : null;
  var depleted = hasCap && remaining <= 0;
  return { used: used, total: hasCap ? cap : null, remaining: remaining, depleted: depleted, hasCap: hasCap };
}

/* Apply the shipper's authorized-products filter to a product list.
   Empty authorization list = no restriction (per spec: don't block
   the shipper while Arturo is still configuring). */
function applyAuthorized(products, authorizedIds) {
  if (!authorizedIds || authorizedIds.length === 0) return products;
  var set = {}; authorizedIds.forEach(function (id) { set[String(id)] = true; });
  /* Match against EVERY id-like field on the product record:
       - `id`        — after rowToRecord this is row.legacy_id, so it
                       matches authorizedProducts entries that were
                       written when products lived in localStorage
                       under their Date.now() ids.
       - `_uuid`     — row.id (UUID) from Supabase; matches entries
                       written by a newer UI that stored UUIDs.
       - `legacy_id` / `legacyId` — defensive fallback if either ever
                       leaks onto the record (some loaders copy the
                       raw db field into `extra`).
       - `sku`       — last-resort match because old UIs occasionally
                       authorized-by-SKU instead of id.
     Without this, products created fresh in Supabase (legacy_id null,
     so p.id is null) silently fail authorization checks and the
     commodity / presentation dropdowns come up empty even though the
     catalog loaded fine. */
  return products.filter(function (p) {
    if (!p) return false;
    return !!set[String(p.id)]
        || !!set[String(p._uuid)]
        || !!set[String(p.legacy_id)]
        || !!set[String(p.legacyId)]
        || !!set[String(p.sku)];
  });
}
function commoditiesFromCatalog(authorizedIds) {
  var prods = applyAuthorized(getProducts(), authorizedIds);
  var seen = {}, out = [];
  prods.forEach(function (p) {
    var name = p.product || p.productName || "";
    if (name && !seen[name]) { seen[name] = true; out.push(name); }
  });
  return out.sort();
}
function presentationsFor(commodity, authorizedIds) {
  if (!commodity) return [];
  var prods = getProducts().filter(function (p) { return (p.product || p.productName || "") === commodity; });
  return applyAuthorized(prods, authorizedIds);
}
function buildPresentationLabel(p) {
  var cat = (p.category === "Organic" || p.category === "OG") ? "OG" : "CV";
  return (p.description || ((p.size || "") + " " + (p.unitsPerCase || p.unitsCase || "") + "u " + cat + " " + (p.labelInitials || ""))).trim();
}

/* Build a pre-filled pallet template from a product line. Looks up
   the product catalog by productId to pull size / unitsCase /
   labelInitials / packType (those fields don't live on the line
   itself — only on the catalog entry). Condition is always "Fresh"
   per business rule. The 5 manual fields (palletNum, casesPallet,
   variety, traceability/julianCode, growerCode) are intentionally
   left blank — the shipper fills those per-pallet. */
function prefilledPalletFromLine(line, products) {
  var prod = (products || []).find(function (p) { return String(p.id) === String(line.productId); });
  return {
    productBase: line.productBase || line.productName || "",
    category: line.category || "",
    condition: "Fresh",
    packType: prod ? (prod.packType || "") : "",
    size: prod ? (prod.size || "") : "",
    unitsCase: prod ? String(prod.unitsCase || prod.unitsPerCase || "") : "",
    labelInitials: prod ? (prod.labelInitials || "") : "",
  };
}

/* Project the manifest from the current line set. For each line with
   productBase + pallets > 0, emit N pre-filled rows. Existing rows
   are bucketed by their (productBase|size|category|label) key and
   reused in FIFO order to preserve manual fields when the user
   tweaks the pallet count of an existing line. Rows whose key has
   no surviving line slot get dropped — the caller checks for lossy
   removals before applying. */
function projectManifestFromLines(lines, currentManifest, products) {
  var keyOf = function (p) {
    return (p.productBase || "") + "|" + (p.size || "") + "|" + (p.category || "") + "|" + (p.labelInitials || "");
  };
  var bucket = {};
  (currentManifest || []).forEach(function (p) {
    var k = keyOf(p);
    if (!bucket[k]) bucket[k] = [];
    bucket[k].push(p);
  });
  var next = [];
  (lines || []).forEach(function (line) {
    var n = parseInt(line.pallets, 10) || 0;
    if (!line.productBase || n <= 0) return;
    var tpl = prefilledPalletFromLine(line, products);
    var k = keyOf(tpl);
    var existing = bucket[k] || [];
    for (var i = 0; i < n; i++) {
      var prev = existing.shift();
      if (prev) {
        next.push({
          ...tpl,
          /* Preserve manual fields from the existing row */
          palletNum: prev.palletNum || "",
          casesPallet: prev.casesPallet || "",
          variety: prev.variety || "",
          traceability: prev.traceability || "",
          julianCode: prev.julianCode || "",
          growerCode: prev.growerCode || "",
        });
      } else {
        next.push({ ...EMPTY_PALLET, ...tpl });
      }
    }
  });
  return next;
}

/* True if a pallet row has any of the 5 user-entered fields filled.
   Used to decide whether to ask confirmation before dropping a row. */
function manifestRowHasManualData(p) {
  return !!(p && (p.palletNum || p.casesPallet || p.variety || p.traceability || p.julianCode || p.growerCode));
}

/* Count of rows in `current` that won't fit anywhere in `projected`
   (FIFO match by the same key as projectManifestFromLines). Only
   counts rows that carry manual data — those are the ones the user
   would actually notice losing. */
function countLossyDropped(currentManifest, projectedManifest) {
  var keyOf = function (p) {
    return (p.productBase || "") + "|" + (p.size || "") + "|" + (p.category || "") + "|" + (p.labelInitials || "");
  };
  var room = {};
  (projectedManifest || []).forEach(function (p) {
    var k = keyOf(p); room[k] = (room[k] || 0) + 1;
  });
  var lost = 0;
  (currentManifest || []).forEach(function (p) {
    var k = keyOf(p);
    if ((room[k] || 0) > 0) { room[k]--; return; }
    if (manifestRowHasManualData(p)) lost++;
  });
  return lost;
}

/* Compare the proforma's product lines against its pallet manifest.
   Returns { empty: bool, list: string[] }:
     - empty: manifest has no rows with palletNum + productBase
     - list:  human-readable mismatch lines, e.g. "Blackberry: 480 cases
              in lines, 460 in manifest — 20 missing".
   Products are keyed by case-insensitive productBase/productName.
   A product that appears in lines but not in manifest (or vice versa)
   is also reported. Used by both ShipperPortal and ShipmentRegistry
   (shared logic — duplicated rather than extracted per CLAUDE.md). */
function manifestMismatches(pro, lang) {
  var realPallets = (pro.palletManifest || []).filter(function (p) { return (p.palletNum || p.productBase); });
  if (realPallets.length === 0) return { empty: true, list: [] };
  function bucketize(arr, key) {
    var out = {};
    arr.forEach(function (l) {
      var name = (l.productBase || l.productName || "").trim();
      if (!name) return;
      var k = name.toLowerCase();
      if (!out[k]) out[k] = { label: name, total: 0 };
      out[k].total += parseInt(l[key], 10) || 0;
    });
    return out;
  }
  var byLine = bucketize(pro.lines || [], "cases");
  var byManifest = bucketize(realPallets, "casesPallet");
  var keys = {};
  Object.keys(byLine).forEach(function (k) { keys[k] = true; });
  Object.keys(byManifest).forEach(function (k) { keys[k] = true; });
  var out = [];
  Object.keys(keys).forEach(function (k) {
    var l = byLine[k] || { label: (byManifest[k] || {}).label || k, total: 0 };
    var m = byManifest[k] || { label: l.label, total: 0 };
    if (l.total === m.total) return;
    var diff = l.total - m.total;
    var msg;
    if (lang === "es") {
      msg = l.label + ": " + l.total + " cajas en líneas, " + m.total + " en manifiesto — " + Math.abs(diff) + (diff > 0 ? " faltan" : " sobran");
    } else {
      msg = l.label + ": " + l.total + " cases in lines, " + m.total + " in manifest — " + Math.abs(diff) + (diff > 0 ? " missing" : " extra");
    }
    out.push(msg);
  });
  return { empty: false, list: out };
}

/* Three-level validation for a {embarque, proforma} pair. Returns
   { blocking, red, orange } string arrays describing what's missing
   at each severity tier.
     Level 1 (blocking)  — submit is refused.
     Level 2 (red)       — submit allowed, field flagged red as
                           "Pendiente" (e.g. invoice number/file
                           that SAT hasn't generated yet).
     Level 3 (orange)    — submit allowed, soft warning (export
                           certs that arrive in transit; trace/
                           grower codes missing on pallets). */
function validateProforma(embarque, pro, lang) {
  var ES = lang === "es";
  var blocking = []; var red = []; var orange = [];
  function blank(v) { return v == null || String(v).trim() === ""; }
  /* L1: trip dates */
  if (blank(pro.dateDeparture)) blocking.push(ES ? "Fecha de salida" : "Departure date");
  if (blank(pro.dateArrival))   blocking.push(ES ? "Fecha de llegada (ETA)" : "Arrival date (ETA)");
  /* L1: destination warehouse */
  if (blank(pro.warehouseId))   blocking.push(ES ? "Bodega de descarga" : "Destination warehouse");
  /* L1: customs broker (MX) — US is locked to default, not validated here */
  if (blank(pro.brokerMXId))    blocking.push(ES ? "Agente aduanal MX" : "MX customs broker");
  /* L1: truck/trailer/driver/alpha */
  var trp = pro.transport || {};
  [
    ["carrier",      ES ? "Línea transportista"         : "Carrier"],
    ["driver",       ES ? "Nombre del chofer"           : "Driver name"],
    ["phone",        ES ? "Celular"                     : "Cell phone #"],
    ["platesTruck",  ES ? "Placas tractor"              : "Truck plates"],
    ["econTruck",    ES ? "Número económico tractor"    : "Truck unit #"],
    ["platesTrailer",ES ? "Placas caja"                 : "Trailer plates"],
    ["econTrailer",  ES ? "Número económico caja"       : "Trailer unit #"],
    ["caat",         ES ? "CAAT"                        : "CAAT"],
    ["codigoAlpha",  ES ? "Código ALPHA"                : "ALPHA code"],
  ].forEach(function (pair) { if (blank(trp[pair[0]])) blocking.push(pair[1]); });
  /* L1: at least one line + manifest non-empty + manifest matches
     line totals (cases per product). */
  var hasLine = (pro.lines || []).some(function (l) { return l.productId && parseInt(l.cases, 10) > 0; });
  if (!hasLine) blocking.push(ES ? "Al menos 1 línea de producto con cajas" : "At least 1 product line with cases");
  var realPallets = (pro.palletManifest || []).filter(function (p) { return p.palletNum || p.productBase; });
  if (realPallets.length === 0) {
    blocking.push(ES ? "Manifiesto de pallets vacío" : "Pallet manifest empty");
  } else {
    /* L1: every "real" pallet must have all fields except trace +
       grower code (those are Level 3). */
    var palletReq = ["palletNum", "productBase", "category", "condition", "packType", "size", "unitsCase", "casesPallet", "labelInitials", "variety"];
    var bad = 0;
    realPallets.forEach(function (p) {
      if (palletReq.some(function (k) { return blank(p[k]); })) bad++;
    });
    if (bad > 0) blocking.push((ES ? bad + " pallet(s) con campos obligatorios faltantes" : bad + " pallet(s) missing required fields"));
  }
  /* L1: manifest totals match line totals per product. */
  var mm = manifestMismatches(pro, lang);
  if (!mm.empty && mm.list.length > 0) {
    blocking.push(ES ? "Las cajas del manifiesto no coinciden con las líneas:" : "Pallet manifest cases don't match lines:");
    mm.list.forEach(function (msg) { blocking.push("  · " + msg); });
  }
  /* L1: pallet numbers must be unique within the manifest. */
  var pnSeen = {};
  var dupePnCount = 0;
  (pro.palletManifest || []).forEach(function (p) {
    var v = String(p.palletNum || "").trim().toUpperCase();
    if (!v) return;
    pnSeen[v] = (pnSeen[v] || 0) + 1;
  });
  Object.keys(pnSeen).forEach(function (k) { if (pnSeen[k] > 1) dupePnCount++; });
  if (dupePnCount > 0) {
    blocking.push(ES ? dupePnCount + " número(s) de pallet duplicado(s) en el manifiesto"
                     : dupePnCount + " duplicate pallet number(s) in the manifest");
  }
  /* L1: total pallets in lines must match the manifest row count.
     This is the per-spec auto-sync guarantee — Product Lines drives
     the row count, so any drift means manual tampering with the
     manifest that needs reconciliation before submit. */
  var totalLinePallets = (pro.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0);
  var manifestRowCount = (pro.palletManifest || []).filter(function (p) { return p.palletNum || p.productBase; }).length;
  if (totalLinePallets > 0 && totalLinePallets !== manifestRowCount) {
    blocking.push((ES ? "Pallets del Manifiesto (" + manifestRowCount + ") no coinciden con Líneas de Producto (" + totalLinePallets + ")"
                      : "Pallet Manifest rows (" + manifestRowCount + ") don't match Product Lines pallet total (" + totalLinePallets + ")"));
  }
  /* L2: shipper invoice NUMBER stays as a red-flag (the SAT folio is
     a small text field, no upload friction, and downstream cert /
     bill linking really needs it). */
  if (blank(embarque.shipperInvoice)) red.push(ES ? "Número de Factura del Exportador — Pendiente" : "Shipper Invoice number — Pending");
  /* The invoice PDF / image attachment is OPTIONAL at submit time —
     the shipper may not have the SAT-stamped PDF in hand when the
     truck leaves, and blocking the submit forces them to wait or
     attach a placeholder. Downgraded to a Level-3 (orange) soft
     warning: the submit confirms with the user and then proceeds.
     Every other upload slot (export cert, pallet photos, other
     docs) is already optional. */
  if (!embarque.invoiceFile)          orange.push(ES ? "Archivo de Factura del Exportador — llegará después" : "Shipper Invoice file — arrives later");
  /* L3: export certs per OG line only. USDA Organic export certs
     don't apply to Conventional (CV) products — those lines are
     intentionally skipped so the validator doesn't nag about a
     non-applicable doc. */
  var noCert = 0;
  (pro.lines || []).filter(function (l) {
    if (!(l.productBase || l.productId)) return false;
    return l.category === "OG";
  }).forEach(function (l) {
    var hasCert = (l.exportCertId && l.exportCertId !== "__manual__") || !blank(l.exportCertNumber) || !blank(l.exportCertManual);
    if (!hasCert) noCert++;
  });
  if (noCert > 0) orange.push((ES ? noCert + " línea(s) orgánica(s) sin certificado de exportación — Pendiente en tránsito" : noCert + " organic line(s) without export certificate — Pending in transit"));
  /* L3: traceability (Julian) + grower code per pallet. */
  var noTrace = 0;
  realPallets.forEach(function (p) {
    if (blank(p.traceability) && blank(p.julianCode)) noTrace++;
    else if (blank(p.growerCode)) noTrace++;
  });
  if (noTrace > 0) orange.push((ES ? noTrace + " pallet(s) sin trazabilidad o código de productor" : noTrace + " pallet(s) missing traceability or grower code"));
  return { blocking: blocking, red: red, orange: orange };
}

/* Background color for a form field given its current value and
   the severity it falls under when empty. Returns undefined (white
   default) when the value is filled. Three palette shades come
   straight from the spec (soft, non-fluorescent). */
function levelBg(value, level) {
  if (value && String(value).trim()) return undefined;
  if (level === "block")  return "#FFF8E1"; /* soft yellow */
  if (level === "red")    return "#FFEBEE"; /* soft red */
  if (level === "orange") return "#FFF3E0"; /* soft orange */
  return undefined;
}

/* Embarque-level worst status — used for the list-row badge.
   Order matches user severity perception: draft (still being
   captured) > rejected (needs fix) > submitted > accepted. */
function worstEmbStatus(emb) {
  var sts = (emb.proformas || []).map(function (p) { return p.status || "draft"; });
  if (sts.length === 0) return "draft";
  if (sts.indexOf("rejected") >= 0) return "rejected";
  if (sts.indexOf("draft") >= 0) return "draft";
  if (sts.indexOf("submitted") >= 0) return "submitted";
  return "accepted";
}

/* ---------- Empty skeletons ---------- */
const EMPTY_LINE = { productBase: "", productId: "", productName: "", productDescription: "", category: "", cases: "", pallets: "", declaredValue: "", exportCertId: "", exportCertNumber: "", exportCertManual: "" };
/* Pallet manifest shape mirrors ShipmentRegistry.EMPTY_PALLET so
   the data Arturo's print templates expect (size / units / label /
   category / packType / condition / variety / traceability / weight)
   is captured directly by the shipper instead of being inferred at
   accept time. `julianCode` is kept as an alias for traceability so
   legacy records survive the shape change. */
const EMPTY_PALLET = { palletNum: "", productBase: "", category: "", condition: "", packType: "", size: "", unitsCase: "", casesPallet: "", labelInitials: "", variety: "", traceability: "", julianCode: "", growerCode: "" };
/* Same tags/colors as ShipmentRegistry.js's TEXAS_QC_TIERS/
   TEXAS_QC_COLORS, duplicated here per this codebase's per-file
   convention. Read-only badges only — the shipper never grades their
   own pallets, GFI does on the Ground-Fresh side. Arturo, 2026-09-16:
   "a nivel shipper... debe mostrar esto" (Texas QC columns visible in
   GF's pallet manifest edit view, missing from the portal's read-only
   view). 2026-10-01 — added Very Good/Good Quality/Bad Quality
   (ClariFresh's own vocabulary, shared across both modes — see
   ShipmentRegistry.js's TEXAS_QC_TIERS comment) + Severe Condition,
   the latter shown only for ClariFresh-mode shipments (Twin River). */
const TEXAS_QC_COLORS = {
  excellent: { bg: "#C8E6C9", text: "#1B5E20" },
  veryGood: { bg: "#E1BEE7", text: "#6A1B9A" },
  goodQuality: { bg: "#AED581", text: "#33691E" },
  /* 2026-10-13 — 5th Twin River/ClariFresh tier, same yellow as
     ShipmentRegistry.js's TEXAS_QC_COLORS.fairQuality — Arturo: "otro
     factor de calidad a twin river que es Fair Quality". Required
     here too (not just visual): the read-only badge below indexes
     this object directly with no fallback, so a pallet graded
     "fairQuality" without this entry would throw at render time. */
  fairQuality: { bg: "#FFEB3B", text: "#5D4A00" },
  good: { bg: "#FFF59D", text: "#7A5C00" },
  regular: { bg: "#FFCDD2", text: "#B71C1C" },
  badQuality: { bg: "#C62828", text: "#FFFFFF" },
  poor: { bg: "#EF5350", text: "#FFFFFF" },
  /* 2026-10-02 — "poorQuality" is ClariFresh's own distinct 4th tier,
     not the same as "poor" (GFI's own report) — Arturo: "poor y poor
     quality son distintos... uno se usa para mi reporte y el otro
     para clarifresh". */
  poorQuality: { bg: "#FFB74D", text: "#E65100" },
};
/* Same tolerance-sheet defect keys/order as ShipmentRegistry.js's
   TOLERANCE_DEFECTS, duplicated here per the same per-file convention
   as TEXAS_QC_COLORS just above — read-only, the shipper only views
   what GFI captured. 2026-10-15, Arturo: "la hoja de tolerancias...
   que de ahi se traslada el bill" — the shipper portal is one more
   place that mirror has to reach. */
const TOLERANCE_CATEGORY_LABELS = {
  en: { severeCondition: "Severe Condition", condition: "Condition", quality: "Quality", findings: "Findings" },
  es: { severeCondition: "Condición Severa", condition: "Condición", quality: "Calidad", findings: "Hallazgos" },
};
const TOLERANCE_DEFECT_LABELS = {
  /* Blackberry-only keys (2026-10-15, LOT 58069/QC 17470 +
     TR-SPEC-BLK-MX) appended alongside the blueberry ones — never
     shared, see TOLERANCE_DEFECTS_BY_PRODUCT in ShipmentRegistry.js.
     bugs/worms/insects (2026-10-21) are the "Findings" category —
     bugs/worms found in the fruit, not a Condition/Quality defect. */
  en: { decay: "Decay", mold: "Mold", insect: "Insect", leaky: "Leaky", shrivel: "Shrivel", soft: "Soft", brokenSkin: "Broken skin", attachedRemains: "Attached remains", floralRemains: "Floral remains", peduncle: "Peduncle", offColor: "Off color", berryDamage: "Berry damage", redBerries: "Red berries", greenBerries: "Green berries", scars: "Scars", dryCell: "Dry cell", redCell: "Red cell", regression: "Regression", whiteCells: "White cells", redGreenDryTips: "Red/green or dry tips", attachedCore: "Attached core", redCells: "Red cells", dryCells: "Dry cells", leaking: "Leaking", freezeDamage: "Freeze damage", softFruit: "Soft fruit", bugs: "Bugs", worms: "Worms", insects: "Insects" },
  es: { decay: "Pudrición", mold: "Moho", insect: "Insecto", leaky: "Fuga de jugo", shrivel: "Deshidratación", soft: "Blanda", brokenSkin: "Piel rota", attachedRemains: "Restos adheridos", floralRemains: "Restos florales", peduncle: "Pedúnculo", offColor: "Color fuera de estándar", berryDamage: "Daño en baya", redBerries: "Bayas rojas", greenBerries: "Bayas verdes", scars: "Cicatrices", dryCell: "Célula seca", redCell: "Célula roja", regression: "Regresión", whiteCells: "Células blancas", redGreenDryTips: "Puntas rojas/verdes o secas", attachedCore: "Núcleo adherido", redCells: "Células rojas", dryCells: "Células secas", leaking: "Fuga de jugo", freezeDamage: "Daño por congelamiento", softFruit: "Fruta blanda", bugs: "Bichos", worms: "Gusanos", insects: "Insectos" },
};
function toleranceDefectsSummary(qcDefects, lang) {
  if (!qcDefects) return "";
  var labels = TOLERANCE_DEFECT_LABELS[lang] || TOLERANCE_DEFECT_LABELS.en;
  var catLabels = TOLERANCE_CATEGORY_LABELS[lang] || TOLERANCE_CATEGORY_LABELS.en;
  var parts = [];
  ["severeCondition", "condition", "quality", "findings"].forEach(function (cat) {
    var list = Array.isArray(qcDefects[cat]) ? qcDefects[cat] : [];
    if (list.length === 0) return;
    var items = list.map(function (e) {
      var label = e.key === "__custom__" ? (e.custom || "—") : (labels[e.key] || e.key);
      var pct = (e.pct === 0 || e.pct) ? " " + e.pct + "%" : "";
      return label + pct;
    });
    parts.push(catLabels[cat] + ": " + items.join(", "));
  });
  return parts.join(" · ");
}
const EMPTY_TRANSPORT = { carrier: "", driver: "", platesTruck: "", econTruck: "", platesTrailer: "", econTrailer: "", caat: "", codigoAlpha: "", phone: "", sealNumber: "", thermograph: "" };

/* Pallet-manifest helpers duplicated from ShipmentRegistry per the
   "intentionally not shared" convention in CLAUDE.md. Keeping them
   in lockstep means weights + dropdown vocabularies match what
   Arturo sees one-to-one. */
const PALLET_SIZES_BY_PRODUCT = {
  Blackberry: ["6oz", "12oz", "18oz"],
  Raspberry: ["6oz", "12oz", "18oz"],
  Blueberry: ["6oz", "18oz", "1 dry pint"],
  Strawberry: ["1lb", "2lb"],
  Avocado: ["28s", "32s", "36s", "40s", "48s", "60s", "70s", "84s"],
  Lime: ["110s", "150s", "175s", "200s", "230s", "250s"],
};
const PALLET_PACK_TYPES = ["clamshell", "bulk", "bag", "case_pack"];
const PALLET_CONDITIONS = ["Fresh", "Fresh Cut", "Frozen"];
const PALLET_SOZ = { "6oz": 6, "12oz": 12, "18oz": 18, "1 dry pint": 16, "1lb": 16, "2lb": 32 };
function calcPalletWeight(p) {
  var oz = (PALLET_SOZ[p.size] || 0) * (parseFloat(p.unitsCase) || 0) * (parseFloat(p.casesPallet) || 0);
  if (!oz) return { lb: 0, kg: 0 };
  var lb = oz / 16;
  return { lb: lb, kg: lb * 0.453592 };
}
/* Label vocab — same logic as Arturo's palletLabelOptions: dedupe
   labelInitials from the product catalog, fall back to a canonical
   set when empty. Returns SearchSelect-ready options. */
function buildPalletLabelOptions() {
  var m = new Map();
  getProducts().forEach(function (p) {
    if (p && p.labelInitials) {
      var init = String(p.labelInitials).toUpperCase();
      if (!m.has(init)) m.set(init, p.label || init);
    }
  });
  if (m.size === 0) ["GF", "TR", "GB", "HH", "SL", "FS", "AS"].forEach(function (i) { m.set(i, i); });
  return Array.from(m.entries()).map(function (entry) { return { value: entry[0], label: entry[0] + (entry[1] && entry[1] !== entry[0] ? " — " + entry[1] : "") }; });
}
/* Variety vocab — from gfi-commodities for the chosen commodity. */
function varietiesForCommodity(commodityName) {
  if (!commodityName) return [];
  var n = String(commodityName).trim().toLowerCase();
  var cats = loadLS("gfi-commodities", []) || [];
  var c = cats.find(function (x) { return (x.nameEn || "").toLowerCase() === n || (x.nameEs || "").toLowerCase() === n; });
  return (c && Array.isArray(c.varieties)) ? c.varieties : [];
}
/* When the shipper picks a productBase on a pallet AND there's a
   unique authorized catalog product for that commodity (filtered by
   the shipper's authorizedProducts setting), pre-fill the catalog-
   driven fields. Ambiguous matches leave the row blank — the
   shipper picks each field manually. */
function autoFillPalletFromCatalog(p, productBase, authorizedIds) {
  if (!productBase) return p;
  /* Membership test mirrors applyAuthorized: check id (legacy), _uuid
     (Supabase row id), raw legacy_id / legacyId / sku fallbacks. See
     the comment block in applyAuthorized for the rationale. */
  var matches = getProducts().filter(function (prod) {
    if ((prod.product || "") !== productBase) return false;
    if (!authorizedIds || authorizedIds.length === 0) return true;
    var auth = {};
    authorizedIds.forEach(function (id) { auth[String(id)] = true; });
    return !!auth[String(prod.id)]
        || !!auth[String(prod._uuid)]
        || !!auth[String(prod.legacy_id)]
        || !!auth[String(prod.legacyId)]
        || !!auth[String(prod.sku)];
  });
  if (matches.length !== 1) return { ...p, productBase: productBase };
  var prod = matches[0];
  var cat = (String(prod.category || "").toLowerCase().indexOf("org") >= 0) ? "OG" : "CV";
  return {
    ...p,
    productBase: productBase,
    category: p.category || cat,
    packType: p.packType || prod.packType || "",
    size: p.size || prod.size || "",
    unitsCase: p.unitsCase || (prod.unitsPerCase || prod.unitsCase || ""),
    labelInitials: p.labelInitials || (prod.labelInitials ? String(prod.labelInitials).toUpperCase() : ""),
    condition: p.condition || prod.condition || "Fresh",
  };
}

function blankProforma(consec, seasonId, shipperName) {
  var _carrier = (function () {
    var nm = (shipperName || "").toLowerCase();
    return nm.indexOf("gomez") >= 0 ? "AGSA Logistics" : "";
  })();
  return {
    id: "SHP-PRO-" + Date.now() + "-" + Math.floor(Math.random() * 1000),
    consec: consec,
    proformaNumber: "",
    seasonId: seasonId || "",
    status: "draft",
    dateDeparture: "", dateArrival: "",
    warehouseId: "", warehouseName: "",
    brokerMXId: "", brokerMXName: "",
    brokerUSId: "", brokerUSName: "",
    transport: { ...EMPTY_TRANSPORT, carrier: _carrier },
    lines: [{ ...EMPTY_LINE }],
    palletManifest: [{ ...EMPTY_PALLET }],
    palletPhotos: [],
    otherDocs: [],
    notes: "",
    submittedAt: "", acceptedAt: "", rejectedAt: "", rejectReason: "",
    gfiLot: "", gfiShipmentId: "",
  };
}

/* Clone a proforma for "Add Proforma" on the same Shipper Invoice.
   Carries forward trip-level fields per spec (same truck, same
   trip, same invoice):
     - dateDeparture + dateArrival (same trip = same dates)
     - transport (same truck)
     - brokers MX + US (same trip)
   Everything else starts fresh — product lines, pallet manifest,
   warehouse (may differ per proforma), photos, notes. Export certs
   are per-line so they're entered fresh when the shipper picks
   products; the cert dropdown is already filtered to this shipper's
   certs (same invoice = same shipper's catalog of certs). */
function clonedProforma(source, consec, seasonId) {
  if (!source) return blankProforma(consec, seasonId, "");
  return {
    id: "SHP-PRO-" + Date.now() + "-" + Math.floor(Math.random() * 1000),
    consec: consec,
    proformaNumber: "",
    seasonId: seasonId || source.seasonId || "",
    status: "draft",
    dateDeparture: source.dateDeparture || "",
    dateArrival: source.dateArrival || "",
    warehouseId: "", warehouseName: "",
    brokerMXId: source.brokerMXId || "", brokerMXName: source.brokerMXName || "",
    brokerUSId: source.brokerUSId || "", brokerUSName: source.brokerUSName || "",
    transport: { ...EMPTY_TRANSPORT, ...(source.transport || {}) },
    lines: [{ ...EMPTY_LINE }],
    palletManifest: [{ ...EMPTY_PALLET }],
    palletPhotos: [],
    otherDocs: [],
    notes: "",
    submittedAt: "", acceptedAt: "", rejectedAt: "", rejectReason: "",
    gfiLot: "", gfiShipmentId: "",
  };
}

/* ---------- Phone country list + helpers ----------
   Static list used by the Carrier Info phone field. MX/US/CA pinned
   to the top per spec; the rest sorted alphabetically by the user's
   language at render time. Duplicated in ShipmentRegistry per the
   "intentionally not shared" rule in CLAUDE.md. */
const PHONE_COUNTRIES = [
  { code: "MX", flag: "🇲🇽", dial: "+52", nameEn: "Mexico", nameEs: "México" },
  { code: "US", flag: "🇺🇸", dial: "+1",  nameEn: "United States", nameEs: "Estados Unidos" },
  { code: "CA", flag: "🇨🇦", dial: "+1",  nameEn: "Canada", nameEs: "Canadá" },
  { code: "AR", flag: "🇦🇷", dial: "+54", nameEn: "Argentina", nameEs: "Argentina" },
  { code: "AU", flag: "🇦🇺", dial: "+61", nameEn: "Australia", nameEs: "Australia" },
  { code: "AT", flag: "🇦🇹", dial: "+43", nameEn: "Austria", nameEs: "Austria" },
  { code: "BE", flag: "🇧🇪", dial: "+32", nameEn: "Belgium", nameEs: "Bélgica" },
  { code: "BZ", flag: "🇧🇿", dial: "+501", nameEn: "Belize", nameEs: "Belice" },
  { code: "BO", flag: "🇧🇴", dial: "+591", nameEn: "Bolivia", nameEs: "Bolivia" },
  { code: "BR", flag: "🇧🇷", dial: "+55", nameEn: "Brazil", nameEs: "Brasil" },
  { code: "CL", flag: "🇨🇱", dial: "+56", nameEn: "Chile", nameEs: "Chile" },
  { code: "CN", flag: "🇨🇳", dial: "+86", nameEn: "China", nameEs: "China" },
  { code: "CO", flag: "🇨🇴", dial: "+57", nameEn: "Colombia", nameEs: "Colombia" },
  { code: "CR", flag: "🇨🇷", dial: "+506", nameEn: "Costa Rica", nameEs: "Costa Rica" },
  { code: "CU", flag: "🇨🇺", dial: "+53", nameEn: "Cuba", nameEs: "Cuba" },
  { code: "CZ", flag: "🇨🇿", dial: "+420", nameEn: "Czech Republic", nameEs: "República Checa" },
  { code: "DK", flag: "🇩🇰", dial: "+45", nameEn: "Denmark", nameEs: "Dinamarca" },
  { code: "DO", flag: "🇩🇴", dial: "+1", nameEn: "Dominican Republic", nameEs: "República Dominicana" },
  { code: "EC", flag: "🇪🇨", dial: "+593", nameEn: "Ecuador", nameEs: "Ecuador" },
  { code: "EG", flag: "🇪🇬", dial: "+20", nameEn: "Egypt", nameEs: "Egipto" },
  { code: "SV", flag: "🇸🇻", dial: "+503", nameEn: "El Salvador", nameEs: "El Salvador" },
  { code: "FI", flag: "🇫🇮", dial: "+358", nameEn: "Finland", nameEs: "Finlandia" },
  { code: "FR", flag: "🇫🇷", dial: "+33", nameEn: "France", nameEs: "Francia" },
  { code: "DE", flag: "🇩🇪", dial: "+49", nameEn: "Germany", nameEs: "Alemania" },
  { code: "GR", flag: "🇬🇷", dial: "+30", nameEn: "Greece", nameEs: "Grecia" },
  { code: "GT", flag: "🇬🇹", dial: "+502", nameEn: "Guatemala", nameEs: "Guatemala" },
  { code: "HT", flag: "🇭🇹", dial: "+509", nameEn: "Haiti", nameEs: "Haití" },
  { code: "HN", flag: "🇭🇳", dial: "+504", nameEn: "Honduras", nameEs: "Honduras" },
  { code: "HU", flag: "🇭🇺", dial: "+36", nameEn: "Hungary", nameEs: "Hungría" },
  { code: "IN", flag: "🇮🇳", dial: "+91", nameEn: "India", nameEs: "India" },
  { code: "ID", flag: "🇮🇩", dial: "+62", nameEn: "Indonesia", nameEs: "Indonesia" },
  { code: "IE", flag: "🇮🇪", dial: "+353", nameEn: "Ireland", nameEs: "Irlanda" },
  { code: "IT", flag: "🇮🇹", dial: "+39", nameEn: "Italy", nameEs: "Italia" },
  { code: "JM", flag: "🇯🇲", dial: "+1", nameEn: "Jamaica", nameEs: "Jamaica" },
  { code: "JP", flag: "🇯🇵", dial: "+81", nameEn: "Japan", nameEs: "Japón" },
  { code: "MA", flag: "🇲🇦", dial: "+212", nameEn: "Morocco", nameEs: "Marruecos" },
  { code: "NL", flag: "🇳🇱", dial: "+31", nameEn: "Netherlands", nameEs: "Países Bajos" },
  { code: "NZ", flag: "🇳🇿", dial: "+64", nameEn: "New Zealand", nameEs: "Nueva Zelanda" },
  { code: "NI", flag: "🇳🇮", dial: "+505", nameEn: "Nicaragua", nameEs: "Nicaragua" },
  { code: "NG", flag: "🇳🇬", dial: "+234", nameEn: "Nigeria", nameEs: "Nigeria" },
  { code: "NO", flag: "🇳🇴", dial: "+47", nameEn: "Norway", nameEs: "Noruega" },
  { code: "PK", flag: "🇵🇰", dial: "+92", nameEn: "Pakistan", nameEs: "Pakistán" },
  { code: "PA", flag: "🇵🇦", dial: "+507", nameEn: "Panama", nameEs: "Panamá" },
  { code: "PY", flag: "🇵🇾", dial: "+595", nameEn: "Paraguay", nameEs: "Paraguay" },
  { code: "PE", flag: "🇵🇪", dial: "+51", nameEn: "Peru", nameEs: "Perú" },
  { code: "PH", flag: "🇵🇭", dial: "+63", nameEn: "Philippines", nameEs: "Filipinas" },
  { code: "PL", flag: "🇵🇱", dial: "+48", nameEn: "Poland", nameEs: "Polonia" },
  { code: "PT", flag: "🇵🇹", dial: "+351", nameEn: "Portugal", nameEs: "Portugal" },
  { code: "PR", flag: "🇵🇷", dial: "+1", nameEn: "Puerto Rico", nameEs: "Puerto Rico" },
  { code: "RO", flag: "🇷🇴", dial: "+40", nameEn: "Romania", nameEs: "Rumania" },
  { code: "RU", flag: "🇷🇺", dial: "+7", nameEn: "Russia", nameEs: "Rusia" },
  { code: "SA", flag: "🇸🇦", dial: "+966", nameEn: "Saudi Arabia", nameEs: "Arabia Saudita" },
  { code: "SG", flag: "🇸🇬", dial: "+65", nameEn: "Singapore", nameEs: "Singapur" },
  { code: "ZA", flag: "🇿🇦", dial: "+27", nameEn: "South Africa", nameEs: "Sudáfrica" },
  { code: "KR", flag: "🇰🇷", dial: "+82", nameEn: "South Korea", nameEs: "Corea del Sur" },
  { code: "ES", flag: "🇪🇸", dial: "+34", nameEn: "Spain", nameEs: "España" },
  { code: "SE", flag: "🇸🇪", dial: "+46", nameEn: "Sweden", nameEs: "Suecia" },
  { code: "CH", flag: "🇨🇭", dial: "+41", nameEn: "Switzerland", nameEs: "Suiza" },
  { code: "TH", flag: "🇹🇭", dial: "+66", nameEn: "Thailand", nameEs: "Tailandia" },
  { code: "TT", flag: "🇹🇹", dial: "+1", nameEn: "Trinidad and Tobago", nameEs: "Trinidad y Tobago" },
  { code: "TR", flag: "🇹🇷", dial: "+90", nameEn: "Turkey", nameEs: "Turquía" },
  { code: "UA", flag: "🇺🇦", dial: "+380", nameEn: "Ukraine", nameEs: "Ucrania" },
  { code: "AE", flag: "🇦🇪", dial: "+971", nameEn: "United Arab Emirates", nameEs: "Emiratos Árabes Unidos" },
  { code: "GB", flag: "🇬🇧", dial: "+44", nameEn: "United Kingdom", nameEs: "Reino Unido" },
  { code: "UY", flag: "🇺🇾", dial: "+598", nameEn: "Uruguay", nameEs: "Uruguay" },
  { code: "VE", flag: "🇻🇪", dial: "+58", nameEn: "Venezuela", nameEs: "Venezuela" },
  { code: "VN", flag: "🇻🇳", dial: "+84", nameEn: "Vietnam", nameEs: "Vietnam" },
];
function buildPhoneCountryOptions(lang) {
  var top = ["MX", "US", "CA"];
  var rest = PHONE_COUNTRIES.filter(function (c) { return top.indexOf(c.code) < 0; });
  rest.sort(function (a, b) {
    var an = lang === "es" ? a.nameEs : a.nameEn;
    var bn = lang === "es" ? b.nameEs : b.nameEn;
    return an.localeCompare(bn);
  });
  var ordered = top.map(function (k) { return PHONE_COUNTRIES.find(function (c) { return c.code === k; }); }).concat(rest);
  return ordered.map(function (c) {
    return {
      value: c.code,
      label: c.flag + " " + c.dial,
      search: c.nameEn + " " + c.nameEs + " " + c.code + " " + c.dial,
    };
  });
}
/* Parse a stored phone string into { country, area, local }. Best-
   effort: tries to identify the dial prefix against the country
   list, then splits the remainder into area + local. Country
   defaults to MX when nothing matches.

   Mirrors the corrected logic in ./PhoneField.js: the dash structure
   is preserved so "area only" stays distinguishable from "local
   only". The previous `.split(/[\s-]+/).filter(Boolean)` collapsed
   empty segments, so "+52-1" (area only) round-tripped as
   { area: "", local: "1" } and the first digit you typed into Area
   reflected back as Local — making focus appear to jump fields. */
function parsePhone(s) {
  var str = String(s == null ? "" : s).trim();
  if (!str) return { country: "MX", area: "", local: "" };
  var m = str.match(/^(\+\d{1,4})(.*)$/);
  if (!m) return { country: "MX", area: "", local: str };
  var dial = m[1];
  var country = (PHONE_COUNTRIES.find(function (c) { return c.dial === dial; }) || { code: "MX" }).code;
  var rest = m[2];
  if (rest.length > 0 && (rest.charAt(0) === "-" || rest.charAt(0) === " ")) {
    rest = rest.slice(1);
  }
  var dashIdx = rest.indexOf("-");
  if (dashIdx >= 0) {
    return {
      country: country,
      area: rest.slice(0, dashIdx).replace(/\D/g, ""),
      local: rest.slice(dashIdx + 1),
    };
  }
  /* No dash means a single segment. Backwards-compat heuristic for
     legacy data that never went through the new serializer: short
     numeric (≤3 digits) → area, longer → local. */
  if (rest.length <= 3 && /^\d*$/.test(rest)) {
    return { country: country, area: rest, local: "" };
  }
  return { country: country, area: "", local: rest };
}
/* Format the local 7-digit number as "000-0000" while the user
   types. Strips non-digits and caps at 7 digits. */
function formatPhoneLocal(s) {
  var digits = String(s == null ? "" : s).replace(/\D/g, "").slice(0, 7);
  if (digits.length <= 3) return digits;
  return digits.slice(0, 3) + "-" + digits.slice(3);
}
/* Serialize back to the stored "+DIAL-AREA-LOCAL" form. ALWAYS emits
   both segments explicit (even when empty): "+52-1-" vs "+52--1" —
   so parsePhone can disambiguate "area only" from "local only". */
function serializePhone(country, area, local) {
  var c = PHONE_COUNTRIES.find(function (x) { return x.code === country; }) || PHONE_COUNTRIES[0];
  var a = String(area || "").replace(/\D/g, "").slice(0, 3);
  var l = formatPhoneLocal(local);
  if (!a && !l) return "";
  return c.dial + "-" + a + "-" + l;
}

/* ---------- Inline UI primitives ---------- */
const inp = { width: "100%", padding: "6px 8px", border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };
/* Read-only cell used in the Pallet Manifest for fields auto-filled
   from Product Lines. Visually distinct from an editable input —
   muted gray background + dashed border — so the user immediately
   sees these are locked. Edits flow upstream from Product Lines. */
const readOnlyCell = { width: "100%", padding: "6px 8px", border: "1px dashed var(--color-border)", borderRadius: 5, background: "rgba(0,0,0,0.04)", color: "var(--color-text-secondary)", fontSize: 11, fontFamily: "inherit", boxSizing: "border-box", minHeight: 30, display: "flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

function Field({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      {children}
    </div>
  );
}
function SectionHeader({ children, hint }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", margin: "14px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <span>{children}</span>
      {hint && <span style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>{hint}</span>}
    </div>
  );
}
function StatusBadge({ status, t }) {
  var c = STATUS_COLORS[status] || STATUS_COLORS.draft;
  var label = t["status" + (status.charAt(0).toUpperCase() + status.slice(1))] || status;
  return <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: c.bg, color: c.text, border: "1px solid " + c.border, boxShadow: STATUS_BEVEL, whiteSpace: "nowrap" }}>{label}</span>;
}
function Toast({ msg, onDismiss }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    if (!msg) return;
    setFading(false);
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(onDismiss, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [msg, onDismiss]);
  if (!msg) return null;
  return createPortal(<div style={{ position: "fixed", bottom: 24, right: 24, padding: "10px 18px", background: "var(--color-accent)", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 999999, animation: fading ? "fadeOutCorner 0.3s ease forwards" : undefined }}>{msg}</div>, document.body);
}

/* =================== PROFORMA SUB-FORM ===================
   Rendered inside the active tab of the Embarque modal. The
   parent owns the embarque object; this component only edits its
   slice and calls onChange with the patched proforma. */
function ProformaForm({ proforma, embarque, allEmbarques, shipper, lang, onChange, onChangeEmbarque, onPersistEmbarque, growers, shipperMode, catalogsVersion }) {
  var t = T[lang];
  var locked = proforma.status === "submitted" || proforma.status === "accepted";

  /* Collapsible-section state mirrors ShipmentRegistry's DetailPanel.
     All sections default open so the shipper sees everything on first
     render; clicking the chevron collapses what they don't need. */
  var [secOpen, setSecOpen] = useState({ header: true, transport: true, products: true, pallets: true, documents: true, notes: true });
  var toggleSec = function (k) { setSecOpen(function (prev) { var n = { ...prev }; n[k] = !n[k]; return n; }); };
  /* Collapsible header — matches Arturo's chevron+title+count
     pattern byte-for-byte. */
  var CollapseHeader = function (props) {
    var id = props.id, title = props.title, count = props.count;
    var isOpen = !!secOpen[id];
    return (
      <button type="button" onClick={function () { toggleSec(id); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 12px", marginTop: 14, background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: 8, cursor: "pointer", textAlign: "left" }}>
        <span style={{ fontSize: 11, color: "var(--color-text-muted)", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block", width: 10 }}>{"▶"}</span>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", flex: 1 }}>{title}</span>
        {count !== undefined && <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{count}</span>}
      </button>
    );
  };
  /* Common open-section body wrapper — matches Arturo's "padding /
     border / borderTop:none / negative margin-top to seal with the
     header" pattern. */
  var sectionBody = { padding: "14px 14px", border: "1px solid var(--color-border)", borderTop: "none", borderRadius: "0 0 8px 8px", marginTop: -1 };

  /* Embarque setter — falls back to a no-op if the parent didn't
     wire it (defensive; current EmbarqueModal always passes it). */
  var setEmb = function (field, value) {
    if (onChangeEmbarque) onChangeEmbarque({ [field]: value });
  };
  /* Embarque setter that ALSO persists the patch to Supabase right
     after updating local state. Used for document uploads (invoice
     file, export cert) so the shipper can attach files even after
     the proforma is locked (submitted/accepted) and never lose them
     to a missed Save click. */
  var persistEmb = function (field, value) {
    if (onPersistEmbarque) onPersistEmbarque({ [field]: value });
    else if (onChangeEmbarque) onChangeEmbarque({ [field]: value });
  };

  /* Path namespace for ANY upload on this embarque:
       {supplier_id}/{embarque_id}/...
     Keyed off shipper.id (legacy id) + embarque.id so collisions
     across shippers / embarques are impossible. safePathSegment
     handles whitespace and accents in legacy supplier ids. */
  var pathPrefix = function () {
    var s = safePathSegment(shipper && shipper.id ? shipper.id : "_no-shipper");
    var e = safePathSegment(embarque && embarque.id ? embarque.id : "_no-emb");
    return s + "/" + e;
  };
  /* Toast surface — child components may pass it down; fall back to
     console so we never silently swallow an upload error. */
  var toastErr = function (msg) {
    if (typeof window !== "undefined" && window.__gfiToast) window.__gfiToast(msg);
    else if (typeof console !== "undefined" && console.warn) console.warn(msg);
  };

  /* Invoice file upload — embarque-level single slot. The PDF is
     uploaded DIRECTLY to Supabase Storage (bucket `shipment-docs`);
     the embarque row stores only `{ name, size, type, path, url }`
     — no base64 — so the JSONB pipeline never sees the binary. This
     replaces the previous FileReader → setEmb("invoiceFile", { data })
     path that kept losing PDFs through stripBlobs. */
  var attachInvoiceFile = async function (files) {
    if (!files || files.length === 0) return;
    var file = files[0];
    try {
      var path = pathPrefix() + "/invoice." + extFromFile(file);
      var up = await uploadFileToStorage(SHIPMENT_DOCS_BUCKET, path, file);
      persistEmb("invoiceFile", {
        name: file.name,
        size: (file.size / 1024).toFixed(1) + " KB",
        type: file.type,
        path: up.path,
        url: up.publicUrl,
      });
      if (typeof console !== "undefined" && console.log) {
        console.log("[ShipperPortal][upload] invoiceFile →", up.path, "(" + (file.size / 1024).toFixed(1) + " KB) — auto-persisted");
      }
    } catch (e) {
      toastErr((lang === "es" ? "Error al subir factura: " : "Invoice upload failed: ") + (e && e.message ? e.message : String(e)));
    }
  };
  var removeInvoiceFile = function () {
    /* Best-effort delete in Storage, then clear the field. The
       Storage delete is async-and-forget — we don't want a slow
       network to block the UI clearing the slot. */
    var current = embarque && embarque.invoiceFile;
    if (current && current.path) removeFromStorage(SHIPMENT_DOCS_BUCKET, current.path);
    setEmb("invoiceFile", null);
  };
  /* Export Certificate file (embarque-level, shared across proformas). */
  var attachExportCertFile = async function (files) {
    if (!files || files.length === 0) return;
    var file = files[0];
    try {
      var path = pathPrefix() + "/exportCert." + extFromFile(file);
      var up = await uploadFileToStorage(SHIPMENT_DOCS_BUCKET, path, file);
      persistEmb("exportCertFile", {
        name: file.name,
        size: (file.size / 1024).toFixed(1) + " KB",
        type: file.type,
        path: up.path,
        url: up.publicUrl,
      });
      if (typeof console !== "undefined" && console.log) {
        console.log("[ShipperPortal][upload] exportCertFile →", up.path, "— auto-persisted");
      }
    } catch (e) {
      toastErr((lang === "es" ? "Error al subir certificado: " : "Export cert upload failed: ") + (e && e.message ? e.message : String(e)));
    }
  };
  var removeExportCertFile = function () {
    var current = embarque && embarque.exportCertFile;
    if (current && current.path) removeFromStorage(SHIPMENT_DOCS_BUCKET, current.path);
    setEmb("exportCertFile", null);
  };

  /* Setters operate by patching the proforma. The parent (EmbarqueModal)
     persists the result via setF when onChange fires. */
  var patch = function (slice) { onChange({ ...proforma, ...slice }); };
  var setTransportField = function (k, v) { patch({ transport: { ...(proforma.transport || EMPTY_TRANSPORT), [k]: v } }); };

  var updateLine = function (idx, field, value) {
    var next = (proforma.lines || []).map(function (l, i) {
      if (i !== idx) return l;
      var u = { ...l, [field]: value };
      if (field === "productBase") {
        u.productId = ""; u.productDescription = ""; u.category = ""; u.productName = value;
        /* A cert already attached to this line is only valid for its
           OWN commodity — changing the product must drop a mismatched
           selection instead of letting it silently ride along. */
        if (u.exportCertId && u.exportCertId !== "__manual__") {
          var attachedCert = certsForShipper(shipper && shipper.id).find(function (x) { return String(x.id) === String(u.exportCertId); });
          if (attachedCert && extractCommodity(attachedCert.productName) !== extractCommodity(value)) {
            u.exportCertId = ""; u.exportCertNumber = ""; u.exportCertManual = "";
          }
        }
      }
      if (field === "productId" && value) {
        var prod = getProducts().find(function (p) { return String(p.id) === String(value); });
        if (prod) {
          var cat = (String(prod.category || "").toLowerCase().indexOf("org") >= 0) ? "OG" : "CV";
          u.productDescription = buildPresentationLabel(prod);
          u.category = cat;
          u.productName = prod.product || u.productName;
          u.productBase = prod.product || u.productBase;
          /* Switching to a CV product invalidates any export cert
             that was attached — those are USDA Organic certs only. */
          if (cat === "CV") {
            u.exportCertId = "";
            u.exportCertNumber = "";
            u.exportCertManual = "";
          }
        }
      }
      if (field === "exportCertId") {
        if (value === "__manual__") { u.exportCertNumber = ""; }
        else if (value) {
          var c = certsForShipper(shipper && shipper.id).find(function (x) { return String(x.id) === String(value); });
          /* Refuse picks that would consume a depleted cert. The
             dropdown still renders depleted certs so the shipper
             sees them (with "(Agotado / Depleted)" suffix), but the
             selection silently no-ops + alerts so they can't be
             attached to a line. */
          if (c && certUsage(c, loadLS("gfi-shipments", []), loadLS("gfi-bills", []), loadLS("gfi-shipper-shipments", [])).depleted) {
            alert(lang === "es" ? "Este certificado está agotado y no se puede usar." : "This certificate is depleted and can't be used.");
            return l;
          }
          if (c) { u.exportCertNumber = c.certNumber || ""; u.exportCertManual = ""; }
        } else { u.exportCertNumber = ""; }
      }
      return u;
    });
    /* Auto-sync the Pallet Manifest from the new lines whenever a
       pallet-shaping field changed. Confirms before dropping rows
       that carry manual data (palletNum / cases / variety / trace /
       grower). If the user cancels, the line change is also
       reverted so the line count and manifest stay consistent. */
    if (field === "pallets" || field === "productId" || field === "productBase") {
      var projected = projectManifestFromLines(next, proforma.palletManifest || [], getProducts());
      var lossy = countLossyDropped(proforma.palletManifest || [], projected);
      if (lossy > 0 && !window.confirm(t.confirmLoseManifestRows.replace("{n}", String(lossy)))) {
        return;
      }
      patch({ lines: next, palletManifest: projected });
    } else {
      patch({ lines: next });
    }
  };
  var addLine = function () {
    if ((proforma.lines || []).length >= MAX_LINES) return;
    patch({ lines: [...(proforma.lines || []), { ...EMPTY_LINE }] });
  };
  var removeLine = function (idx) {
    var nl = (proforma.lines || []).slice(); nl.splice(idx, 1);
    var nextLines = nl.length > 0 ? nl : [{ ...EMPTY_LINE }];
    /* Removing a line may strip its pallet rows from the manifest. */
    var projected = projectManifestFromLines(nextLines, proforma.palletManifest || [], getProducts());
    var lossy = countLossyDropped(proforma.palletManifest || [], projected);
    if (lossy > 0 && !window.confirm(t.confirmLoseManifestRows.replace("{n}", String(lossy)))) {
      return;
    }
    patch({ lines: nextLines, palletManifest: projected });
  };
  /* Per-row validation tinting. Both helpers return yellow (#FFF8E1)
     when the field is blank. Required-field sets match the L1 lists
     used by the validator above:
       - pallets: palletNum, productBase, category, condition, packType,
         size, unitsCase, casesPallet, labelInitials, variety
         (traceability + julianCode + growerCode are Level 3 / optional)
       - lines: productBase, productId, pallets, cases, declaredValue
         (exportCert is Level 3 / optional)                              */
  var PALLET_REQ_FIELDS = ["palletNum", "productBase", "category", "condition", "packType", "size", "unitsCase", "casesPallet", "labelInitials", "variety"];
  var LINE_REQ_FIELDS   = ["productBase", "productId", "pallets", "cases", "declaredValue"];
  var palBg = function (field, value) {
    if (PALLET_REQ_FIELDS.indexOf(field) < 0) return undefined;
    return (value === undefined || value === null || String(value).trim() === "") ? "#FFF8E1" : undefined;
  };
  var lineBg = function (field, value) {
    if (LINE_REQ_FIELDS.indexOf(field) < 0) return undefined;
    return (value === undefined || value === null || String(value).trim() === "") ? "#FFF8E1" : undefined;
  };

  var updatePallet = function (idx, field, value) {
    var nm = (proforma.palletManifest || []).map(function (p, i) {
      if (i !== idx) return p;
      var nextP = { ...p, [field]: value };
      /* Auto-fill from the catalog when productBase changes AND
         exactly one authorized SKU matches the commodity. */
      if (field === "productBase") {
        nextP = autoFillPalletFromCatalog(nextP, value, authorizedIds);
      }
      /* Keep julianCode and traceability mirrored so legacy data
         doesn't bifurcate. */
      if (field === "traceability") nextP.julianCode = value;
      if (field === "julianCode") nextP.traceability = value;
      return nextP;
    });
    patch({ palletManifest: nm });
  };
  var addPallet = function () {
    if ((proforma.palletManifest || []).length >= MAX_PALLETS) return;
    patch({ palletManifest: [...(proforma.palletManifest || []), { ...EMPTY_PALLET }] });
  };
  var removePallet = function (idx) {
    var current = proforma.palletManifest || [];
    if (current.length <= 1) {
      alert(lang === "es" ? "Debe quedar al menos 1 pallet en el manifiesto." : "At least 1 pallet must remain in the manifest.");
      return;
    }
    var target = current[idx];
    var label = target && target.palletNum ? "#" + target.palletNum : "#" + (idx + 1);
    var msg = lang === "es" ? "¿Eliminar pallet " + label + "?" : "Delete pallet " + label + "?";
    if (!window.confirm(msg)) return;
    var nm = current.slice(); nm.splice(idx, 1);
    patch({ palletManifest: nm });
  };

  /* Pallet photo upload — proforma-level multi-file slot. Each
     photo goes through Storage just like invoiceFile / exportCert
     above. Path: {supplier}/{emb}/{proforma_id}/photo/{file_id}.{ext}
     — file_id is a per-upload random suffix so simultaneous uploads
     of two same-named photos don't clobber each other. */
  var attachPhotos = async function (files) {
    if (!files || files.length === 0) return;
    var prefix = pathPrefix() + "/" + safePathSegment(proforma.id || "_no-pro") + "/photo";
    var uploads = Array.prototype.map.call(files, async function (file) {
      try {
        var fid = Date.now() + "-" + Math.floor(Math.random() * 1000000);
        var path = prefix + "/" + fid + "." + extFromFile(file);
        var up = await uploadFileToStorage(SHIPMENT_DOCS_BUCKET, path, file);
        return {
          id: fid,
          name: file.name,
          size: (file.size / 1024).toFixed(1) + " KB",
          type: file.type,
          path: up.path,
          url: up.publicUrl,
        };
      } catch (e) {
        toastErr((lang === "es" ? "Error al subir foto " : "Photo upload failed for ") + (file.name || "?") + ": " + (e && e.message ? e.message : String(e)));
        return null;
      }
    });
    var results = (await Promise.all(uploads)).filter(Boolean);
    if (results.length > 0) patch({ palletPhotos: (proforma.palletPhotos || []).concat(results) });
  };
  var removePhoto = function (idx) {
    var arr = (proforma.palletPhotos || []).slice();
    var removed = arr[idx];
    arr.splice(idx, 1);
    if (removed && removed.path) removeFromStorage(SHIPMENT_DOCS_BUCKET, removed.path);
    patch({ palletPhotos: arr });
  };

  /* Per-shipper restrictions read once per render of this proforma
     form. They reflect Settings → Shipper Settings: authorized
     products (filters commodity + presentation dropdowns), assigned
     MX brokers (filters MX dropdown), default US broker (shown
     locked + auto-stamped at submit). */
  var shipperSettings = useMemo(function () { return getShipperSettings(shipper && shipper.id); }, [shipper]);
  var authorizedIds = shipperSettings.authorizedProducts || [];
  var isUnrestricted = authorizedIds.length === 0;
  /* Defensive Supabase refresh for the product catalog. The shipper
     portal renders commodity + presentation dropdowns from
     `gfi-products` localStorage, which is normally seeded by
     ShipperPortalShell's parallel preload — but the form often opens
     before that preload finishes (race condition), so the dropdowns
     come up empty and the user can't pick a product. This effect
     loads products from Supabase on mount, dual-writes them to LS,
     then flips `productsVer` to force the commodities memo (and all
     downstream presentationsFor / autoFillPalletFromCatalog calls
     that read `getProducts()` at render time) to re-evaluate against
     the now-hydrated catalog. */
  var [productsVer, setProductsVer] = useState(0);
  useEffect(function () {
    var alive = true;
    loadFromSupabase("products").then(function (rows) {
      if (!alive) return;
      try { localStorage.setItem("gfi-products", JSON.stringify(rows || [])); } catch (e) {}
      setProductsVer(function (v) { return v + 1; });
    }).catch(function () { /* leave whatever was in LS already */ });
    return function () { alive = false; };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var commodities = useMemo(function () { return commoditiesFromCatalog(authorizedIds); }, [authorizedIds, productsVer]);
  /* FIX 50 — admin can restrict which warehouses this shipper sees
     via shipper_settings.assignedWarehouses. Empty list = no
     restriction (legacy behavior). assignedWarehouses stores legacy
     ids; match against w.id with a w._uuid fallback like the broker
     resolver does. */
  var warehouses = useMemo(function () {
    var all = getWarehouses();
    var settings = getShipperSettings(shipper && shipper.id);
    var allowed = (settings && settings.assignedWarehouses) || [];
    if (!allowed.length) return all;
    var allowSet = {}; allowed.forEach(function (id) { allowSet[String(id)] = true; });
    return all.filter(function (w) {
      return !!allowSet[String(w.id)] || !!allowSet[String(w._uuid)];
    });
    /* catalogsVersion (bumped once EmbarqueModal's fresh Supabase
       fetch of brokers/warehouses lands, see its effect) forces this
       to recompute against the just-refreshed localStorage mirror —
       Arturo, 2026-09-16: "tampoco muestra el MC custom broker". */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipper, catalogsVersion]);
  var palletLabelOptions = useMemo(buildPalletLabelOptions, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var mxBrokers = useMemo(function () { return visibleMxBrokers(shipper && shipper.id); }, [shipper, catalogsVersion]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var defaultUSBroker = useMemo(function () { return resolveDefaultUSBroker(shipper && shipper.id); }, [shipper, catalogsVersion]);
  var shipperCerts = useMemo(function () { return certsForShipper(shipper && shipper.id); }, [shipper]);
  /* Loaded once per form mount so every cert dropdown row uses the
     same shipments snapshot when computing live usage. Reading
     localStorage 16+ times in a render hot path would be wasteful. */
  var allShipmentsForCertUsage = useMemo(function () { return loadLS("gfi-shipments", []); }, [shipper, proforma.id]);
  var allBillsForCertUsage = useMemo(function () { return loadLS("gfi-bills", []); }, [shipper, proforma.id]);
  /* Portal's own embarques/proformas — the shipper's pending or
     accepted-but-not-yet-promoted slots that already consume a cert. */
  var allPortalEmbarquesForCertUsage = useMemo(function () { return loadLS("gfi-shipper-shipments", []); }, [shipper, proforma.id]);

  return (
    <div style={{ opacity: locked ? 0.75 : 1, pointerEvents: locked ? "none" : "auto" }}>

      {/* Rejection banner */}
      {proforma.status === "rejected" && proforma.rejectReason && (
        <div style={{ padding: "8px 12px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6, color: "#B91C1C", fontSize: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{t.rejectionReason}:</div>
          <div>{proforma.rejectReason}</div>
          <div style={{ marginTop: 4, fontStyle: "italic", color: "#7c2d2d" }}>{t.fixAndResubmit}</div>
        </div>
      )}

      {locked && (
        <div style={{ padding: "6px 12px", background: "var(--color-bg)", borderRadius: 5, marginBottom: 10, fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", textAlign: "center" }}>
          {proforma.status === "accepted" ? t.readonlyAcc : t.readonlyPro}
        </div>
      )}

      {/* GFI lot + Entry # once assigned. Entry # is pulled live
          from the linked shipment so updates in ShipmentRegistry
          surface here on next render. Read-only — the shipper can't
          edit it. */}
      {proforma.gfiLot && (
        <div style={{ padding: "8px 12px", background: "rgba(42,122,111,0.08)", border: "1px solid rgba(42,122,111,0.25)", borderRadius: 6, marginBottom: 10, fontSize: 12, fontWeight: 700, display: "flex", gap: 18, flexWrap: "wrap" }}>
          <div>
            {t.gfiLotAssigned}: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)", fontSize: 14 }}>LOT {proforma.gfiLot}</span>
          </div>
          {(function () {
            if (!proforma.gfiShipmentId) return null;
            var shp = (loadLS("gfi-shipments", []) || []).find(function (s) { return s && String(s.id) === String(proforma.gfiShipmentId); });
            var entry = shp ? (shp.entryNumber || "") : "";
            return (
              <div>
                ENTRY #: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: entry ? "var(--color-accent)" : "var(--color-text-muted)", fontSize: 14 }}>{entry || "—"}</span>
              </div>
            );
          })()}
        </div>
      )}

      {/* SECTION 1 — Shipment Header (collapsible). Holds the
          identifying + routing data: consec, trip dates, warehouse,
          brokers, plus the EMBARQUE-level shipper invoice number
          (shared across all proformas of this embarque). */}
      <CollapseHeader id="header" title={t.secHeader} />
      {secOpen.header && (
        <div style={sectionBody}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Field label={t.consec}>
              <input value={proforma.consec || ""} readOnly style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", background: "var(--color-bg)", cursor: "default" }} placeholder="—" />
            </Field>
            <Field label={t.departureDate + " *"}>
              <input type="date" value={proforma.dateDeparture || ""} onChange={function (e) { patch({ dateDeparture: e.target.value }); }} style={{ ...inp, background: levelBg(proforma.dateDeparture, "block") || inp.background }} />
            </Field>
            <Field label={t.arrivalDate + " *"}>
              <input type="date" value={proforma.dateArrival || ""} onChange={function (e) { patch({ dateArrival: e.target.value }); }} style={{ ...inp, background: levelBg(proforma.dateArrival, "block") || inp.background }} />
            </Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
            <Field label={t.warehouse + " *"}>
              <div style={{ background: levelBg(proforma.warehouseId, "block"), borderRadius: 5, padding: levelBg(proforma.warehouseId, "block") ? 2 : 0 }}>
                <SearchSelect value={proforma.warehouseId || ""} onChange={function (v) { var w = warehouses.find(function (x) { return String(x.id) === String(v); }); patch({ warehouseId: v, warehouseName: w ? (w.shortName || w.fullName || w.name || "") : "" }); }} placeholder={t.selectWarehouse} options={warehouses.map(function (w) { return { value: w.id, label: w.shortName || w.fullName || w.name || w.id }; })} />
              </div>
            </Field>
            <Field label={t.brokerMX + " *"}>
              {mxBrokers.length === 0 ? (
                <div style={{ ...inp, padding: "10px 8px", fontSize: 11, color: "#B91C1C", fontStyle: "italic", background: "rgba(239,68,68,0.05)", borderColor: "rgba(239,68,68,0.3)" }}>{t.brokerMXNone}</div>
              ) : (
                <div style={{ background: levelBg(proforma.brokerMXId, "block"), borderRadius: 5, padding: levelBg(proforma.brokerMXId, "block") ? 2 : 0 }}>
                  <SearchSelect value={proforma.brokerMXId || ""} onChange={function (v) { var b = mxBrokers.find(function (x) { return String(x.id) === String(v); }); patch({ brokerMXId: v, brokerMXName: b ? (b.shortName || b.fullName || "") : "" }); }} placeholder={t.selectBroker} options={mxBrokers.map(function (b) { return { value: b.id, label: b.shortName || b.fullName || b.id }; })} />
                </div>
              )}
            </Field>
            <Field label={t.brokerUS}>
              {defaultUSBroker ? (
                <div style={{ padding: "8px 10px", border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg)", fontSize: 12 }}>
                  <div style={{ fontWeight: 600, color: "var(--color-text)" }}>{defaultUSBroker.shortName || defaultUSBroker.fullName || "—"}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>🔒 {t.brokerUSLocked}</div>
                </div>
              ) : (
                <div style={{ ...inp, padding: "10px 8px", fontSize: 11, color: "#B91C1C", fontStyle: "italic", background: "rgba(239,68,68,0.05)", borderColor: "rgba(239,68,68,0.3)" }}>{t.brokerUSMissing}</div>
              )}
            </Field>
          </div>
          {/* Shipper Invoice (embarque-level — shared across proformas
              of the same invoice). Level 2 (red) when empty per the
              SAT-can-fail rule. */}
          <div style={{ marginTop: 8, maxWidth: 480 }}>
            <Field label={t.shipperInvoiceReq}>
              {(function () {
                var prefix = invoicePrefixFor(shipper && shipper.id);
                var missing = !embarque.shipperInvoice;
                var ph = missing ? (lang === "es" ? "Pendiente" : "Pending") : "—";
                var bg = missing ? "#FFEBEE" : "var(--color-bg-input)";
                if (!prefix) {
                  return <input value={embarque.shipperInvoice || ""} onChange={function (e) { setEmb("shipperInvoice", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", background: bg, borderColor: missing ? "rgba(239,68,68,0.4)" : "var(--color-border)" }} placeholder={ph} />;
                }
                var suffix = stripInvoicePrefix(embarque.shipperInvoice, prefix);
                return (
                  <div style={{ display: "flex", border: "1px solid " + (missing ? "rgba(239,68,68,0.4)" : "var(--color-border)"), borderRadius: 5, overflow: "hidden", background: bg }}>
                    <span style={{ padding: "7px 10px", background: "var(--color-bg)", color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, borderRight: "1px solid var(--color-border)", whiteSpace: "nowrap" }}>{prefix}</span>
                    <input value={suffix} onChange={function (e) { setEmb("shipperInvoice", prefix + e.target.value); }} style={{ flex: 1, padding: "7px 10px", border: "none", background: "transparent", color: "var(--color-text)", fontFamily: "'JetBrains Mono', monospace", outline: "none" }} placeholder={ph} />
                  </div>
                );
              })()}
            </Field>
          </div>
        </div>
      )}

      {/* SECTION 3 — Products (collapsible). Same product lines
          table as before, just wrapped. Section header carries
          the count for at-a-glance scanning. */}
      <CollapseHeader id="products" title={t.secProducts} count={(proforma.lines || []).filter(function (l) { return l.productBase || l.productName; }).length} />
      {secOpen.products && (
        <div style={sectionBody}>
        <SectionHeader hint={isUnrestricted ? t.productsUnrestricted : t.productsRestricted}>{t.productLines}</SectionHeader>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
        <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
          <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 10, color: "var(--color-text-secondary)", width: "13%" }}>{t.product}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 10, color: "var(--color-text-secondary)", width: "22%" }}>{t.presentation}</th>
          <th style={{ padding: "6px 4px", textAlign: "center", fontSize: 10, color: "var(--color-text-secondary)", width: "5%" }}>{t.category}</th>
          <th style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, color: "var(--color-text-secondary)", width: "7%" }}>{t.pallets}</th>
          <th style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, color: "var(--color-text-secondary)", width: "7%" }}>{t.cases}</th>
          {/* Declared Value — export-document reference only. NOT a
              sale price (the shipper never sees real sale data). */}
          <th style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, color: "var(--color-text-secondary)", width: "11%" }} title={t.declaredValueHint}>{t.declaredValue}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", fontSize: 10, color: "var(--color-text-secondary)", width: "27%" }}>{t.exportCert}</th>
          <th style={{ width: "8%" }}></th>
        </tr></thead>
        <tbody>{(proforma.lines || []).map(function (line, idx) {
          var presOpts = presentationsFor(line.productBase || line.productName, authorizedIds).map(function (p) { return { value: p.id, label: buildPresentationLabel(p) }; });
          /* Cert dropdown: shipper's filtered (active/non-expired)
             certs with per-option availability count. Depleted certs
             stay visible with an "(Agotado / Depleted)" suffix so the
             shipper sees why a cert is unavailable; the updateLine
             guard refuses to pick a depleted one. Manual entry stays
             at the bottom as a last resort. */
          /* FIX 30 — depleted certs are hidden from the dropdown
             entirely instead of being shown with an "(Agotado)"
             suffix. The shipper sees only certs with at least one
             invoice still available. */
          /* 2026-09-30 (Arturo: "muestrame solo los certificados
             disponibles de ese shipper... y solo de los productos que
             contenga el embarque") — shipperCerts already scopes to
             the shipper, but this dropdown never also restricted to
             the CURRENT line's commodity, so every cert the shipper
             has (any product) showed up on every line regardless of
             what product that line is actually for. Same commodity
             match ShipmentRegistry.js's getAvailableExportCerts
             already uses (product name substring match, ignoring pack
             size/label/category — a cert covers one commodity). */
          var lineCommodity = extractCommodity(line.productBase || line.productName);
          var availableCount = 0;
          var certOpts = shipperCerts
            .filter(function (c) { return !lineCommodity || extractCommodity(c.productName) === lineCommodity; })
            .map(function (c) {
              var u = certUsage(c, allShipmentsForCertUsage, allBillsForCertUsage, allPortalEmbarquesForCertUsage);
              return { cert: c, usage: u };
            })
            .filter(function (e) { return !e.usage.depleted; })
            .map(function (e) {
              var c = e.cert, u = e.usage;
              var base = c.certNumber + " · " + (c.productName || "—");
              var suffix;
              if (u.hasCap) {
                /* Format per spec: "(12 disponibles de 16)" /
                   "(12 available of 16)" — full prose, not a fraction. */
                suffix = " (" + u.remaining + (lang === "es" ? " disponibles de " : " available of ") + u.total + ")";
              } else {
                /* No cap configured — count as available. */
                suffix = "";
              }
              availableCount++;
              return { value: c.id, label: base + suffix };
            });
          var certPlaceholder = availableCount === 0 ? t.noCertsAvailable : t.selectCert;
          certOpts.push({ value: "__manual__", label: lang === "es" ? "Capturar manualmente..." : "Enter manually..." });
          return (
            <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
              <td style={{ padding: "6px 4px" }}><SearchSelect value={line.productBase || line.productName || ""} onChange={function (v) { updateLine(idx, "productBase", v); }} placeholder={t.selectProduct} options={commodities.map(function (c) { return { value: c, label: c }; })} style={{ background: lineBg("productBase", line.productBase || line.productName) }} /></td>
              <td style={{ padding: "6px 4px" }}><SearchSelect value={line.productId || ""} onChange={function (v) { updateLine(idx, "productId", v); }} placeholder={t.selectPresentation} options={presOpts} disabled={!(line.productBase || line.productName)} style={{ background: lineBg("productId", line.productId) }} /></td>
              <td style={{ padding: "6px 4px", textAlign: "center" }}>{line.category && <span style={{ padding: "2px 6px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: line.category === "OG" ? "var(--color-badge-og-bg, rgba(42,122,111,0.12))" : "var(--color-badge-cv-bg, rgba(107,114,128,0.12))", color: line.category === "OG" ? "var(--color-badge-og-text, #2A7A6F)" : "var(--color-badge-cv-text, #374151)" }}>{line.category}</span>}</td>
              <td style={{ padding: "6px 4px" }}><input type="number" min="0" value={line.pallets || ""} onChange={function (e) { updateLine(idx, "pallets", e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: lineBg("pallets", line.pallets) || inp.background }} /></td>
              <td style={{ padding: "6px 4px" }}><input type="number" min="0" value={line.cases || ""} onChange={function (e) { updateLine(idx, "cases", e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: lineBg("cases", line.cases) || inp.background }} /></td>
              <td style={{ padding: "6px 4px" }}><input type="number" min="0" step="0.01" value={line.declaredValue || ""} onChange={function (e) { updateLine(idx, "declaredValue", e.target.value); }} title={t.declaredValueHint} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", background: lineBg("declaredValue", line.declaredValue) || inp.background }} placeholder="$" /></td>
              <td style={{ padding: "6px 4px" }}>
                {/* Export certs apply ONLY to OG products. CV lines
                    show the field disabled + grayed out so the
                    shipper sees the field exists but can't pick one. */}
                {(function () {
                  var isCV = line.category === "CV";
                  var ph = isCV ? (lang === "es" ? "N/A (Convencional)" : "N/A (Conventional)") : certPlaceholder;
                  return (
                    <>
                      <SearchSelect value={isCV ? "" : (line.exportCertId || "")} onChange={function (v) { updateLine(idx, "exportCertId", v); }} placeholder={ph} options={certOpts} disabled={isCV} />
                      {!isCV && line.exportCertId === "__manual__" && <input value={line.exportCertManual || ""} onChange={function (e) { updateLine(idx, "exportCertManual", e.target.value); }} placeholder={t.certManualPlaceholder} style={{ ...inp, marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }} />}
                    </>
                  );
                })()}
              </td>
              <td style={{ padding: "6px 4px", textAlign: "center" }}>
                <button type="button" onClick={function () { removeLine(idx); }} style={{ padding: "3px 7px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"×"}</button>
              </td>
            </tr>
          );
        })}</tbody>
      </table>
      {(proforma.lines || []).length < MAX_LINES && <button type="button" onClick={addLine} style={{ marginTop: 6, padding: "5px 12px", borderRadius: 4, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {t.product}</button>}
        </div>
      )}

      {/* SECTION 4 — Pallet Manifest (collapsible). */}
      <CollapseHeader id="pallets" title={t.secPallets + " *"} count={(proforma.palletManifest || []).filter(function (p) { return p.palletNum || p.productBase; }).length + " / " + MAX_PALLETS} />
      {secOpen.pallets && (
        <div style={sectionBody}>
      <SectionHeader hint={t.palletManifestHint + " · " + (proforma.palletManifest || []).length + "/" + MAX_PALLETS}>{t.palletManifest} *</SectionHeader>
      {/* Julian day badge — info-only reference for the shipper while
          keying the Traceability (Julian) column. Recomputed every
          render so it stays current across the year. */}
      {(function () {
        var today = new Date();
        var start = new Date(today.getFullYear(), 0, 0);
        var diff = today - start;
        var julian = Math.floor(diff / 86400000);
        return (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
            <div title={lang === "es" ? "Día Juliano actual (día del año)" : "Today's Julian day (day of year)"} style={{ padding: "4px 10px", borderRadius: 12, background: "rgba(31,78,121,0.10)", border: "1px solid rgba(31,78,121,0.35)", color: "#1F4E79", fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }}>
              📅 {lang === "es" ? "Hoy" : "Today"}: <span style={{ fontSize: 13, fontWeight: 800 }}>{julian}</span>
            </div>
          </div>
        );
      })()}
      {/* Live reconciliation banner — shown as soon as cases don't
          line up, so the shipper sees the problem while editing
          rather than at submit time. */}
      {(function () {
        var mm = manifestMismatches(proforma, lang);
        if (mm.empty) {
          return <div style={{ marginBottom: 8, padding: "8px 12px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 5, fontSize: 11, color: "#B91C1C" }}>{t.needManifest}</div>;
        }
        if (mm.list.length === 0) return null;
        return (
          <div style={{ marginBottom: 8, padding: "8px 12px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 5, fontSize: 11, color: "#B91C1C" }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{t.needManifestMatch}</div>
            {mm.list.map(function (msg, i) { return <div key={i} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>· {msg}</div>; })}
          </div>
        );
      })()}
      {/* Full pallet manifest — layout mirrored 1:1 from
          ShipmentRegistry's form so the shipper sees the same table
          Arturo does. Outer wrapper carries `overflowX: auto` so the
          last columns (Trazabilidad Julian / Cód. Productor / Peso)
          don't get clipped on narrower viewports: scroll happens
          INSIDE the table, never on the modal itself. Table carries
          a minWidth so columns stop compressing past readability and
          trigger the wrapper's scrollbar instead. */}
      <div style={{ overflowX: "auto", width: "100%" }}>
      <table style={{ width: "100%", minWidth: 1200, borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          {/* Group header over the Texas QC columns so the shipper
              knows what "Quality"/"Condition" refer to — Arturo,
              2026-09-16: "falto agregaras a esas 2 columnas el titulo
              para que el shipper sepa de que se trata: Texas QC
              Report". Empty cells above the other 14 columns keep
              this row purely a label strip, no visual gridlines. */}
          <tr style={{ color: "var(--color-text-secondary)" }}>
            <th colSpan={14} style={{ padding: 0, border: "none" }} />
            <th colSpan={proforma.texasQcMode === "clarifresh" ? 4 : 3} style={{ padding: "4px 4px 2px", textAlign: "center", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-accent)", borderLeft: "1px solid var(--color-accent)", borderRight: "1px solid var(--color-accent)", borderTop: "1px solid var(--color-accent)" }}>{t.texasQcHeader}</th>
          </tr>
          <tr style={{ borderBottom: "2px solid var(--color-border)", color: "var(--color-text-secondary)", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          <th style={{ padding: "6px 4px", textAlign: "left" }}>{t.palletNum}</th>
          <th style={{ padding: "6px 4px", textAlign: "left" }}>{t.tagId}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 110 }}>{t.product}</th>
          <th style={{ padding: "6px 4px", textAlign: "center" }}>{t.category}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 95 }}>{t.palletCondition}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 90 }}>{t.palletPack}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 90 }}>{t.palletPresentation}</th>
          <th style={{ padding: "6px 4px", textAlign: "right" }}>{t.palletUnitsCase}</th>
          <th style={{ padding: "6px 4px", textAlign: "right" }}>{t.casesPallet}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 110 }}>{t.palletLabel}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 110 }}>{t.palletVariety}</th>
          <th style={{ padding: "6px 4px", textAlign: "left" }}>{t.julianCode}</th>
          <th style={{ padding: "6px 4px", textAlign: "left" }}>{t.growerCode}</th>
          <th style={{ padding: "6px 4px", textAlign: "right", minWidth: 90 }}>{t.palletWeight}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 100, borderLeft: "1px solid var(--color-accent)" }}>{t.texasQuality}</th>
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 100 }}>{t.texasCondition}</th>
          {proforma.texasQcMode === "clarifresh" && <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 100 }}>{t.texasSevereCondition}</th>}
          {/* 2026-10-15, Arturo (GFI Team/internal mode): the same
              per-pallet defect capture now also exists for non-
              ClariFresh shipments (Quality only, read-only mirror of
              what ShipmentRegistry.js captured) — no longer gated to
              clarifresh-only. */}
          <th style={{ padding: "6px 4px", textAlign: "left", minWidth: 140, borderRight: "1px solid var(--color-accent)" }}>{t.qcDefects}</th>
        </tr></thead>
          <tbody>{(function () {
            /* Build the set of pallet numbers that appear more than
               once in the manifest. Any row whose palletNum is in
               this set renders red + carries a tooltip explaining
               the duplicate. Trim + case-insensitive to catch user
               typing variants of the same physical pallet. */
            var pnCounts = {};
            (proforma.palletManifest || []).forEach(function (p) {
              var v = String(p.palletNum || "").trim().toUpperCase();
              if (!v) return;
              pnCounts[v] = (pnCounts[v] || 0) + 1;
            });
            return (proforma.palletManifest || []).map(function (p, idx) {
            var sizeOptions = PALLET_SIZES_BY_PRODUCT[p.productBase] || [];
            var w = calcPalletWeight(p);
            var varietyOpts = varietiesForCommodity(p.productBase);
            var conditionOpts = PALLET_CONDITIONS.map(function (c) {
              return { value: c, label: c === "Fresh" ? t.palletCondFresh : c === "Fresh Cut" ? t.palletCondFreshCut : c === "Frozen" ? t.palletCondFrozen : c };
            });
            var packOpts = PALLET_PACK_TYPES.map(function (pk) {
              var labelMap = { clamshell: t.packClamshell, bulk: t.packBulk, bag: t.packBag, case_pack: t.packCasePack };
              return { value: pk, label: labelMap[pk] || pk };
            });
            var pnKey = String(p.palletNum || "").trim().toUpperCase();
            var pnDupe = !!pnKey && (pnCounts[pnKey] || 0) > 1;
            return (
              <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                {/* Editable: Pallet # — red when duplicated within
                    this manifest. Tooltip carries the bilingual error
                    message for accessibility. */}
                <td style={{ padding: "5px 3px" }}><input value={p.palletNum || ""} onChange={function (e) { updatePallet(idx, "palletNum", e.target.value); }} onBlur={function (e) { var val = e.target.value.replace(/\D/g, ""); if (val && val.length < 3) { val = val.padStart(3, "0"); updatePallet(idx, "palletNum", val); } }} title={pnDupe ? t.duplicatePalletNum : ""} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: pnDupe ? "#FFEBEE" : (palBg("palletNum", p.palletNum) || inp.background), borderColor: pnDupe ? "#DC2626" : "var(--color-border)", color: pnDupe ? "#B91C1C" : "var(--color-text)" }} />{pnDupe && <div style={{ fontSize: 9, color: "#B91C1C", marginTop: 2, fontWeight: 700, textAlign: "center" }}>{t.duplicatePalletNum}</div>}</td>
                {/* Tag ID — the warehouse tag number, assigned by GFI
                    once the pallet is physically received (not by the
                    shipper), so this is always read-only here, "—"
                    until GFI stamps it on the Ground-Fresh side. Same
                    field already surfaced next to Pallet # in Bill/
                    Invoice/SL Invoice — Arturo, 2026-09-16: "hay que
                    agregar los numeros de tag de la bodega... al
                    shipper portal para que sean visibles para ellos". */}
                <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.tagId || "—"}</div></td>
                {/* Read-only pre-fills (Product / Category / Condition /
                    Pack / Size / Units/Case / Label) — driven by the
                    matching Product Line; edit upstream to change here. */}
                <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.productBase || "—"}</div></td>
                <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.category || "—"}</div></td>
                <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.condition || "Fresh"}</div></td>
                <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.packType || "—"}</div></td>
                <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.size || "—"}</div></td>
                <td style={{ padding: "5px 3px" }}><div style={{ ...readOnlyCell, textAlign: "right", fontFamily: "'Courier New',monospace" }}>{p.unitsCase || "—"}</div></td>
                {/* Editable: Cases/Pallet */}
                <td style={{ padding: "5px 3px" }}><input type="number" min="0" value={p.casesPallet || ""} onChange={function (e) { updatePallet(idx, "casesPallet", e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, background: palBg("casesPallet", p.casesPallet) || inp.background }} /></td>
                {/* Read-only pre-fill: Label */}
                <td style={{ padding: "5px 3px" }}><div style={readOnlyCell}>{p.labelInitials || "—"}</div></td>
                {/* Editable: Variety */}
                <td style={{ padding: "5px 3px" }}><SearchSelect value={p.variety || ""} onChange={function (v) { updatePallet(idx, "variety", v); }} placeholder={varietyOpts.length === 0 ? "—" : "—"} disabled={varietyOpts.length === 0} limit={30} options={varietyOpts.map(function (v) { return { value: v, label: v }; })} style={{ background: palBg("variety", p.variety) }} /></td>
                <td style={{ padding: "5px 3px" }}><input value={p.traceability || p.julianCode || ""} onChange={function (e) { updatePallet(idx, "traceability", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></td>
                <td style={{ padding: "5px 3px" }}>{shipperMode ? (
                  /* FIX 8 — dropdown of growers belonging to this
                     shipper. Label format: "001 - Juan Sandoval". */
                  <SearchSelect
                    value={p.growerCode || ""}
                    onChange={function (v) { updatePallet(idx, "growerCode", v); }}
                    placeholder="—"
                    limit={30}
                    options={(growers || []).map(function (g) {
                      var nm = g.personType === "moral" ? (g.razonSocial || "—") : ([g.nombre, g.apPaterno, g.apMaterno].filter(Boolean).join(" ") || "—");
                      var code = g.producerCode || "";
                      /* Dropdown still shows "001 - Juan Sandoval" for
                         picking; once selected, the closed trigger
                         renders only the code so the field stays
                         compact (per the table's narrow column). */
                      return { value: code, label: (code || "—") + " - " + nm, triggerLabel: code, search: nm + " " + code };
                    }).filter(function (o) { return !!o.value; })}
                  />
                ) : (
                  <input value={p.growerCode || ""} onChange={function (e) { updatePallet(idx, "growerCode", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
                )}</td>
                <td style={{ padding: "5px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
                  {w.lb > 0 ? (
                    <div>
                      <div style={{ fontWeight: 700 }}>{w.lb.toFixed(1) + " lb"}</div>
                      <div style={{ color: "var(--color-text-muted)", fontSize: 9 }}>{w.kg.toFixed(1) + " kg"}</div>
                    </div>
                  ) : <span style={{ color: "var(--color-text-muted)" }}>—</span>}
                </td>
                {/* Texas QC Report — read-only badges. GFI grades these
                   on the Ground-Fresh side after Texas inspection; the
                   shipper only ever views the result. */}
                <td style={{ padding: "5px 3px", borderLeft: "1px solid var(--color-accent)" }}>{p.texasQuality ? <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: TEXAS_QC_COLORS[p.texasQuality].bg, color: TEXAS_QC_COLORS[p.texasQuality].text, boxShadow: STATUS_BEVEL }}>{t["texasQc" + p.texasQuality.charAt(0).toUpperCase() + p.texasQuality.slice(1)]}</span> : <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>
                <td style={{ padding: "5px 3px" }}>{p.texasCondition ? <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: TEXAS_QC_COLORS[p.texasCondition].bg, color: TEXAS_QC_COLORS[p.texasCondition].text, boxShadow: STATUS_BEVEL }}>{t["texasQc" + p.texasCondition.charAt(0).toUpperCase() + p.texasCondition.slice(1)]}</span> : <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>
                {proforma.texasQcMode === "clarifresh" && <td style={{ padding: "5px 3px" }}>{p.texasSevereCondition ? <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: TEXAS_QC_COLORS[p.texasSevereCondition].bg, color: TEXAS_QC_COLORS[p.texasSevereCondition].text, boxShadow: STATUS_BEVEL }}>{t["texasQc" + p.texasSevereCondition.charAt(0).toUpperCase() + p.texasSevereCondition.slice(1)]}</span> : <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>}
                {/* 2026-10-15, Arturo (GFI Team/internal mode): same
                    read-only defects mirror, now also for non-
                    ClariFresh shipments. */}
                <td style={{ padding: "5px 3px", borderRight: "1px solid var(--color-accent)", fontSize: 9, color: "var(--color-text-secondary)" }}>{toleranceDefectsSummary(p.qcDefects, lang) || <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>
                {/* Per-row remove button intentionally omitted:
                    rows are driven by Product Lines pallet count. */}
              </tr>
            );
          });
          })()}</tbody>
      </table>
      </div>
      {/* Add-pallet button intentionally omitted: change the Product
          Lines pallet count to add or remove manifest rows. */}
        </div>
      )}

      {/* SECTION 2 — Transport Data (collapsible). Layout MIRRORS
          ShipmentRegistry 1:1 — same labels, same row structure
          (3-col carrier / 2-col truck / 2-col trailer / 2-col
          CAAT-alpha / 2-col seal-thermograph), same TRUCK INFO +
          TRAILER INFO subtitles, same input styles. The * marker
          (Level 1 required) renders with soft-yellow bg per the
          validation spec; the data keys are identical so portal
          submissions map directly into Arturo's shipment record. */}
      <CollapseHeader id="transport" title={t.secTransport} />
      {secOpen.transport && (
        <div style={sectionBody}>
          {(function () {
            var tr = proforma.transport || {};
            /* Per-field bg helper — required Level-1 fields show
               soft yellow when empty; others stay default. */
            var trBg = function (key, req) { return req ? (levelBg(tr[key], "block") || inp.background) : inp.background; };
            var sub = { fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" };
            var lbl = { fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 };
            var inField = function (key, label, req, up) {
              return (
                <div>
                  <div style={lbl}>{label + (req ? " *" : "")}</div>
                  <input value={tr[key] || ""} onChange={function (e) { setTransportField(key, up ? e.target.value.toUpperCase() : e.target.value); }} style={{ ...inp, background: trBg(key, req), ...(up ? { textTransform: "uppercase" } : {}) }} />
                </div>
              );
            };
            /* Cell phone rendered as 3 fields (country / area / local
               with auto-dash). Stored as a single concatenated string
               on tr.phone so print docs and CSV export still see one
               value. Defaults to MX (+52). */
            var phoneParsed = parsePhone(tr.phone || "");
            var countryOpts = buildPhoneCountryOptions(lang);
            var phoneMissing = !String(tr.phone || "").trim();
            var phoneBg = phoneMissing ? "#FFF8E1" : inp.background;
            var phoneCommit = function (country, area, local) {
              setTransportField("phone", serializePhone(country, area, local));
            };
            var phoneField = (
              <div>
                <div style={lbl}>{t.phone + " *"}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <div style={{ width: 110, flexShrink: 0 }}>
                    <SearchSelect
                      value={phoneParsed.country}
                      onChange={function (v) { phoneCommit(v, phoneParsed.area, phoneParsed.local); }}
                      options={countryOpts}
                      placeholder="—"
                      style={{ background: phoneBg }}
                    />
                  </div>
                  <input
                    value={phoneParsed.area}
                    onChange={function (e) { phoneCommit(phoneParsed.country, e.target.value.replace(/\D/g, "").slice(0, 3), phoneParsed.local); }}
                    placeholder={lang === "es" ? "Área" : "Area"}
                    maxLength={3}
                    style={{ ...inp, width: 60, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", background: phoneBg }}
                  />
                  <input
                    value={phoneParsed.local}
                    onChange={function (e) { phoneCommit(phoneParsed.country, phoneParsed.area, e.target.value); }}
                    placeholder="000-0000"
                    maxLength={8}
                    style={{ ...inp, flex: 1, fontFamily: "'JetBrains Mono', monospace", background: phoneBg }}
                  />
                </div>
              </div>
            );
            return (
              <>
                {/* Carrier row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.5fr", gap: 8, marginBottom: 10 }}>
                  {inField("carrier", t.carrier, true)}
                  {inField("driver", t.driver, true)}
                  {phoneField}
                </div>
                {/* Compact paired-column grid: each column stacks two
                    related fields (truck / trailer / caat+alpha /
                    seal+temp recorder). Replaces the old TRUCK INFO /
                    TRAILER INFO headers — labels alone carry meaning. */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {inField("econTruck", t.econTruck, true, true)}
                    {inField("platesTruck", t.platesTruck, true, true)}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {inField("econTrailer", t.econTrailer, true, true)}
                    {inField("platesTrailer", t.platesTrailer, true, true)}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {inField("caat", t.caat, true, true)}
                    {inField("codigoAlpha", t.codigoAlpha, true, true)}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {inField("sealNumber", t.sealNumber, false)}
                    {inField("thermograph", t.thermograph, false)}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* SECTION 5 — Documents (collapsible). Split into two
          subsections so the shipper understands which files live on
          the embarque (shared across proformas) vs which live on
          this specific proforma:
            EMBARQUE-LEVEL (shared):
              - Invoice File (single, required)
              - Export Certificate (single, optional)
            PROFORMA-LEVEL (per proforma):
              - Loaded Pallet Photos
              - Other Documents
          The count badge sums all files visible from this proforma's
          perspective: embarque files (which any proforma sees) plus
          this proforma's own files. */}
      <CollapseHeader id="documents" title={t.secDocs} count={(embarque.invoiceFile ? 1 : 0) + (embarque.exportCertFile ? 1 : 0) + (embarque.otherDocs || []).length + (proforma.palletPhotos || []).length + (proforma.otherDocs || []).length} />
      {secOpen.documents && (
        <div style={{ ...sectionBody, pointerEvents: "auto", opacity: 1 }}>
          {/* ---------- SHIPMENT-LEVEL (embarque) ---------- */}
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", padding: "0 0 6px", borderBottom: "1px dashed var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <span>{t.shipmentDocsSub}</span>
            <span style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>{t.shipmentDocsHint}</span>
          </div>

          {/* Shipper Invoice file (embarque-level).
              2026-09-14 — Arturo: same complaint as the list pill, but
              for this detail-view upload slot — a GFI-captured embarque
              (docs never uploaded through this portal slot, by design)
              showed the red "❌ / upload required" prompt as if
              something were still pending, when there's nothing left
              to do. Once the active proforma is accepted, drop the
              alarm styling and dashed upload prompt for a neutral note
              instead — still lets them upload later if they want to,
              just doesn't imply it's required or missing. */}
          <div style={{ marginBottom: 14 }}>
            <Field label={t.invoiceFile + " " + (embarque.invoiceFile ? "✅" : (proforma.status === "accepted" ? "" : "❌"))}>
              {embarque.invoiceFile ? (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 5, fontSize: 11, maxWidth: 520 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#047857", display: "flex", alignItems: "center", gap: 8 }}>
                    ✓ {embarque.invoiceFile.name} <span style={{ color: "var(--color-text-muted)" }}>({embarque.invoiceFile.size})</span>
                    <span style={{ padding: "1px 6px", borderRadius: 3, background: "rgba(42,122,111,0.12)", color: "var(--color-accent)", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "inherit" }}>{t.shipmentDocBadge}</span>
                  </span>
                  <span style={{ display: "flex", gap: 6 }}>
                    {(embarque.invoiceFile.url || embarque.invoiceFile.data) && <a href={embarque.invoiceFile.url || embarque.invoiceFile.data} target="_blank" rel="noreferrer" style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", textDecoration: "none", fontSize: 10, fontWeight: 700 }}>{lang === "es" ? "Ver" : "View"}</a>}
                    <button type="button" onClick={removeInvoiceFile} style={{ padding: "2px 7px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 10, fontWeight: 700 }}>{"×"}</button>
                  </span>
                </div>
              ) : proforma.status === "accepted" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "GFI ya tiene esta factura por su cuenta — no es necesario subirla aquí." : "GFI already has this invoice on their side — no need to upload it here."}</span>
                  <FileDropZone onFiles={attachInvoiceFile} multiple={false} lang={lang}>
                    <label style={{ display: "inline-block", padding: "4px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>
                      {t.uploadBtn}
                      <input type="file" accept=".pdf,image/*" onChange={function (e) { attachInvoiceFile(e.target.files); }} style={{ display: "none" }} />
                    </label>
                  </FileDropZone>
                </div>
              ) : (
                <FileDropZone onFiles={attachInvoiceFile} multiple={false} lang={lang}>
                  <label style={{ display: "inline-block", padding: "6px 12px", borderRadius: 5, border: "1px dashed #DC2626", background: "rgba(239,68,68,0.04)", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                    {t.uploadBtn}
                    <input type="file" accept=".pdf,image/*" onChange={function (e) { attachInvoiceFile(e.target.files); }} style={{ display: "none" }} />
                  </label>
                </FileDropZone>
              )}
            </Field>
          </div>

          {/* Export Certificate file (embarque-level, optional). */}
          <div style={{ marginBottom: 18 }}>
            <Field label={t.exportCertFile}>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 4, fontStyle: "italic" }}>{t.exportCertFileHint}</div>
              {embarque.exportCertFile ? (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 5, fontSize: 11, maxWidth: 520 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#047857", display: "flex", alignItems: "center", gap: 8 }}>
                    ✓ {embarque.exportCertFile.name} <span style={{ color: "var(--color-text-muted)" }}>({embarque.exportCertFile.size})</span>
                    <span style={{ padding: "1px 6px", borderRadius: 3, background: "rgba(42,122,111,0.12)", color: "var(--color-accent)", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "inherit" }}>{t.shipmentDocBadge}</span>
                  </span>
                  <span style={{ display: "flex", gap: 6 }}>
                    {(embarque.exportCertFile.url || embarque.exportCertFile.data) && <a href={embarque.exportCertFile.url || embarque.exportCertFile.data} target="_blank" rel="noreferrer" style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", textDecoration: "none", fontSize: 10, fontWeight: 700 }}>{lang === "es" ? "Ver" : "View"}</a>}
                    <button type="button" onClick={removeExportCertFile} style={{ padding: "2px 7px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 10, fontWeight: 700 }}>{"×"}</button>
                  </span>
                </div>
              ) : (
                <FileDropZone onFiles={attachExportCertFile} multiple={false} lang={lang}>
                  <label style={{ display: "inline-block", padding: "6px 12px", borderRadius: 5, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                    {t.uploadBtn}
                    <input type="file" accept=".pdf,image/*" onChange={function (e) { attachExportCertFile(e.target.files); }} style={{ display: "none" }} />
                  </label>
                </FileDropZone>
              )}
            </Field>
          </div>

          {/* Legacy embarque-level Other Documents: show only if the
              record was created before the split (so old data isn't
              hidden). New uploads go to the per-proforma block. */}
          {(embarque.otherDocs || []).length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <Field label={t.otherDocs + " (" + t.shipmentDocBadge + ")"}>
                <div style={{ fontSize: 11, color: "var(--color-accent)", fontWeight: 600 }}>
                  {(embarque.otherDocs || []).length + " " + (lang === "es" ? "documento(s)" : "document(s)")}
                </div>
                <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
                  {(embarque.otherDocs || []).map(function (d, i) {
                    var isImg = d.type && String(d.type).indexOf("image/") === 0;
                    var rm = function () {
                      var next = (embarque.otherDocs || []).slice(); next.splice(i, 1);
                      setEmb("otherDocs", next);
                    };
                    if (isImg) {
                      return (
                        <div key={d.id || i} style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "var(--color-bg)", position: "relative" }}>
                          <img src={d.url || d.data} alt={d.name} style={{ width: "100%", height: 80, objectFit: "cover", display: "block", background: "#000" }} />
                          <button type="button" onClick={rm} title={lang === "es" ? "Eliminar" : "Remove"} style={{ position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: 9, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: "16px", padding: 0 }}>{"×"}</button>
                          <div style={{ padding: "4px 6px", fontSize: 9 }}>
                            <div style={{ fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                            <div style={{ color: "var(--color-text-muted)", marginTop: 1 }}>{d.size}</div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={d.id || i} style={{ border: "1px solid rgba(16,185,129,0.3)", borderRadius: 6, background: "rgba(16,185,129,0.06)", padding: "8px 10px", position: "relative", minHeight: 70 }}>
                        <button type="button" onClick={rm} title={lang === "es" ? "Eliminar" : "Remove"} style={{ position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: 9, border: "none", background: "rgba(220,38,38,0.85)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: "16px", padding: 0 }}>{"×"}</button>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#047857", fontSize: 14, fontWeight: 700 }}>✓ <span style={{ fontSize: 18 }}>📄</span></div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 14 }}>{d.name}</div>
                        <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>{d.size}{d.type ? " · " + d.type.split("/").pop().toUpperCase() : ""}</div>
                        {(d.url || d.data) && <div style={{ marginTop: 4 }}><a href={d.url || d.data} target="_blank" rel="noreferrer" style={{ fontSize: 9, color: "var(--color-accent)", textDecoration: "none", fontWeight: 600 }}>{lang === "es" ? "Ver" : "View"}</a></div>}
                      </div>
                    );
                  })}
                </div>
              </Field>
            </div>
          )}

          {/* ---------- PROFORMA-LEVEL ---------- */}
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", padding: "0 0 6px", borderBottom: "1px dashed var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, marginTop: 6 }}>
            <span>{t.proformaDocsSub}</span>
            <span style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>{t.proformaDocsHint}</span>
          </div>

          {/* Pallet photos — image grid with thumbnails. */}
          <Field label={t.palletPhotos}>
            <FileDropZone onFiles={attachPhotos} lang={lang}>
              <label style={{ display: "inline-block", padding: "6px 12px", borderRadius: 5, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                {t.uploadBtn}
                <input type="file" accept="image/*" multiple onChange={function (e) { attachPhotos(e.target.files); }} style={{ display: "none" }} />
              </label>
            </FileDropZone>
            {(proforma.palletPhotos || []).length > 0 && (
              <>
                <div style={{ marginTop: 8, fontSize: 11, color: "var(--color-accent)", fontWeight: 600 }}>
                  {(proforma.palletPhotos || []).length + " " + (lang === "es" ? "foto(s) cargada(s)" : "photo(s) uploaded")}
                </div>
                <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
                  {(proforma.palletPhotos || []).map(function (ph, i) {
                    return (
                      <div key={i} style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "var(--color-bg)", position: "relative" }}>
                        <img src={ph.url || ph.data} alt={ph.name} style={{ width: "100%", height: 80, objectFit: "cover", display: "block", background: "#000" }} />
                        <button type="button" onClick={function () { removePhoto(i); }} title={lang === "es" ? "Eliminar" : "Remove"} style={{ position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: 9, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: "16px", padding: 0 }}>{"×"}</button>
                        <div style={{ padding: "4px 6px", fontSize: 9 }}>
                          <div style={{ fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ph.name}</div>
                          <div style={{ color: "var(--color-text-muted)", marginTop: 1 }}>{ph.size}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Field>

          {/* Other Documents — now stored on the proforma so each
              proforma carries its own bundle (certs/photos/receipts
              specific to its load). Embarque-level legacy doc bucket
              above handles records created before the split. */}
          <div style={{ marginTop: 14 }}>
            <Field label={t.otherDocs}>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginBottom: 4, fontStyle: "italic" }}>{t.otherDocsHint}</div>
              {(function () {
                /* Storage upload — each file goes straight to the
                   `shipment-docs` bucket; the proforma row stores
                   only metadata + path/url. Path:
                     {supplier}/{emb}/{pro}/other/{file_id}.{ext} */
                var processOtherDocs = async function (files) {
                  if (!files || files.length === 0) return;
                  var prefix = pathPrefix() + "/" + safePathSegment(proforma.id || "_no-pro") + "/other";
                  var uploads = Array.prototype.map.call(files, async function (file) {
                    try {
                      var fid = Date.now() + "-" + Math.floor(Math.random() * 1000000);
                      var path = prefix + "/" + fid + "." + extFromFile(file);
                      var up = await uploadFileToStorage(SHIPMENT_DOCS_BUCKET, path, file);
                      return {
                        id: fid,
                        name: file.name,
                        type: file.type,
                        size: (file.size / 1024).toFixed(1) + " KB",
                        uploadedAt: new Date().toISOString(),
                        path: up.path,
                        url: up.publicUrl,
                      };
                    } catch (e) {
                      toastErr((lang === "es" ? "Error al subir " : "Upload failed for ") + (file.name || "?") + ": " + (e && e.message ? e.message : String(e)));
                      return null;
                    }
                  });
                  var results = (await Promise.all(uploads)).filter(Boolean);
                  if (results.length > 0) patch({ otherDocs: (proforma.otherDocs || []).concat(results) });
                };
                return (
                  <FileDropZone onFiles={processOtherDocs} lang={lang}>
                    <label style={{ display: "inline-block", padding: "6px 12px", borderRadius: 5, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                      {t.uploadBtn}
                      <input type="file" multiple onChange={function (e) { processOtherDocs(e.target.files); e.target.value = ""; }} style={{ display: "none" }} />
                    </label>
                  </FileDropZone>
                );
              })()}
              {(proforma.otherDocs || []).length > 0 && (
                <>
                  <div style={{ marginTop: 8, fontSize: 11, color: "var(--color-accent)", fontWeight: 600 }}>
                    {(proforma.otherDocs || []).length + " " + (lang === "es" ? "documento(s) cargado(s)" : "document(s) uploaded")}
                  </div>
                  <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
                    {(proforma.otherDocs || []).map(function (d, i) {
                      var isImg = d.type && String(d.type).indexOf("image/") === 0;
                      var rm = function () {
                        var next = (proforma.otherDocs || []).slice();
                        var removed = next.splice(i, 1)[0];
                        if (removed && removed.path) removeFromStorage(SHIPMENT_DOCS_BUCKET, removed.path);
                        patch({ otherDocs: next });
                      };
                      if (isImg) {
                        return (
                          <div key={d.id || i} style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "var(--color-bg)", position: "relative" }}>
                            <img src={d.url || d.data} alt={d.name} style={{ width: "100%", height: 80, objectFit: "cover", display: "block", background: "#000" }} />
                            <button type="button" onClick={rm} title={lang === "es" ? "Eliminar" : "Remove"} style={{ position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: 9, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: "16px", padding: 0 }}>{"×"}</button>
                            <div style={{ padding: "4px 6px", fontSize: 9 }}>
                              <div style={{ fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                              <div style={{ color: "var(--color-text-muted)", marginTop: 1 }}>{d.size}</div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={d.id || i} style={{ border: "1px solid rgba(16,185,129,0.3)", borderRadius: 6, background: "rgba(16,185,129,0.06)", padding: "8px 10px", position: "relative", minHeight: 70 }}>
                          <button type="button" onClick={rm} title={lang === "es" ? "Eliminar" : "Remove"} style={{ position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: 9, border: "none", background: "rgba(220,38,38,0.85)", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: "16px", padding: 0 }}>{"×"}</button>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#047857", fontSize: 14, fontWeight: 700 }}>✓ <span style={{ fontSize: 18 }}>📄</span></div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 14 }}>{d.name}</div>
                          <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>{d.size}{d.type ? " · " + d.type.split("/").pop().toUpperCase() : ""}</div>
                          {(d.url || d.data) && <div style={{ marginTop: 4 }}><a href={d.url || d.data} target="_blank" rel="noreferrer" style={{ fontSize: 9, color: "var(--color-accent)", textDecoration: "none", fontWeight: 600 }}>{lang === "es" ? "Ver" : "View"}</a></div>}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Field>
          </div>
        </div>
      )}

      {/* SECTION 6 — Notes (collapsible). */}
      <CollapseHeader id="notes" title={t.secNotes} />
      {secOpen.notes && (
        <div style={sectionBody}>
          <textarea value={proforma.notes || ""} onChange={function (e) { patch({ notes: e.target.value }); }} placeholder={t.notesPlaceholder} rows={3} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
        </div>
      )}
    </div>
  );
}

/* Action toolbar rendered ABOVE the proforma form per spec. Bundles
   Delete Proforma + Print Documents + Status badge + Submit so the
   primary actions are reachable without scrolling. Kept as a sibling
   component (instead of inlining) so EmbarqueModal can render it
   between the tab strip and the form body. */
function ProformaActionBar({ proforma, embarque, lang, t, locked, canSubmit, onSubmit, onDelete }) {
  /* Print-time gate: must pass the basic readiness check (has data
     to render) AND the full L1+L2 validator. Compose a tooltip from
     all active validation messages so the shipper sees exactly
     what's blocking print. */
  var readyForData = proformaReadyToPrint(embarque, proforma);
  var v = validateProforma(embarque, proforma, lang);
  var printErrs = (v.blocking || []).concat(v.red || []);
  var canPrint = readyForData && printErrs.length === 0;
  var blockMsg = !readyForData ? t.printNeedsData
    : (printErrs.length > 0 ? (lang === "es" ? "No se puede imprimir:" : "Cannot print:") + "\n" + printErrs.map(function (e) { return "• " + e; }).join("\n") : "");
  var doPrint = function (kind) {
    if (!canPrint) { alert(blockMsg); return; }
    var synth = buildSyntheticShipment(embarque, proforma);
    var ctx = {
      suppliers: loadLS("gfi-suppliers", []),
      warehouses: getWarehouses(),
      brokers: getBrokers(),
      commodities: loadLS("gfi-commodities", []),
      seasons: getSeasons(),
      lang,
    };
    printShipmentDocs([synth], kind, ctx, function (err) {
      if (err === "popup-blocked") alert(t.printPopupBlocked);
    });
  };
  var printBtn = function (kind, label, color) {
    return (
      <button key={kind} type="button" onClick={function () { doPrint(kind); }} title={canPrint ? "" : blockMsg} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid " + (canPrint ? color : "var(--color-border)"), background: canPrint ? "rgba(31,78,121,0.04)" : "var(--color-bg)", color: canPrint ? color : "var(--color-text-muted)", cursor: canPrint ? "pointer" : "not-allowed", fontSize: 11, fontWeight: 700, opacity: canPrint ? 1 : 0.55 }}>{"🖨 "}{label}</button>
    );
  };
  return (
    <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {!locked && <button type="button" onClick={onDelete} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delProforma}</button>}
        <span style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginLeft: 4 }}>{t.printDocs}:</span>
        {printBtn("carta-porte", t.printCartaPorte, "#2C3E50")}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <StatusBadge status={proforma.status || "draft"} t={t} />
        {!locked && <button type="button" onClick={onSubmit} title={canSubmit.ok ? "" : canSubmit.reason} style={{ padding: "6px 16px", borderRadius: 5, border: "none", background: canSubmit.ok ? "var(--color-accent)" : "#9ca3af", color: "#fff", cursor: canSubmit.ok ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700 }}>{proforma.status === "rejected" ? t.resubmit : t.submit}</button>}
      </div>
    </div>
  );
}

/* =================== EMBARQUE MODAL ===================
   Owns the embarque object and its proforma array. Renders the
   invoice header up top and tabs for each proforma below. */
function EmbarqueModal({ embarque, allEmbarques, shipper, lang, initialProformaId, growers, shipperMode, onSave, onSubmitProforma, onDelete, onCancel }) {
  var t = T[lang];
  var [f, setF] = useState(embarque);
  /* Brokers/warehouses catalogs (gfi-brokers/gfi-warehouses) are
     localStorage-only reads (getBrokers/getWarehouses) with no
     Supabase-freshness guarantee of their own — same staleness
     class as the consignee bug below. Arturo, 2026-09-16: "tampoco
     muestra el MC custom broker" (Mexican Customs Broker dropdown/
     read-only value came up empty even though the proforma has a
     real brokerMXId). Force a fresh pull once per modal open and
     bump catalogsVersion so ProformaForm's memoized broker/warehouse
     lists recompute against the refreshed mirror. */
  var [catalogsVersion, setCatalogsVersion] = useState(0);
  useEffect(function () {
    Promise.all([loadFromSupabase("brokers"), loadFromSupabase("warehouses")])
      .then(function () { setCatalogsVersion(function (v) { return v + 1; }); })
      .catch(function () {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var [dirty, setDirty] = useState(false);
  /* EmbarqueModal-level Send-Tracking-Link state — the button sits
     in the top toolbar (next to Bill of Lading / Delete Embarque)
     and stays available regardless of the active proforma's status. */
  var [embTrackingOpen, setEmbTrackingOpen] = useState(false);
  /* Quality Report (Texas QC) — read-only view for the shipper of the
     same PDF GFI already sent them by email, only once GFI has
     actually sent one (p.qualityReportSentAt). Arturo, 2026-09-16:
     "hay que agregar... los qc reports de texas al shipper portal
     para que sean visibles para ellos". Regenerated live from the
     synced palletManifest (tagId/texasQuality/texasCondition already
     propagate wholesale) rather than stored as a file, so it always
     matches whatever GFI's copy currently shows. */
  var [qcReportOpen, setQcReportOpen] = useState(false);
  var [qcCompany, setQcCompany] = useState(function () { return loadCompanyProfile(); });
  useEffect(function () {
    if (!qcReportOpen) return;
    loadFromSupabase("company_profile").then(function () { setQcCompany(loadCompanyProfile()); }).catch(function () {});
  }, [qcReportOpen]);
  /* Open to a specific proforma when the parent passes initialProformaId
     (row clicks from the proforma-centric main table); otherwise default
     to the first tab. */
  var [activeIdx, setActiveIdx] = useState(function () {
    if (initialProformaId) {
      var idx = (embarque.proformas || []).findIndex(function (p) { return String(p.id) === String(initialProformaId); });
      if (idx >= 0) return idx;
    }
    return 0;
  });

  /* Re-sync from prop when external save (e.g. after submit accepts
     a proforma in another tab) refreshes the upstream. */
  useEffect(function () { setF(embarque); }, [embarque]);

  var set = function (k, v) { setF(function (prev) { return { ...prev, [k]: v }; }); setDirty(true); };
  var updateProforma = function (idx, next) {
    setF(function (prev) {
      var ps = (prev.proformas || []).slice();
      ps[idx] = next;
      return { ...prev, proformas: ps };
    });
    setDirty(true);
  };
  var deleteProforma = function (idx) {
    var p = (f.proformas || [])[idx];
    if (!p) return;
    if (p.status === "submitted" || p.status === "accepted") { alert(t.embCantDelete); return; }
    if (!window.confirm(t.confirmDeleteProforma)) return;
    setF(function (prev) {
      var ps = (prev.proformas || []).slice(); ps.splice(idx, 1);
      return { ...prev, proformas: ps.length > 0 ? ps : [blankProforma(reserveProformaConsec(shipper, allEmbarques, activeSeason()), (activeSeason() || {}).id, shipper ? (shipper.shortName || shipper.fullName || "") : "")] };
    });
    setActiveIdx(0);
    setDirty(true);
  };

  /* Invoice-level file upload (single attachment). */
  var attachInvoice = function (files) {
    if (!files || files.length === 0) return;
    var file = files[0];
    var r = new FileReader();
    r.onload = function (ev) { set("invoiceFile", { name: file.name, size: (file.size / 1024).toFixed(1) + " KB", type: file.type, data: ev.target.result }); };
    r.readAsDataURL(file);
  };
  var removeInvoice = function () { set("invoiceFile", null); };

  /* Consignee block — GFI's company info, edited only by Arturo in
     Settings → Shipper Settings → Company Info. Shipper sees it
     read-only here so they know who to ship to and which USDA
     organic certificate references their flow. */
  /* Consignee — prefer the per-shipper-portal record (admin's
     Consignee Info tab writes there for legacy UX) and fall back to
     the global company_profile when that key hasn't been seeded.
     gfi-consignee-info is browser-local so a shipper user signing
     in on a different machine wouldn't see Arturo's value otherwise.
     company_profile lives in Supabase — but it's only ever loaded
     into THIS tab's localStorage mirror (gfi-company-profile) by
     primeCatalogs() at app boot. Arturo, 2026-09-16: "siguen mostrar
     la informacion del consignee entrando al shipper portal a traves
     del portal de GFI" — same recurring race/staleness bug already
     fixed elsewhere this session (signatures/accounts): a tab that
     opens the Shipper Portal before that priming finishes (or where
     it silently failed) finds gfi-company-profile empty and shows
     "not configured" even though the real data is sitting in
     Supabase the whole time. Fixed the same way as those other spots
     — force a fresh loadFromSupabase("company_profile") on mount so
     this never depends on priming having already succeeded. The
     field names differ (legalName vs companyName, organicCert vs
     organicCertNumber) — the mapping below normalizes them into the
     consignee shape the JSX downstream expects. */
  var buildConsignee = function () {
    var local = loadLS("gfi-consignee-info", null);
    if (local && local.companyName) return local;
    /* Same fix as getWarehouses/getBrokers above: prefer the fresh
       in-memory Supabase cache (populated as soon as the effect below
       calls loadFromSupabase("company_profile")) over the
       localStorage mirror, which only gets written by CompanyProfile.js
       itself and may never have been in this tab. */
    var cachedRecs = getCached("company_profile");
    var rec = (cachedRecs && cachedRecs.length > 0)
      ? (cachedRecs.find(function (r) { return String(r.id) === "gfi"; }) || cachedRecs[0])
      : (function () { var cp = loadLS("gfi-company-profile", null); return Array.isArray(cp) ? cp[0] : cp; })();
    if (!rec) return null;
    return {
      companyName:       rec.legalName || rec.companyName || "",
      addressLine:       rec.addressLine || "",
      city:              rec.city || "",
      state:             rec.state || "",
      zip:               rec.zip || "",
      country:           rec.country || "",
      phone:             rec.phone || "",
      email:             rec.email || "",
      website:           rec.website || "",
      organicCertNumber: rec.organicCert || rec.organicCertNumber || "",
      organicCertifier:  rec.organicCertifier || "",
      notes:             rec.notes || "",
    };
  };
  var [consignee, setConsignee] = useState(buildConsignee);
  useEffect(function () {
    if (consignee && consignee.companyName) return;
    loadFromSupabase("company_profile").then(function () {
      var next = buildConsignee();
      if (next && next.companyName) setConsignee(next);
    }).catch(function () {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Clear all editable fields of the active proforma AND the
     embarque-level fields the user sees in the same form (Shipper
     Invoice number + Invoice File + Export Cert File + legacy
     otherDocs). The identifying scaffold is preserved: id, consec
     (shipper's internal counter never re-issues), seasonId, status,
     and any lifecycle stamps (submittedAt / acceptedAt / rejectedAt /
     gfiLot / gfiShipmentId / rejectReason). The next-consec counter
     in Settings stays untouched, so no number is "lost".

     Note: clearing embarque-level fields affects ALL proformas in
     the embarque. That matches the confirm prompt's "data will be
     lost" warning and mirrors the GFI module's Clear Form behavior. */
  var clearProforma = function (idx) {
    var current = (f.proformas || [])[idx];
    if (!current) return;
    if (!window.confirm(t.confirmClear)) return;
    var fresh = blankProforma(current.consec, current.seasonId, shipper ? (shipper.shortName || shipper.fullName || "") : "");
    /* Restore identifiers + lifecycle metadata. */
    fresh.id = current.id;
    fresh.status = current.status || "draft";
    fresh.submittedAt = current.submittedAt || "";
    fresh.acceptedAt = current.acceptedAt || "";
    fresh.rejectedAt = current.rejectedAt || "";
    fresh.rejectReason = current.rejectReason || "";
    fresh.gfiLot = current.gfiLot || "";
    fresh.gfiShipmentId = current.gfiShipmentId || "";
    setF(function (prev) {
      return {
        ...prev,
        /* Embarque-level invoice fields reset to empty so the form
           visibly clears the Shipper Invoice number + attached file. */
        shipperInvoice: "",
        invoiceFile: null,
        exportCertFile: null,
        otherDocs: [],
        proformas: (prev.proformas || []).map(function (p, i) { return i === idx ? fresh : p; }),
      };
    });
    setDirty(true);
  };

  /* Add a new proforma. Copies fields from the first proforma
     (transport, brokers, departure date, lines minus qty) per spec. */
  var addProforma = function () {
    var season = activeSeason();
    var nextConsec = reserveProformaConsec(shipper, [...(allEmbarques || []).filter(function (e) { return e.id !== f.id; }), f], season);
    var source = (f.proformas || [])[0];
    var fresh = clonedProforma(source, nextConsec, (season || {}).id);
    setF(function (prev) { return { ...prev, proformas: [...(prev.proformas || []), fresh] }; });
    setActiveIdx((f.proformas || []).length); /* focus the newly added tab */
    setDirty(true);
  };

  /* Submission gate per proforma — wraps the shared 3-level
     validator. Returns the full { blocking, red, orange } so the
     submit handler can render a categorized summary, but exposes
     `ok` for the button-disabled state which gates only on Level 1
     (blocking). Level 2/3 warnings never disable the button — they
     pop a confirm dialog with the warning summary instead. */
  /* `ok` rolls up Level 1 (blocking) AND Level 2 (red) — both render
     as visible errors in the form, so both must be clean before the
     submit button enables. Level 3 (orange) stays as a soft warning
     (export certs / trace / grower codes arrive after the truck
     leaves, so they can't strictly block). */
  var canSubmitProforma = function (pro) {
    var v = validateProforma(f, pro, lang);
    return { ok: v.blocking.length === 0 && v.red.length === 0, blocking: v.blocking, red: v.red, orange: v.orange };
  };

  var doSave = function () { onSave(f); setDirty(false); };
  var doSubmitProforma = function (idx) {
    var pro = (f.proformas || [])[idx];
    if (!pro) return;
    var gate = canSubmitProforma(pro);
    if (!gate.ok) {
      /* Any L1 or L2 error blocks. Concatenate both lists in the
         alert so the shipper sees the full punch list. */
      var head = lang === "es" ? "No se puede enviar — corrige primero:\n\n" : "Can't submit — fix these first:\n\n";
      var allErrors = gate.blocking.concat(gate.red);
      alert(head + allErrors.map(function (m) { return "• " + m; }).join("\n"));
      return;
    }
    /* Orange (Level 3) is still a soft confirm — these are pending
       docs that the shipper acknowledges will arrive in transit. */
    if (gate.orange.length > 0) {
      var warn = lang === "es" ? "Hay pendientes que llegarán en tránsito:\n\n" : "Pending items that arrive in transit:\n\n";
      warn += (lang === "es" ? "NARANJA (en tránsito):\n" : "ORANGE (in transit):\n") + gate.orange.map(function (m) { return "  • " + m; }).join("\n") + "\n\n";
      warn += lang === "es" ? "¿Enviar de todos modos?" : "Submit anyway?";
      if (!window.confirm(warn)) return;
    } else if (!window.confirm(t.confirmSubmit)) return;
    /* Stamp the live US broker default at submit time so the proforma
       always carries Arturo's current pick. Shipper never edits this
       field; the form only displays it as locked read-only. */
    var us = resolveDefaultUSBroker(shipper && shipper.id);
    var brokerUSPatch = us
      ? { brokerUSId: us.id, brokerUSName: us.shortName || us.fullName || "" }
      : { brokerUSId: pro.brokerUSId || "", brokerUSName: pro.brokerUSName || "" };
    var stamped = { ...pro, ...brokerUSPatch, status: "submitted", submittedAt: new Date().toISOString() };
    var nextF = { ...f, proformas: (f.proformas || []).map(function (p, i) { return i === idx ? stamped : p; }) };
    setF(nextF);
    setDirty(false);
    onSubmitProforma(nextF, idx);
  };
  var doDelete = function () {
    var hasLocked = (f.proformas || []).some(function (p) { return p.status === "submitted" || p.status === "accepted"; });
    if (hasLocked) { alert(t.embCantDelete); return; }
    if (!window.confirm(t.confirmDeleteEmb)) return;
    onDelete(f.id);
  };

  var active = (f.proformas || [])[activeIdx] || (f.proformas || [])[0];
  var embStatus = worstEmbStatus(f);

  return (
    <DraggableModal title={t.embTitle + " · " + (lang === "es" ? "Factura" : "Invoice") + " " + (f.shipperInvoice || "—")} onClose={onCancel} defaultMaximized initialWidth={Math.min(1280, Math.floor(window.innerWidth * 0.88))} initialHeight={Math.min(900, window.innerHeight - 60)}>

      {/* Top identity + action strip — mirrors ShipmentRegistry's
          DetailPanel header. LEFT: PRO/LOT identity, shipper +
          embarque consec sub, status/week/season badges. RIGHT:
          print + submit + delete buttons. Reads the active proforma
          so it updates when the user switches tabs. The shipper
          invoice + invoice file used to live here in their own
          strip; they're now inside the form's collapsible sections
          (Shipment Header and Documents respectively) so the top
          stays clean and action-focused. */}
      {(function () {
        var p = active || {};
        var pStatus = p.status || "draft";
        var locked = pStatus === "submitted" || pStatus === "accepted";
        var readyForData = active ? proformaReadyToPrint(f, active) : false;
        var subInfo = canSubmitProforma(active || (f.proformas || [])[0] || {});
        /* Print gate combines basic readiness with the full L1+L2
           validator — print can't proceed while any field/manifest
           error is active. Tooltip lists every blocker by line. */
        var printV = active ? validateProforma(f, active, lang) : { blocking: [], red: [], orange: [] };
        var printErrs = (printV.blocking || []).concat(printV.red || []);
        var canPrint = readyForData && printErrs.length === 0;
        var printBlockMsg = !readyForData ? t.printNeedsData
          : (printErrs.length > 0 ? (lang === "es" ? "No se puede imprimir:" : "Cannot print:") + "\n" + printErrs.map(function (e) { return "• " + e; }).join("\n") : "");
        var doPrint = function (kind) {
          if (!canPrint) { alert(printBlockMsg); return; }
          var synth = buildSyntheticShipment(f, active);
          var ctx = {
            suppliers: loadLS("gfi-suppliers", []),
            warehouses: getWarehouses(),
            brokers: getBrokers(),
            commodities: loadLS("gfi-commodities", []),
            seasons: getSeasons(),
            lang,
          };
          printShipmentDocs([synth], kind, ctx, function (err) {
            if (err === "popup-blocked") alert(t.printPopupBlocked);
          });
        };
        /* Bulk-print: walk every proforma in this embarque, run the
           same readiness + validator gate the single-print button
           uses, keep only the ones that pass, and hand the resulting
           array of synthetic shipments to printShipmentDocs in one
           call. Each ready proforma renders as its own page in the
           combined document (printShipmentDocs already inserts a
           page break between shipments). */
        var allReady = [];
        (f.proformas || []).forEach(function (pr) {
          if (!proformaReadyToPrint(f, pr)) return;
          var v = validateProforma(f, pr, lang);
          var errs = (v.blocking || []).concat(v.red || []);
          if (errs.length > 0) return;
          allReady.push(pr);
        });
        var totalPros = (f.proformas || []).length;
        var canPrintAll = allReady.length > 0;
        var printAllLabel = (lang === "es"
          ? "Imprimir Todos los BOLs (" + allReady.length + "/" + totalPros + ")"
          : "Print All BOLs (" + allReady.length + "/" + totalPros + ")");
        var printAllTitle = canPrintAll
          ? (lang === "es"
              ? "Imprime " + allReady.length + " BOL(s) en un solo documento — uno por proforma. Las proformas con errores se omiten."
              : "Prints " + allReady.length + " BOL(s) as a single document — one per proforma. Proformas with errors are skipped.")
          : (lang === "es"
              ? "Ninguna proforma cumple los requisitos para imprimir."
              : "No proforma is ready to print.");
        /* Bundle every ready proforma's BOL into a single ZIP and
           hand it to the browser as a download — replaces the old
           print-window flow so mobile / PWA users don't get stuck
           with a popup they can't dismiss. Each row reuses the
           same { embarque, pro } shape filteredRows builds, so the
           shared downloadBolsAsZip helper handles both call sites
           with one signature. */
        var doPrintAll = async function () {
          if (!canPrintAll) { alert(printAllTitle); return; }
          var rows = allReady.map(function (pr) { return { embarque: f, pro: pr }; });
          var ctx = {
            suppliers: loadLS("gfi-suppliers", []),
            warehouses: getWarehouses(),
            brokers: getBrokers(),
            commodities: loadLS("gfi-commodities", []),
            seasons: getSeasons(),
            lang,
          };
          var invoice = f.shipperInvoice || f.consecEmbarque || "BOLs";
          var zipName = "BOLs_" + invoice;
          try {
            await downloadBolsAsZip(rows, ctx, zipName);
          } catch (err) {
            alert((lang === "es" ? "Error al generar ZIP: " : "Error generating ZIP: ") + (err && err.message ? err.message : err));
          }
        };
        /* LOT [number] big when accepted, otherwise PRO [consec]. */
        var titleLabel = p.gfiLot ? "LOT " + p.gfiLot : "PRO " + (p.consec || "—");
        var wk = getISOWeekNum(p.dateDeparture);
        var seasonObj = seasonForDate(p.dateDeparture || p.dateArrival || f.createdAt || "");
        var seasonStr = seasonLabel(seasonObj);
        return (
          <div style={{ padding: "16px 22px 12px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            {/* LEFT — identity */}
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "var(--color-accent)" }}>{titleLabel}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)", marginTop: 2 }}>
                {shipper ? (shipper.shortName || shipper.fullName) : "—"}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                <StatusBadge status={pStatus} t={t} />
                {wk && <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600, background: "var(--color-bg-card)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)" }}>{t.weekShort + wk}</span>}
                {seasonStr && seasonStr !== "—" && <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600, background: "var(--color-bg-card)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)" }}>{seasonStr}</span>}
              </div>
            </div>
            {/* RIGHT — actions (print + submit + deletes) */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 720 }}>
              <button onClick={function () { doPrint("carta-porte"); }} title={canPrint ? "" : printBlockMsg} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + (canPrint ? "#2C3E50" : "var(--color-border)"), background: canPrint ? "rgba(44,62,80,0.06)" : "var(--color-bg)", color: canPrint ? "#2C3E50" : "var(--color-text-muted)", cursor: canPrint ? "pointer" : "not-allowed", fontSize: 11, fontWeight: 700, opacity: canPrint ? 1 : 0.55 }}>{"🖨 "}{t.printCartaPorte}</button>
              <button onClick={function () { doPrintAll("carta-porte"); }} title={printAllTitle} disabled={!canPrintAll} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + (canPrintAll ? "#2C3E50" : "var(--color-border)"), background: canPrintAll ? "rgba(44,62,80,0.06)" : "var(--color-bg)", color: canPrintAll ? "#2C3E50" : "var(--color-text-muted)", cursor: canPrintAll ? "pointer" : "not-allowed", fontSize: 11, fontWeight: 700, opacity: canPrintAll ? 1 : 0.55 }}>{"📦 "}{printAllLabel}</button>
              <button onClick={function () { setEmbTrackingOpen(true); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #1B3A4B", background: "rgba(27,58,75,0.06)", color: "#1B3A4B", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"📡 "}{t.sendTrackingLink}</button>
              {p.qualityReportSentAt && <button onClick={function () { setQcReportOpen(true); }} title={(lang === "es" ? "Enviado " : "Sent ") + new Date(p.qualityReportSentAt).toLocaleString()} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #1B3A4B", background: "rgba(27,58,75,0.06)", color: "#1B3A4B", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"🔬 "}{lang === "es" ? "Reporte de Calidad" : "Quality Report"}</button>}
              {active && !locked && <button onClick={function () { doSubmitProforma(activeIdx); }} title={subInfo.ok ? "" : (subInfo.blocking || []).join(", ")} style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: subInfo.ok ? "var(--color-accent)" : "#9ca3af", color: "#fff", cursor: subInfo.ok ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700 }}>{pStatus === "rejected" ? t.resubmit : t.submit}</button>}
              {active && !locked && <button onClick={function () { clearProforma(activeIdx); }} title={t.confirmClear} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"🧹 "}{t.clearForm}</button>}
              {active && !locked && <button onClick={function () { deleteProforma(activeIdx); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delProforma}</button>}
              {f.id && <button onClick={doDelete} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.4)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{t.del}</button>}
            </div>
          </div>
        );
      })()}

      {/* Consignee card — read-only GFI info pulled from
          gfi-consignee-info. */}
      <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg-card)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)" }}>{t.consignee}</div>
          <div style={{ fontSize: 9, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.consigneeHint}</div>
        </div>
        {!consignee || !consignee.companyName ? (
          <div style={{ fontSize: 11, color: "#B91C1C", fontStyle: "italic" }}>{t.consigneeMissing}</div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)" }}>{consignee.companyName}</div>
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
                {[consignee.addressLine, [consignee.city, consignee.state, consignee.zip].filter(Boolean).join(", "), consignee.country].filter(function (s) { return s && String(s).trim(); }).join(" · ")}
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>
                {consignee.phone && <span>Tel: {consignee.phone}</span>}
                {consignee.email && <span>Email: {consignee.email}</span>}
              </div>
            </div>
            {consignee.organicCertNumber && (
              <div style={{ padding: "5px 10px", background: "rgba(42,122,111,0.08)", border: "1px solid rgba(42,122,111,0.25)", borderRadius: 5, whiteSpace: "nowrap" }}>
                <div style={{ fontSize: 8, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>USDA Organic</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", fontSize: 12 }}>{consignee.organicCertNumber}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Proforma tabs */}
      <div style={{ padding: "8px 20px 0", borderBottom: "1px solid var(--color-border)", display: "flex", gap: 4, alignItems: "flex-end", overflowX: "auto" }}>
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 8, alignSelf: "center" }}>{t.proformasIn}:</div>
        {(f.proformas || []).map(function (p, i) {
          var st = p.status || "draft";
          var c = STATUS_COLORS[st];
          var isActive = i === activeIdx;
          return (
            <button key={p.id || i} onClick={function () { setActiveIdx(i); }} style={{
              padding: "6px 12px", border: "1px solid " + (isActive ? "var(--color-accent)" : "var(--color-border)"),
              borderBottom: isActive ? "1px solid var(--color-bg-card)" : "1px solid var(--color-border)",
              borderTopLeftRadius: 5, borderTopRightRadius: 5,
              background: isActive ? "var(--color-bg-card)" : "var(--color-bg)",
              cursor: "pointer", fontSize: 11, fontWeight: 600, color: isActive ? "var(--color-accent)" : "var(--color-text-secondary)",
              marginBottom: -1,
            }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.consec || "—"}</span>
              <span style={{ display: "inline-block", marginLeft: 6, padding: "1px 6px", borderRadius: 6, fontSize: 8, background: c.bg, color: c.text }}>{t["status" + (st.charAt(0).toUpperCase() + st.slice(1))]}</span>
              {p.gfiLot && <span style={{ marginLeft: 4, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: "var(--color-accent)" }}>· LOT {p.gfiLot}</span>}
            </button>
          );
        })}
        <button onClick={addProforma} style={{ padding: "6px 12px", border: "1px dashed var(--color-accent)", borderRadius: 5, background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 700, marginLeft: 4, marginBottom: -1 }}>{t.addProforma}</button>
      </div>

      {/* Active proforma form body — actions live in the top
          identity strip above; no per-form action bar here.
          2026-09-14 — Arturo (screenshot): on an embarque whose BOL
          had already been sent (with a long recipient list), the
          modal got stuck — no way to scroll down to the Close/Save
          footer. Root cause: the "BOL enviado al aduanal" block below
          used to be a SIBLING placed AFTER this div instead of inside
          it, so it (and the real footer after it) sat outside this
          flex:1 overflow:auto region — DraggableModal's own
          fixed-header/scroll-body/fixed-footer contract only holds
          when there's exactly one scrollable region; a second
          fixed-height block wedged between the scroll area and the
          footer just pushed the footer off dm-window's clipped
          height with nothing able to reach it. Moved the BOL block
          inside here (scrolls with the form) so only the real footer
          stays fixed. */}
      <div style={{ padding: "14px 20px", overflowY: "auto", flex: 1, background: "var(--color-bg-card)" }}>
        {/* 2026-09-14 — Arturo, right after the scroll fix above: on
            phone/iPad this block (especially a long recipient list)
            still ate most of the visible scroll space up top before
            reaching the actual proforma fields — "no necesito ver eso
            en el telefono ni iPad, filtralo". Hidden on narrow/tablet
            viewports via the `.bol-sent-broker-block` CSS rule in
            App.css (same <=900px breakpoint the rest of the app's
            mobile handling uses); still shown on desktop, where there
            was never a complaint about it. */}
        {embarque.bolSentAt && (
          <div className="bol-sent-broker-block" style={{ padding: "10px 14px", marginBottom: 14, borderRadius: 6, border: "1px solid var(--color-border)", background: "rgba(5,150,105,0.05)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#059669", marginBottom: 4 }}>
              {"🛃 "}{lang === "es" ? "BOL enviado al aduanal" : "BOL sent to broker"}
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 6 }}>
              {(lang === "es" ? "Enviado el " : "Sent on ") + new Date(embarque.bolSentAt).toLocaleString(lang === "es" ? "es-MX" : "en-US")}
            </div>
            {(embarque.bolSentTo || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(embarque.bolSentTo || []).map(function (rr, i) {
                  return (
                    <span key={i} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", color: "var(--color-text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>
                      {(rr.name ? rr.name + " · " : "") + (rr.email || "")}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {active && <ProformaForm
          key={active.id}
          proforma={active}
          catalogsVersion={catalogsVersion}
          embarque={f}
          onChangeEmbarque={function (patch) { setF(function (prev) { return { ...prev, ...patch }; }); setDirty(true); }}
          /* Patch + persist in one shot. Used by document-upload
             helpers so attaching a file commits to Supabase even
             when the proforma is locked (submitted/accepted) and
             the Save button is not available. */
          onPersistEmbarque={function (patch) {
            setF(function (prev) {
              var next = { ...prev, ...patch };
              try { onSave(next); } catch (e) { /* surfaced by handleSave's toast */ }
              return next;
            });
            setDirty(false);
          }}
          allEmbarques={allEmbarques}
          shipper={shipper}
          growers={growers}
          shipperMode={shipperMode}
          lang={lang}
          onChange={function (next) { updateProforma(activeIdx, next); }}
        />}
      </div>

      {/* Embarque footer — Close + Save only per spec. All other
          actions (delete embarque, delete proforma, print, submit)
          live in the top action bar above. */}
      <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", alignItems: "center", background: "var(--color-bg)", gap: 6 }}>
        <button type="button" onClick={onCancel} style={{ padding: "6px 14px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.close}</button>
        <button type="button" onClick={doSave} disabled={!dirty} style={{ padding: "6px 14px", borderRadius: 5, border: "1px solid var(--color-border)", background: dirty ? "var(--color-bg-input)" : "var(--color-bg)", color: "var(--color-text)", cursor: dirty ? "pointer" : "default", fontSize: 12, fontWeight: 600, opacity: dirty ? 1 : 0.5 }}>{t.save}</button>
      </div>
      {embTrackingOpen && (function () {
        var activeProf = (f.proformas || [])[activeIdx] || {};
        return (
          <TrackingLinkModal
            lang={lang}
            payload={{
              id: f.id || null,
              lot: activeProf.consec || activeProf.proformaNumber || f.consecEmbarque || "",
              shipperName: (shipper && (shipper.shortName || shipper.fullName)) || f.shipperName || "",
              transport: activeProf.transport || {},
            }}
            onClose={function () { setEmbTrackingOpen(false); }}
          />
        );
      })()}
      {qcReportOpen && (function () {
        var activeProf = (f.proformas || [])[activeIdx] || {};
        var synth = buildSyntheticShipment(f, activeProf);
        return (
          <QualityReportPreview
            shipment={synth}
            supplier={shipper}
            company={qcCompany}
            onClose={function () { setQcReportOpen(false); }}
          />
        );
      })()}
    </DraggableModal>
  );
}

/* =================== LIST / SHELL =================== */
/* `lockSupplierId` (optional) wires the Shipper Portal into the
   actual shipper user's view: the resolver below uses this id to
   find their supplier record instead of falling back to the first
   shipper in the catalog. When falsy the behavior is unchanged —
   admin staff browsing the Shipper Portal module sees whichever
   shipper getCurrentShipper would historically pick. */
/* Build the distribution list for the BOL email. Order:
     1. Admin (Arturo) — LOCKED, always checked, can't be unchecked.
        Pulled from the Outbound Email in Company Profile.
     2. Default US Customs Broker contacts (from Shipper Settings).
     3. Selected MX Customs Broker contacts on the proforma.
     4. Shipper's own contacts (so they get their own copy on file).
   Each non-admin row is `checked: true` so the shipper can quickly
   uncheck unwanted addresses. Duplicates are deduped by email
   (case-insensitive). */
function buildBolRecipients(embarque, proforma, shipper, lang, t) {
  var company = loadCompanyProfile();
  var brokers = getBrokers();
  var rows = [];
  var seen = {};
  function push(name, email, opts) {
    if (!email) return;
    var key = String(email).trim().toLowerCase();
    if (!key || seen[key]) return;
    seen[key] = true;
    rows.push({ name: name || "", email: email, checked: true, locked: !!(opts && opts.locked) });
  }
  function contactName(c) {
    var parts = [c.firstName || c.nombre || "", c.middleName || "", c.lastName || c.apPaterno || ""].filter(Boolean);
    return parts.join(" ").trim() || c.email || "";
  }
  /* (1) Admin — required */
  push(t.emailBolRecipientAdmin, company.outboundEmail || company.email || "", { locked: true });
  /* (2) Default US broker contacts */
  var us = resolveDefaultUSBroker(shipper && shipper.id);
  if (us && Array.isArray(us.contacts)) {
    us.contacts.forEach(function (c) {
      var who = (contactName(c) || us.shortName || us.fullName || "") + " · " + t.emailBolRecipientUSBroker;
      push(who, c.email);
    });
  }
  /* (3) MX broker selected on the proforma */
  var mxId = proforma && proforma.brokerMXId;
  if (mxId) {
    var mx = brokers.find(function (b) { return String(b.id) === String(mxId) || String(b._uuid) === String(mxId); });
    if (mx && Array.isArray(mx.contacts)) {
      mx.contacts.forEach(function (c) {
        var who = (contactName(c) || mx.shortName || mx.fullName || "") + " · " + t.emailBolRecipientMXBroker;
        push(who, c.email);
      });
    }
  }
  /* (4) Shipper's own contacts */
  if (shipper && Array.isArray(shipper.contacts)) {
    var shipperLabel = shipper.shortName || shipper.fullName || (lang === "es" ? "Exportador" : "Shipper");
    shipper.contacts.forEach(function (c) {
      push((contactName(c) || shipperLabel) + " · " + shipperLabel, c.email);
    });
  }
  return rows;
}

export default function ShipperPortal({ lang: globalLang, lockSupplierId, profile, openLot, onLotOpened }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [embarques, setEmbarques] = useState([]);
  /* Refresca gfi-commodities localStorage desde Supabase al montar
     el portal — sin esto las dropdowns de variedades quedan con la
     cache stale del navegador. commsVer trigger re-render post-fetch
     para que varietiesForCommodity re-lea el cache actualizado. */
  var [commsVer, setCommsVer] = useState(0);
  useEffect(function () {
    loadFromSupabase("commodities")
      .then(function (rows) {
        try { localStorage.setItem("gfi-commodities", JSON.stringify(rows || [])); } catch (e) {}
        setCommsVer(function (v) { return v + 1; });
      })
      .catch(function () {});
  }, []);
  void commsVer;
  var [showForm, setShowForm] = useState(false);
  var [editing, setEditing] = useState(null);
  var [toast, setToast] = useState(null);
  /* Multi-select for bulk print, mirrors ShipmentRegistry's pattern.
     Keyed by "embarqueId::proformaId" so each row is uniquely
     selectable across embarques. */
  var [selected, setSelected] = useState({});
  /* List-level Send-Tracking-Link state. Holds the resolved payload
     when the toolbar button is clicked with exactly one row selected. */
  var [listTrackingOpen, setListTrackingOpen] = useState(null);
  /* Email-BOL flow. The fixed "📧 Email BOL" button in the top action
     row opens a picker (showEmailPicker = true). The shipper picks an
     embarque, which closes the picker and sets emailingEmbarque — the
     full embarque, NOT a single proforma, since one email may include
     multiple BOL PDFs (one per proforma on that embarque). */
  var [showEmailPicker, setShowEmailPicker] = useState(false);
  var [emailingEmbarque, setEmailingEmbarque] = useState(null);
  var [search, setSearch] = useState("");
  var [filterStatus, setFilterStatus] = useState("");
  var [filterCert, setFilterCert] = useState("");
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  /* Xero-style filter popover — cert + season live behind ⋮; status
     is handled by the tabs row, so it's intentionally not duplicated
     in the popover. */
  var [showFilters, setShowFilters] = useState(false);
  var filtersRef = useRef(null);
  useEffect(function () {
    if (!showFilters) return;
    function onDown(e) { if (filtersRef.current && !filtersRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setShowFilters(false); }
    function onKey(e) { if (e.key === "Escape") setShowFilters(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function () { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [showFilters]);
  var activeFilterCount = (filterCert ? 1 : 0) + ((filterSeasons && filterSeasons.length > 0) ? 1 : 0);
  /* Transient sort state — column key + asc/desc. Default is most-
     recent submitted/created first (dateDeparture desc as a proxy). */
  var [sortCol, setSortCol] = useState("proforma");
  var [sortDir, setSortDir] = useState("desc");
  /* When a row is clicked we open the embarque modal and tell it
     which proforma tab to focus. */
  var [initialProformaId, setInitialProformaId] = useState(null);
  var [reloadKey, setReloadKey] = useState(0);
  var seasons = useMemo(function () { return getSeasons(); }, [reloadKey]);

  /* When the Shipper Portal Shell renders us with lockSupplierId, we
     pin the shipper to that exact supplier so a real shipper user
     can never see another tenant's data. Admin staff (no
     lockSupplierId) keep the legacy "first shipper" resolver. */
  var shipper = useMemo(function () {
    if (lockSupplierId) {
      var sups = getSuppliers();
      var hit = sups.find(function (s) { return String(s.id) === String(lockSupplierId); });
      if (hit) return hit;
    }
    return getCurrentShipper();
  }, [reloadKey, lockSupplierId]);

  /* Growers belonging to this shipper — only loaded when a real
     shipper is locked into the view. Feeds the per-pallet "Cód.
     Productor" dropdown in ProformaForm with entries like
     "001 - Juan Sandoval" instead of a free-text input (FIX 8).
     Admin Shipper Portal stays as a text input. */
  var [growers, setGrowers] = useState([]);
  /* Seed gfi-commodities if missing — the commodities catalog
     (with variety lists per fruit) is normally created by
     ProductRegistry on its first mount. The shipper portal never
     opens ProductRegistry, so without this seed varietiesForCommodity
     returns [] and the Variety dropdown is disabled even though the
     field is marked required. Defaults match ProductRegistry.INIT_COMM.
     Run once on mount with NOT (have-data) guard so an admin's
     real catalog isn't clobbered. */
  useEffect(function () {
    try {
      var existing = JSON.parse(localStorage.getItem("gfi-commodities") || "null");
      if (Array.isArray(existing) && existing.length > 0) return;
      var seed = [
        { id: 1, nameEn: "Strawberry", nameEs: "Fresa", htsCode: "08101001", taxCode: "50302001", varieties: ["Albion", "Fronteras", "San Andreas"] },
        { id: 2, nameEn: "Blueberry", nameEs: "Arándano", htsCode: "08104002", taxCode: "50302002", varieties: ["Biloxi", "Legacy", "Star"] },
        { id: 3, nameEn: "Blackberry", nameEs: "Zarzamora", htsCode: "08102001", taxCode: "50303003", varieties: ["Tupy", "Kiowa", "Brazos", "Natchez"] },
        { id: 4, nameEn: "Raspberry", nameEs: "Frambuesa", htsCode: "08104003", taxCode: "50302004", varieties: ["Centennial", "Malu", "Driscoll's Maravilla"] },
        { id: 5, nameEn: "Avocado", nameEs: "Aguacate", htsCode: "08044001", taxCode: "50301001", varieties: ["Hass", "Fuerte", "Lamb Hass"] },
        { id: 6, nameEn: "Lime", nameEs: "Limón", htsCode: "08055001", taxCode: "50301005", varieties: ["Persa", "Key Lime"] },
      ];
      localStorage.setItem("gfi-commodities", JSON.stringify(seed));
    } catch (e) { /* quota / parse — non-fatal, varietiesForCommodity will return [] */ }
  }, []);
  useEffect(function () {
    if (!lockSupplierId) { setGrowers([]); return; }
    let alive = true;
    loadFromSupabase("growers")
      .then(function (rows) {
        if (!alive) return;
        var mine = (rows || []).filter(function (g) {
          return String(g.shipperId) === String(lockSupplierId) && g.isActive !== false;
        });
        setGrowers(mine);
      })
      .catch(function () { if (alive) setGrowers([]); });
    return function () { alive = false; };
  }, [lockSupplierId, reloadKey]);

  /* Cert filter — options from gfi-export-certs, scoped to this
     shipper's certs (the shared registry contains every shipper's
     certs, so without scoping the dropdown would surface irrelevant
     ones). Refreshed on `reloadKey` so newly created certs appear
     after a focus event. */
  var certFilterOptions = useMemo(function () {
    var raw = loadLS("gfi-export-certs", []);
    var supId = shipper ? String(shipper.id) : "";
    var seen = {};
    var arr = [];
    raw.forEach(function (c) {
      if (!c || seen[c.id]) return;
      if (supId && String(c.supplierId) !== supId) return;
      seen[c.id] = true;
      arr.push({ value: c.id, label: c.certNumber || c.id });
    });
    arr.sort(function (a, b) { return String(a.label).localeCompare(String(b.label)); });
    return arr;
  }, [shipper, reloadKey]);
  var certIdToNum = useMemo(function () {
    var m = {};
    loadLS("gfi-export-certs", []).forEach(function (c) { if (c && c.id) m[c.id] = nrmCertNum(c.certNumber); });
    return m;
  }, [reloadKey]);

  var aliveRef = useRef(true);
  /* Tracks whether the currently-open EmbarqueModal came from the
     openLot deep link, so onCancel knows to fire the
     "gfi-shipment-closed" return-navigation event. */
  var openedFromExternalRef = useRef(false);
  /* Source of truth = Supabase `shipper_shipments`. Dual-write to
     localStorage so ShipmentRegistry's acceptPortalSubmission /
     rejectAcceptedShipment flow (still on localStorage until BillReg/
     etc. settle) sees fresh embarque state. Drop the dual write once
     all cross-readers are migrated. */
  var writeEmbarquesCache = function (next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };
  useEffect(function () {
    aliveRef.current = true;
    loadFromSupabase("shipper_shipments", { force: reloadKey > 0 })
      .then(function (rows) { if (aliveRef.current) { setEmbarques(rows); writeEmbarquesCache(rows); } })
      .catch(function (e) {
        /* Fall back to localStorage so a transient Supabase outage
           doesn't blank the shipper portal — they can still see what
           they already had and try again on next focus. */
        if (aliveRef.current) {
          setEmbarques(loadLS(STORAGE_KEY, []));
          if (e && e.message) setToast("Supabase load failed: " + e.message);
        }
      });
    return function () { aliveRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  /* Re-pull when the tab regains focus — Arturo may have accepted
     or rejected a proforma in another tab, and the shipper should
     see the updated status / assigned lot without a manual refresh. */
  useEffect(function () {
    var onFocus = function () { setReloadKey(function (k) { return k + 1; }); };
    window.addEventListener("focus", onFocus);
    return function () { window.removeEventListener("focus", onFocus); };
  }, []);

  /* persistSingle: save one embarque to Supabase, update local state and
     localStorage cache. Used by handleSave / handleSubmitProforma.

     Diagnostic logs are intentionally verbose: this is the exact step
     where shipper-portal PDF uploads have repeatedly failed to reach
     the admin. The logs report sizes (not bodies) so devtools doesn't
     drown in base64. */
  var persistSingle = async function (emb) {
    /* Compact file summary — reports whichever payload pointer is
       present (Storage url/path or legacy base64 data) without
       dumping the body. The PRE-SAVE log confirms uploads to
       `shipment-docs` bucket completed before the embarque row goes
       to Supabase; POST-SAVE confirms the round-trip preserved the
       Storage refs. */
    var fileSummary = function (f) {
      if (!f) return '(none)';
      var bits = [f.name || '?'];
      if (typeof f.url === 'string' && f.url) bits.push('url=' + f.url.length + 'ch');
      if (typeof f.path === 'string' && f.path) bits.push('path=' + f.path);
      if (typeof f.data === 'string') bits.push('data=' + f.data.length + 'ch');
      return bits.join(' · ');
    };
    if (typeof console !== 'undefined' && console.log) {
      console.log('[ShipperPortal][PRE-SAVE]', {
        id: emb.id,
        consec: emb.consecEmbarque,
        invoiceFile: fileSummary(emb.invoiceFile),
        exportCertFile: fileSummary(emb.exportCertFile),
        otherDocs: (emb.otherDocs || []).length + ' file(s)',
        proformas: (emb.proformas || []).length,
        proformaStatuses: (emb.proformas || []).map(function (p) { return p.status; }),
      });
    }
    var saved = await saveToSupabase("shipper_shipments", emb);
    if (typeof console !== 'undefined' && console.log) {
      console.log('[ShipperPortal][POST-SAVE]', {
        id: saved.id,
        consec: saved.consecEmbarque,
        invoiceFile: fileSummary(saved.invoiceFile),
        exportCertFile: fileSummary(saved.exportCertFile),
        otherDocs: (saved.otherDocs || []).length + ' file(s)',
        docsCol: saved.docs ? Object.keys(saved.docs).join(',') : '(none)',
      });
    }
    var ex = embarques.find(function (x) { return x.id === emb.id; });
    var next = ex ? embarques.map(function (x) { return x.id === emb.id ? saved : x; }) : embarques.concat([saved]);
    setEmbarques(next); writeEmbarquesCache(next);
    return saved;
  };
  var persistDeleteEmb = async function (id) {
    await deleteFromSupabase("shipper_shipments", id);
    var next = embarques.filter(function (x) { return x.id !== id; });
    setEmbarques(next); writeEmbarquesCache(next);
  };

  /* Only this shipper's embarques. */
  var myEmbarques = useMemo(function () {
    if (!shipper) return [];
    return embarques.filter(function (e) { return String(e.supplierId) === String(shipper.id); });
  }, [embarques, shipper]);

  /* Counts are per-PROFORMA across all my embarques (since a proforma
     is the unit Arturo reviews). */
  var counts = useMemo(function () {
    var c = { draft: 0, submitted: 0, accepted: 0, rejected: 0 };
    myEmbarques.forEach(function (e) {
      (e.proformas || []).forEach(function (p) {
        var st = p.status || "draft";
        c[st] = (c[st] || 0) + 1;
      });
    });
    return c;
  }, [myEmbarques]);

  /* Flatten embarques → one row per proforma. Each row carries a
     reference to its parent embarque so we can pull invoice / docs
     and pass the right ids on click. */
  var rows = useMemo(function () {
    var out = [];
    myEmbarques.forEach(function (e) {
      (e.proformas || []).forEach(function (p) { out.push({ embarque: e, pro: p }); });
    });
    return out;
  }, [myEmbarques]);

  /* Apply search / status / cert / season filters to the flat list. */
  var filteredRows = useMemo(function () {
    var list = rows;
    if (filterStatus) list = list.filter(function (r) { return (r.pro.status || "draft") === filterStatus; });
    if (filterCert) {
      var wantNum = certIdToNum[filterCert] || "";
      list = list.filter(function (r) {
        return (r.pro.lines || []).some(function (l) {
          if (l.exportCertId && String(l.exportCertId) === String(filterCert)) return true;
          if (wantNum && nrmCertNum(l.exportCertNumber) === wantNum) return true;
          if (wantNum && nrmCertNum(l.exportCertManual) === wantNum) return true;
          return false;
        });
      });
    }
    if (filterSeasons && filterSeasons.length > 0) {
      list = list.filter(function (r) {
        var sid = resolveSeasonId(seasons, r.pro.dateDeparture || r.pro.dateArrival || r.embarque.createdAt || "", r.pro.seasonId || r.embarque.seasonId);
        return filterSeasons.indexOf(sid) >= 0;
      });
    }
    if (search) {
      var s = search.toLowerCase();
      list = list.filter(function (r) {
        return (r.embarque.shipperInvoice || "").toLowerCase().includes(s)
          || (r.embarque.consecEmbarque || "").toLowerCase().includes(s)
          || (r.pro.consec || "").toLowerCase().includes(s)
          || (r.pro.proformaNumber || "").toLowerCase().includes(s)
          || String(r.pro.gfiLot || "").includes(s)
          || (r.pro.warehouseName || "").toLowerCase().includes(s);
      });
    }
    return list;
  }, [rows, filterStatus, filterCert, certIdToNum, filterSeasons, search, seasons]);

  /* Sort the filtered flat list. Numeric columns parse the int;
     string columns localeCompare; date columns compare lexicographically
     (YYYY-MM-DD strings sort correctly that way). */
  var sortedRows = useMemo(function () {
    function rowPallets(r) { return (r.pro.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0); }
    function rowCases(r) { return (r.pro.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0); }
    function rowVal(r, col) {
      switch (col) {
        case "shipperInvoice": return String(r.embarque.shipperInvoice || "");
        /* Sort by the numeric tail of the consec (e.g. "GB-013" → 13)
           so DESC actually puts the newest proforma on top. String
           sort would put "GB-9" above "GB-10" lexicographically. */
        case "proforma": return parseInt(String(r.pro.consec || "0").replace(/\D/g, ""), 10) || 0;
        case "dateDeparture": return String(r.pro.dateDeparture || "");
        case "dateArrival": return String(r.pro.dateArrival || "");
        /* Week sorts by arrival ISO date — chronological order; the
           derived week number alone is ambiguous across years. */
        case "week": return String(r.pro.dateArrival || "");
        case "warehouse": return String(r.pro.warehouseName || "");
        case "pallets": return rowPallets(r);
        case "cases": return rowCases(r);
        case "status": return String(r.pro.status || "draft");
        case "gfiLot": { var n = parseInt(r.pro.gfiLot, 10); return isNaN(n) ? 0 : n; }
        default: return "";
      }
    }
    var arr = filteredRows.slice();
    arr.sort(function (a, b) {
      var av = rowVal(a, sortCol);
      var bv = rowVal(b, sortCol);
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      var as = String(av), bs = String(bv);
      return sortDir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return arr;
  }, [filteredRows, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) { setSortDir(sortDir === "asc" ? "desc" : "asc"); return; }
    setSortCol(col); setSortDir("desc");
  }
  function arrow(col) {
    if (sortCol !== col) return <span style={{ marginLeft: 4, color: "var(--color-text-muted)", opacity: 0.4 }}>↕</span>;
    return <span style={{ marginLeft: 4, color: "var(--color-accent)" }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  /* Toolbar Send-Tracking-Link handler. Selection keys are
     "embarqueId::proformaId"; we need exactly one selected row so
     the link binds to a single driver + LOT. */
  var openTrackingFromSelection = function () {
    var keys = sortedRows.map(function (r) { return r.embarque.id + "::" + r.pro.id; }).filter(function (k) { return !!selected[k]; });
    if (keys.length !== 1) {
      alert(lang === "es" ? "Selecciona un embarque" : "Select one shipment");
      return;
    }
    var row = sortedRows.find(function (r) { return r.embarque.id + "::" + r.pro.id === keys[0]; });
    if (!row) return;
    setListTrackingOpen({
      id: row.embarque.id || null,
      lot: row.pro.consec || row.pro.proformaNumber || row.embarque.consecEmbarque || "",
      shipperName: (shipper && (shipper.shortName || shipper.fullName)) || row.embarque.shipperName || "",
      transport: row.pro.transport || {},
    });
  };

  /* Toolbar "Email BOL" handler. Replaces the old picker: the shipper
     checks 1+ proformas in the table and clicks Email BOL. All selected
     proformas must belong to a SINGLE embarque (invoice) — one invoice =
     one email with ALL its proformas. A selection spanning two invoices
     is refused so each invoice goes out as its own email. */
  var startEmailBol = function () {
    var selRows = sortedRows.filter(function (r) { return !!selected[r.embarque.id + "::" + r.pro.id]; });
    if (selRows.length === 0) {
      alert(lang === "es" ? "Selecciona al menos una proforma para enviar su BOL." : "Select at least one proforma to email its BOL.");
      return;
    }
    var embIds = [];
    selRows.forEach(function (r) { if (embIds.indexOf(r.embarque.id) < 0) embIds.push(r.embarque.id); });
    if (embIds.length > 1) {
      alert(lang === "es"
        ? "Seleccionaste proformas de " + embIds.length + " facturas distintas. Los BOL se envían por factura — selecciona proformas de una sola factura y envía una a la vez."
        : "You selected proformas from " + embIds.length + " different invoices. BOLs are sent per invoice — select proformas from a single invoice and send one at a time.");
      return;
    }
    setEmailingEmbarque(selRows[0].embarque);
  };

  var newEmbarque = function () {
    if (!shipper) { alert(lang === "es" ? "No hay shipper configurado. Crea uno en Proveedores primero." : "No shipper configured. Create one in Suppliers first."); return; }
    /* Block creation while the shipper has an unresolved rejection.
       "Unresolved" means at least one proforma is still in the
       "rejected" status — i.e., the shipper hasn't deleted the
       embarque, deleted that proforma, or resubmitted with fixes
       (which flips status back to "submitted"). */
    var hasRejected = (embarques || []).some(function (e) {
      if (String(e.supplierId) !== String(shipper.id)) return false;
      return (e.proformas || []).some(function (p) { return p.status === "rejected"; });
    });
    if (hasRejected) {
      alert(lang === "es"
        ? "Tienes un embarque rechazado pendiente. Elimínalo o corrígelo antes de crear uno nuevo."
        : "You have a rejected shipment pending. Delete or fix it before creating a new one.");
      return;
    }
    var season = activeSeason();
    var embConsec = reserveEmbConsec(shipper, embarques, season);
    var proConsec = reserveProformaConsec(shipper, embarques, season);
    var blank = {
      id: "SHP-EMB-" + Date.now(),
      createdAt: new Date().toISOString(),
      supplierId: shipper.id,
      shipperName: shipper.shortName || shipper.fullName || "",
      shipperInitials: shipper.initials || "",
      seasonId: season ? String(season.id) : "",
      consecEmbarque: embConsec,
      shipperInvoice: "",
      invoiceFile: null,
      exportCertFile: null,
      /* otherDocs at embarque level kept for backward compatibility
         with records created before docs were split into shipment-
         vs proforma-level. New uploads go to proforma.otherDocs. */
      otherDocs: [],
      notes: "",
      proformas: [blankProforma(proConsec, season ? String(season.id) : "", shipper.shortName || shipper.fullName || "")],
    };
    setEditing(blank);
    setShowForm(true);
  };

  var openEmbarque = function (e, proformaId) { setInitialProformaId(proformaId || null); setEditing(e); setShowForm(true); };

  /* PalletBreakdown Lot-cell deep link — same open path as the row
     onClick (openEmbarque(emb, p.id)). Only clears openLot on an
     actual match so it survives the async embarques load instead of
     firing against an empty array on first render. */
  useEffect(function () {
    if (!openLot) return;
    var foundEmb = null, foundP = null;
    (embarques || []).some(function (emb) {
      var p = (emb.proformas || []).find(function (pp) { return String(pp.gfiLot || "") === String(openLot); });
      if (p) { foundEmb = emb; foundP = p; return true; }
      return false;
    });
    if (foundEmb) {
      openEmbarque(foundEmb, foundP.id);
      openedFromExternalRef.current = true;
      if (onLotOpened) onLotOpened();
    }
  }, [openLot, embarques]);

  /* BOL-to-broker tracking (feature #3). An embarque counts as "sent"
     once bolSentAt exists (set by the Email-BOL onSent). The red
     "not sent yet" highlight only applies to embarques created on/after
     the deploy date, so historical embarques that crossed before this
     feature existed never turn red. _createdAt is the Supabase timestamp;
     createdAt is the in-memory value for a brand-new unsaved embarque. */
  var BOL_TRACKING_SINCE = "2026-06-05";
  var bolRowState = function (emb) {
    if (emb && emb.bolSentAt) return "sent";
    var created = (emb && (emb._createdAt || emb.createdAt)) || "";
    if (created >= BOL_TRACKING_SINCE) return "pending";
    return "legacy";
  };

  /* 2026-10-13, Arturo: a shipment he captured directly in Ground-
     Fresh (proforma GF-001) and one Gomez Berries submitted
     independently through this portal both landed on GF-001 for the
     same shipper — "deben estar en vivo los consecutivos en ambos
     lados". reserveProformaConsec (above) only ever reads the LOCAL
     gfi-shipper-settings cache, never a fresh Supabase read — if
     either side's browser cache is stale when it reserves a number,
     both sides can independently compute the same "next" value. Fix
     mirrors the one just added to ShipmentRegistry.js's own
     handleSave: right before the real write, re-check every proforma
     about to be saved against a FRESH read of both GFI's own
     shipments and every other embarque's proformas for this shipper,
     and bump forward to the next free number on a collision. */
  var resolveProformaCollisions = async function (emb) {
    var supplierId = shipper && (shipper._uuid || shipper.id);
    if (!supplierId || !Array.isArray(emb.proformas) || emb.proformas.length === 0) return emb;
    try {
      var freshShipments = await loadFromSupabase("shipments", { force: true });
      var freshPortalRows = await loadFromSupabase("shipper_shipments", { force: true });
      /* 2026-10-30, Arturo: proforma numbers can repeat across
         different seasons (e.g. A002 in 2025-2026 reused in
         2026-2027) — track "used" numbers keyed by season, not
         globally, so a bump only triggers on a real same-season
         collision. Numbers with no season recorded (legacy rows)
         are tracked under a single "" bucket, same as before. */
      var usedBySeason = {};
      var addUsed = function (seasonId, consec) {
        var key = String(seasonId || "");
        if (!usedBySeason[key]) usedBySeason[key] = new Set();
        usedBySeason[key].add(String(consec).toUpperCase());
      };
      var isUsed = function (seasonId, consec) {
        var key = String(seasonId || "");
        return !!(usedBySeason[key] && usedBySeason[key].has(String(consec).toUpperCase()));
      };
      freshShipments.forEach(function (x) {
        if (x && String(x.supplierId) === String(supplierId) && x.proforma) addUsed(x.seasonId, x.proforma);
      });
      freshPortalRows.forEach(function (p) {
        if (!p || String(p.supplierId) !== String(supplierId)) return;
        if (p.id === emb.id || p.legacy_id === emb.id) return; // this same embarque — handled per-proforma below, not as "already used"
        (p.proformas || []).forEach(function (pr) { if (pr && pr.consec) addUsed(pr.seasonId, pr.consec); });
      });
      var nextProformas = emb.proformas.map(function (pr) {
        if (!pr || !pr.consec) return pr;
        if (!isUsed(pr.seasonId, pr.consec)) { addUsed(pr.seasonId, pr.consec); return pr; }
        var m = /^(.*?)(\d+)$/.exec(String(pr.consec));
        if (!m) return pr;
        var prefix = m[1], width = m[2].length, n = parseInt(m[2], 10), candidate;
        do { n += 1; candidate = prefix + String(n).padStart(width, "0"); } while (isUsed(pr.seasonId, candidate));
        addUsed(pr.seasonId, candidate);
        console.warn("[ShipperPortal] proforma collision detected, bumped " + pr.consec + " -> " + candidate);
        return Object.assign({}, pr, { consec: candidate });
      });
      return Object.assign({}, emb, { proformas: nextProformas });
    } catch (e) {
      console.warn("[ShipperPortal] proforma collision check failed:", e && e.message);
      return emb;
    }
  };
  var handleSave = async function (emb) {
    try {
      var adjustedEmb = await resolveProformaCollisions(emb);
      var saved = await persistSingle(adjustedEmb);
      /* Advance the per-shipper counters only AFTER the embarque /
         its proformas actually persisted. Reserve/preview was
         already non-mutating; cancel = no counter advance. */
      commitEmbConsec(shipper, saved.consecEmbarque);
      (saved.proformas || []).forEach(function (p) { commitProformaConsec(shipper, p.consec); });
      setToast(t.submitted);
      setEditing(saved);
    } catch (e) {
      setToast("⚠ " + (e.message || String(e)));
    }
  };
  var handleSubmitProforma = async function (emb) {
    /* The proforma's status was already flipped by the form; we just
       persist the embarque. */
    try {
      var adjustedEmb = await resolveProformaCollisions(emb);
      var saved = await persistSingle(adjustedEmb);
      commitEmbConsec(shipper, saved.consecEmbarque);
      (saved.proformas || []).forEach(function (p) { commitProformaConsec(shipper, p.consec); });
      setToast(t.sent);
      setEditing(saved);
    } catch (e) {
      setToast("⚠ " + (e.message || String(e)));
    }
  };
  var handleDelete = async function (id) {
    try {
      await persistDeleteEmb(id);
      setToast(t.deleted);
      setShowForm(false); setEditing(null);
    } catch (e) {
      setToast("⚠ " + (e.message || String(e)));
    }
  };

  /* Per-proforma summary helpers used by the flat row renderer. */
  function proProductSummary(pro) {
    var seen = {}; var bits = [];
    (pro.lines || []).forEach(function (l) {
      var n = l.productName || l.productBase || "";
      if (n && !seen[n]) { seen[n] = true; bits.push(n); }
    });
    if (bits.length === 0) return "—";
    return bits.slice(0, 3).join(" · ") + (bits.length > 3 ? " +" + (bits.length - 3) : "");
  }
  function proTotalPallets(pro) { return (pro.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0); }
  function proTotalCases(pro)   { return (pro.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0); }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, height: "100%" }}>
      {/* HEADER ROW — compact title + ⋮ filters + + New on the right.
          Status counts go into the tabs row below, replacing the old
          summary cards. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 24, flexWrap: "wrap" }}>
        <div style={{ lineHeight: 1.1 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{t.title}</h2>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
          {shipper && <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 2, fontFamily: "'JetBrains Mono', monospace" }}>{t.loggedAs}: <strong>{shipper.shortName || shipper.fullName || "—"}</strong> · {shipper.initials || "??"} · {seasonLabel(activeSeason())}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }} ref={filtersRef}>
          {/* Bulk download all valid BOLs from the currently
              filtered table. Walks filteredRows (already respects
              status/cert/season/search), runs the same readiness +
              validator gate. Refuses if rows span multiple shippers
              (the user is told to filter to one). Names the ZIP
              after the invoice when only one is present, otherwise
              uses date + count. */}
          {(function () {
            var allReady = [];
            filteredRows.forEach(function (row) {
              if (!proformaReadyToPrint(row.embarque, row.pro)) return;
              var v = validateProforma(row.embarque, row.pro, lang);
              var errs = (v.blocking || []).concat(v.red || []);
              if (errs.length > 0) return;
              allReady.push(row);
            });
            var totalRows = filteredRows.length;
            var canExport = allReady.length > 0;
            /* Detect distinct shippers in the ready set. */
            var shipperIds = {};
            allReady.forEach(function (row) {
              var sid = String(row.embarque.supplierId || "");
              if (sid) shipperIds[sid] = true;
            });
            var distinctShippers = Object.keys(shipperIds).length;
            /* Detect distinct invoices for ZIP naming. */
            var invoices = {};
            allReady.forEach(function (row) {
              var inv = String(row.embarque.shipperInvoice || "").trim();
              if (inv) invoices[inv] = true;
            });
            var distinctInvoices = Object.keys(invoices);
            var label = lang === "es"
              ? "Descargar BOLs (" + allReady.length + "/" + totalRows + ")"
              : "Download BOLs (" + allReady.length + "/" + totalRows + ")";
            var title = canExport
              ? (lang === "es"
                  ? "Descarga " + allReady.length + " BOL(s) en un ZIP. Las proformas con errores se omiten."
                  : "Downloads " + allReady.length + " BOL(s) in a ZIP. Proformas with errors are skipped.")
              : (lang === "es"
                  ? "Ningún BOL listo para descargar en los embarques filtrados."
                  : "No BOLs ready to download in filtered shipments.");
            var doIt = async function () {
              if (!canExport) { alert(title); return; }
              if (distinctShippers > 1) {
                alert(lang === "es"
                  ? "Los BOLs filtrados son de varios shippers. Filtra por un solo shipper antes de descargar."
                  : "Filtered BOLs span multiple shippers. Filter to a single shipper before downloading.");
                return;
              }
              var ctx = {
                suppliers: loadLS("gfi-suppliers", []),
                warehouses: getWarehouses(),
                brokers: getBrokers(),
                commodities: loadLS("gfi-commodities", []),
                seasons: getSeasons(),
                lang,
              };
              /* ZIP naming: one invoice → BOLs_<invoice>.zip.
                 Multiple invoices same shipper → BOLs_<YYYY-MM-DD>_<N>BOLs.zip */
              var zipName;
              if (distinctInvoices.length === 1) {
                zipName = "BOLs_" + distinctInvoices[0];
              } else {
                var d = new Date();
                var ymd = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
                zipName = "BOLs_" + ymd + "_" + allReady.length + "BOLs";
              }
              try {
                await downloadBolsAsZip(allReady, ctx, zipName);
              } catch (err) {
                alert((lang === "es" ? "Error al generar ZIP: " : "Error generating ZIP: ") + (err && err.message ? err.message : err));
              }
            };
            return (
              <button
                type="button"
                onClick={doIt}
                disabled={!canExport}
                title={title}
                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid " + (canExport ? "#2C3E50" : "var(--color-border)"), background: canExport ? "rgba(44,62,80,0.06)" : "var(--color-bg)", color: canExport ? "#2C3E50" : "var(--color-text-muted)", cursor: canExport ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700, opacity: canExport ? 1 : 0.55, display: "flex", alignItems: "center", gap: 4 }}
              >{"📦 "}{label}</button>
            );
          })()}
          <button
            type="button"
            onClick={function () { setShowFilters(function (v) { return !v; }); }}
            style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#ffffff", color: "var(--color-text)", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}
            title={lang === "es" ? "Filtros" : "Filters"}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>⋮</span>
            <span>{lang === "es" ? "Filtros" : "Filters"}</span>
            {activeFilterCount > 0 && (
              <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "var(--color-accent-text)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", marginLeft: 2 }}>{activeFilterCount}</span>
            )}
          </button>
          {/* Email BOL — driven by the table selection. The shipper
              checks 1+ proformas (all from ONE invoice) and clicks here;
              startEmailBol validates and opens the send dialog directly,
              no picker. */}
          <button
            onClick={startEmailBol}
            style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #1F4E79", background: "rgba(31,78,121,0.06)", color: "#1F4E79", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}
            title={t.emailBol}
          >{"📧 "}{t.emailBol}</button>
          <button
            onClick={openTrackingFromSelection}
            style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #1B3A4B", background: "rgba(27,58,75,0.06)", color: "#1B3A4B", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}
            title={t.sendTrackingLink}
          >{"📡 "}{t.sendTrackingLink}</button>
          <button onClick={newEmbarque} style={{ padding: "5px 12px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>+ {t.newSubmission}</button>
          {/* FILTER POPOVER — absolute under the trigger row. */}
          {showFilters && (
            <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, zIndex: 50, width: 560, maxWidth: "92vw", background: "#fff", border: "1px solid var(--color-border)", borderRadius: 10, boxShadow: "0 12px 36px rgba(0,0,0,0.18)", padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{lang === "es" ? "Filtros" : "Filters"}{activeFilterCount > 0 ? " (" + activeFilterCount + ")" : ""}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {activeFilterCount > 0 && (
                    <button type="button" onClick={function () { setFilterCert(""); setFilterSeasons([]); }} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#fff", color: "var(--color-text)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Limpiar todo" : "Clear all"}</button>
                  )}
                  <button type="button" onClick={function () { setShowFilters(false); }} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#fff", color: "var(--color-text)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Cerrar" : "Close"}</button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Certificado" : "Certificate"}</div>
                  <SearchSelect value={filterCert} onChange={setFilterCert} placeholder={t.allCerts} limit={20} options={certFilterOptions} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Temporada" : "Season"}</div>
                  <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SEARCH — always visible below header. */}
      <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
        <input
          value={search}
          onChange={function (e) { setSearch(e.target.value); }}
          placeholder={t.search}
          style={{ ...inp, maxWidth: 420, width: "100%", padding: "5px 8px", fontSize: 12 }}
        />
        {search && (
          <button type="button" onClick={function () { setSearch(""); }} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }} title={lang === "es" ? "Limpiar búsqueda" : "Clear search"}>×</button>
        )}
      </div>

      {/* TABS — All / Drafts / Submitted / Accepted / Rejected with
          per-proforma counts (mirrors the old summary cards). */}
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid var(--color-border)", marginBottom: 6, flexWrap: "wrap" }}>
        {[
          ["", lang === "es" ? "Todas" : "All", (counts.draft || 0) + (counts.submitted || 0) + (counts.accepted || 0) + (counts.rejected || 0), null],
          ["draft", t.cardDraft, counts.draft || 0, STATUS_COLORS.draft.text],
          ["submitted", t.cardSubmitted, counts.submitted || 0, STATUS_COLORS.submitted.text],
          ["accepted", t.cardAccepted, counts.accepted || 0, STATUS_COLORS.accepted.text],
          ["rejected", t.cardRejected, counts.rejected || 0, STATUS_COLORS.rejected.text],
        ].map(function (tab) {
          var key = tab[0], label = tab[1], count = tab[2], activeColor = tab[3];
          var isActive = filterStatus === key;
          var hl = activeColor || "var(--color-accent)";
          return (
            <button
              key={key || "all"}
              type="button"
              onClick={function () { setFilterStatus(key); }}
              style={{
                padding: "6px 14px",
                background: "transparent",
                border: "none",
                borderBottom: isActive ? "2px solid " + hl : "2px solid transparent",
                marginBottom: -2,
                color: isActive ? hl : "var(--color-text-secondary)",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {myEmbarques.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14, border: "1px solid var(--color-border)", borderRadius: 10 }}>{t.noResults}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
        {(function () {
          /* Sticky-header table consistent with BillRegistry / InvoiceRegistry:
             clickable column headers with up/down arrows, mono fonts for
             ids/dates/numbers, row hover, click row opens the embarque
             modal pre-focused on the right proforma tab. Now with a
             leading checkbox column + bulk print toolbar mirroring
             ShipmentRegistry's selection pattern. */
          var thBase = { padding: "6px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em", background: "var(--color-bg)", position: "sticky", top: 0, zIndex: 1, cursor: "pointer", whiteSpace: "nowrap", borderBottom: "2px solid var(--color-border)" };
          var thRight = { ...thBase, textAlign: "right" };
          var thCenter = { ...thBase, textAlign: "center" };
          /* Selection helpers — keyed by "embarqueId::proformaId" to
             stay unique across embarques. */
          var visibleKeys = sortedRows.map(function (r) { return r.embarque.id + "::" + r.pro.id; });
          var selKeys = visibleKeys.filter(function (k) { return !!selected[k]; });
          var allVisibleSelected = visibleKeys.length > 0 && selKeys.length === visibleKeys.length;
          var someVisibleSelected = selKeys.length > 0 && !allVisibleSelected;
          var toggleAllVisible = function () {
            setSelected(function (prev) {
              var next = { ...prev };
              if (allVisibleSelected) visibleKeys.forEach(function (k) { delete next[k]; });
              else visibleKeys.forEach(function (k) { next[k] = true; });
              return next;
            });
          };
          var toggleOne = function (key) {
            setSelected(function (prev) {
              var next = { ...prev };
              if (next[key]) delete next[key]; else next[key] = true;
              return next;
            });
          };
          var clearSelection = function () { setSelected({}); };
          /* Bulk print — builds a synthetic shipment per selected row
             and hands them all to printShipmentDocs in one call, so
             the doc viewer renders them as one multi-page document
             (page-break between shipments). Skips rows whose proforma
             doesn't have enough data per proformaReadyToPrint. */
          var doBulkPrint = function (kind) {
            var synths = [];
            var blockers = [];
            sortedRows.forEach(function (r) {
              var key = r.embarque.id + "::" + r.pro.id;
              if (!selected[key]) return;
              if (!proformaReadyToPrint(r.embarque, r.pro)) return;
              /* Print-time gate per selected proforma. If any has
                 active L1+L2 errors, list them with PRO# so the
                 shipper can find the offender quickly. */
              var v = validateProforma(r.embarque, r.pro, lang);
              var errs = (v.blocking || []).concat(v.red || []);
              if (errs.length > 0) {
                blockers.push("PRO " + (r.pro.consec || "?") + ": " + errs.join(", "));
                return;
              }
              synths.push(buildSyntheticShipment(r.embarque, r.pro));
            });
            if (blockers.length > 0) {
              var head = lang === "es" ? "No se puede imprimir — proformas con errores:" : "Cannot print — proformas with errors:";
              alert(head + "\n\n" + blockers.map(function (b) { return "• " + b; }).join("\n"));
              return;
            }
            if (synths.length === 0) { alert(t.bulkPrintNone); return; }
            var ctx = {
              suppliers: loadLS("gfi-suppliers", []),
              warehouses: getWarehouses(),
              brokers: getBrokers(),
              commodities: loadLS("gfi-commodities", []),
              seasons: getSeasons(),
              lang,
            };
            printShipmentDocs(synths, kind, ctx, function (err) {
              if (err === "popup-blocked") alert(t.printPopupBlocked);
            });
          };
          return (
            <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
              {/* Bulk action bar — shown only when at least one row
                  is selected. Mirrors ShipmentRegistry's toolbar:
                  count badge + per-doc-type print buttons + Clear. */}
              {selKeys.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 8, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", marginBottom: 6, fontSize: 12, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selKeys.length + " " + t.selected}</span>
                  <span style={{ color: "var(--color-text-muted)" }}>|</span>
                  <button onClick={function () { doBulkPrint("carta-porte"); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #2C3E50", background: "rgba(44,62,80,0.06)", color: "#2C3E50", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"🖨 "}{t.printCartaPorte}</button>
                  <button onClick={clearSelection} style={{ marginLeft: "auto", padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.clearSel}</button>
                </div>
              )}
            <div style={{ overflowX: "auto", overflowY: "auto", flex: "1 1 auto", minHeight: 0, borderRadius: 10, border: "1px solid var(--color-border)" }}>
              <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                <thead><tr>
                  {/* Header checkbox: select-all-visible. The
                      indeterminate state reflects "some but not all"
                      via a ref since native checkbox has no prop. */}
                  <th style={{ ...thCenter, cursor: "default", width: 36 }}>
                    <input type="checkbox" checked={allVisibleSelected} ref={function (el) { if (el) el.indeterminate = someVisibleSelected; }} onChange={toggleAllVisible} style={{ accentColor: "var(--color-accent)", cursor: "pointer", width: 15, height: 15 }} />
                  </th>
                  <th onClick={function () { toggleSort("shipperInvoice"); }} style={thBase}>{t.colInvoice}{arrow("shipperInvoice")}</th>
                  <th style={{ ...thCenter, cursor: "default", width: 44 }} title={lang === "es" ? "BOL al aduanal" : "BOL to broker"}>BOL</th>
                  {/* 2026-10-05 — "Embarque" column (consecEmbarque)
                     removed — Arturo: "esa columna solo la ven ellos,
                     yo no la veo y creo que solo los confundiria... si
                     no tiene ningun uso ocultala y si no rompe nada
                     elimina eso". Confirmed safe to remove from the
                     UI: the shipperInvoice (now the only ID column)
                     already uniquely identifies each row, and for
                     every embarque GFI itself creates this value is
                     literally always "TA-E001" (the counter that
                     advances it is never incremented on that path —
                     a separate, pre-existing, lower-priority issue,
                     not touched here) — so it carried no real
                     distinguishing information anyway. The underlying
                     field/column (consecEmbarque) and its counter
                     logic (nextEmbConsec/commitEmbConsec) are left
                     completely untouched — they still matter for
                     numbering a shipper's OWN new submissions, this
                     only removes the 3 places that merely displayed
                     it to the shipper with no functional role
                     (this table column, the email-picker's embarque
                     list, and the embarque detail modal's title). */}
                  <th onClick={function () { toggleSort("proforma"); }} style={thBase}>{t.colProforma}{arrow("proforma")}</th>
                  <th onClick={function () { toggleSort("dateDeparture"); }} style={thBase}>{t.colDeparture}{arrow("dateDeparture")}</th>
                  <th onClick={function () { toggleSort("dateArrival"); }} style={thBase}>{t.colEta}{arrow("dateArrival")}</th>
                  <th onClick={function () { toggleSort("week"); }} style={thCenter}>{t.weekShort}{arrow("week")}</th>
                  <th onClick={function () { toggleSort("warehouse"); }} style={thBase}>{t.colWarehouse}{arrow("warehouse")}</th>
                  <th style={{ ...thBase, cursor: "default" }}>{t.colProducts}</th>
                  <th onClick={function () { toggleSort("pallets"); }} style={thRight}>{t.colPallets}{arrow("pallets")}</th>
                  <th onClick={function () { toggleSort("cases"); }} style={thRight}>{t.colCases}{arrow("cases")}</th>
                  <th onClick={function () { toggleSort("status"); }} style={thCenter}>{t.colStatus}{arrow("status")}</th>
                  {/* FIX 43 — Export Cert # column. Aggregates the
                      distinct cert numbers referenced by any line on
                      this proforma (export cert id resolved to its
                      number, or the manual override). */}
                  <th style={{ ...thBase, cursor: "default" }}>{t.exportCert}</th>
                  <th onClick={function () { toggleSort("gfiLot"); }} style={thCenter}>{t.colGfiLot}{arrow("gfiLot")}</th>
                  <th style={{ ...thCenter, cursor: "default" }}>{t.colEntry}</th>
                </tr></thead>
                <tbody>
                  {sortedRows.length === 0 ? (
                    <tr><td colSpan={13} style={{ padding: "30px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 12, fontStyle: "italic" }}>{t.noResultsFiltered}</td></tr>
                  ) : (function () {
                    /* Build a one-shot lookup map from proforma →
                       shipment so the Entry # column can read the
                       saved entryNumber from gfi-shipments without
                       N queries. The proforma carries gfiShipmentId
                       once it's been accepted into a real shipment. */
                    var shipmentsById = {};
                    loadLS("gfi-shipments", []).forEach(function (s) { if (s && s.id != null) shipmentsById[String(s.id)] = s; });
                    return sortedRows.map(function (r) {
                    var emb = r.embarque, p = r.pro;
                    var st = p.status || "draft";
                    var pallets = proTotalPallets(p);
                    var cases = proTotalCases(p);
                    var rowKey = emb.id + "::" + p.id;
                    var isSel = !!selected[rowKey];
                    var bolState = bolRowState(emb);
                    var linkedShipment = p.gfiShipmentId ? shipmentsById[String(p.gfiShipmentId)] : null;
                    var entryNum = linkedShipment ? (linkedShipment.entryNumber || "") : "";
                    return (
                      <tr key={rowKey} onClick={function () { openEmbarque(emb, p.id); }} style={{ cursor: "pointer", borderBottom: "1px solid var(--color-border)", background: isSel ? "rgba(42,122,111,0.06)" : (bolState === "pending" ? "rgba(220,38,38,0.07)" : "transparent") }} onMouseEnter={function (e) { if (!isSel) e.currentTarget.style.background = "var(--color-bg-hover, rgba(0,0,0,0.02))"; }} onMouseLeave={function (e) { if (!isSel) e.currentTarget.style.background = (bolState === "pending" ? "rgba(220,38,38,0.07)" : "transparent"); }}>
                        {/* Checkbox cell — stopPropagation so toggling
                            doesn't also open the embarque modal. */}
                        <td style={{ padding: "6px 8px", textAlign: "center" }} onClick={function (e) { e.stopPropagation(); }}>
                          <input type="checkbox" checked={isSel} onChange={function () { toggleOne(rowKey); }} style={{ accentColor: "var(--color-accent)", cursor: "pointer", width: 15, height: 15 }} />
                        </td>
                        {/* Invoice cell — single line only (number or
                            INCOMPLETE marker). Embarque consec moved
                            to its own column; missing-invoice flag
                            moved into the Status cell as a red pill. */}
                        <td style={{ padding: "6px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: emb.shipperInvoice ? "var(--color-text)" : "#DC2626", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {emb.shipperInvoice || "(" + t.incomplete + ")"}
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }} title={bolState === "sent" ? ((lang === "es" ? "BOL enviado al aduanal" : "BOL sent to broker") + (emb.bolSentAt ? " — " + new Date(emb.bolSentAt).toLocaleString(lang === "es" ? "es-MX" : "en-US") : "")) : (bolState === "pending" ? (lang === "es" ? "BOL pendiente de enviar" : "BOL not sent yet") : "")}>
                          {bolState === "sent"
                            ? <span><span style={{ fontSize: 13 }}>{"🛃"}</span><span style={{ color: "#059669", fontWeight: 700, marginLeft: 1 }}>{"✓"}</span></span>
                            : bolState === "pending"
                              ? <span style={{ fontSize: 12, color: "#DC2626", fontWeight: 700 }}>{"●"}</span>
                              : <span style={{ color: "var(--color-text-muted)" }}>{"—"}</span>}
                        </td>
                        <td style={{ padding: "6px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: "var(--color-accent)" }}>{p.consec || "—"}</td>
                        <td style={{ padding: "6px 8px", fontSize: 11, color: "var(--color-text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>{p.dateDeparture ? new Date(p.dateDeparture + "T00:00:00").toLocaleDateString("en-US") : "—"}</td>
                        <td style={{ padding: "6px 8px", fontSize: 11, color: "var(--color-text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>{p.dateArrival ? new Date(p.dateArrival + "T00:00:00").toLocaleDateString("en-US") : "—"}</td>
                        <td style={{ padding: "6px 8px", fontSize: 11, textAlign: "center", color: "var(--color-text-secondary)", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{p.dateArrival ? t.weekShort + getISOWeekNum(p.dateArrival) : "—"}</td>
                        <td style={{ padding: "6px 8px", fontSize: 12 }}>{p.warehouseName || "—"}</td>
                        <td style={{ padding: "6px 8px", fontSize: 12 }}>{proProductSummary(p)}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600 }}>{pallets || "—"}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600 }}>{cases || "—"}</td>
                        {/* Status cell — primary badge plus a compact
                            red pill when the shipper invoice file
                            hasn't been uploaded yet. */}
                        <td style={{ padding: "6px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                          <StatusBadge status={st} t={t} />
                          {/* 2026-09-14 — Arturo: an embarque he captured
                              directly in Ground-Fresh (never uploaded
                              through this portal's own invoiceFile slot —
                              docs don't propagate that direction, by
                              design) was showing "Falta factura" even
                              though he already has the real invoice on
                              file on his side — "para yo poderla subir es
                              porque ellos mismos me la enviaron por
                              email". Once a proforma is accepted there's
                              nothing left to submit and no action the
                              missing-upload warning could still be
                              nagging about, whether it got there via the
                              portal or via GFI directly — so the pill
                              only makes sense pre-acceptance. */}
                          {!emb.invoiceFile && st !== "accepted" && (
                            <span style={{ marginLeft: 4, padding: "1px 6px", borderRadius: 8, background: "rgba(220,38,38,0.1)", color: "#B91C1C", fontSize: 9, fontWeight: 700, border: "1px solid rgba(220,38,38,0.3)" }}>{t.missingDocs}</span>
                          )}
                        </td>
                        {/* FIX 43 — Export Cert # cell: dedupe cert
                            numbers across the proforma's lines so the
                            user sees one entry per cert. Manual
                            entries land in exportCertManual. */}
                        <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{(function () {
                          var nums = [];
                          (p.lines || []).forEach(function (l) {
                            var n = l && (l.exportCertNumber || l.exportCertManual);
                            if (n && nums.indexOf(n) < 0) nums.push(n);
                          });
                          return nums.length ? nums.join(", ") : "—";
                        })()}</td>
                        <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: p.gfiLot ? "var(--color-accent)" : "var(--color-text-muted)" }}>{p.gfiLot ? "LOT " + p.gfiLot : "—"}</td>
                        <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600, color: entryNum ? "var(--color-text)" : "var(--color-text-muted)" }}>{entryNum || "—"}</td>
                      </tr>
                    );
                  });
                  })()}
                </tbody>
              </table>
            </div>
            </div>
          );
        })()}
        </div>
      )}

      {showForm && editing && <EmbarqueModal
        embarque={editing}
        allEmbarques={embarques}
        shipper={shipper}
        lang={lang}
        initialProformaId={initialProformaId}
        growers={growers}
        shipperMode={!!lockSupplierId}
        onSave={handleSave}
        onSubmitProforma={handleSubmitProforma}
        onDelete={handleDelete}
        onCancel={function () { setShowForm(false); setEditing(null); setInitialProformaId(null); if (openedFromExternalRef.current) { openedFromExternalRef.current = false; window.dispatchEvent(new CustomEvent("gfi-shipment-closed")); } }}
      />}
      <Toast msg={toast} onDismiss={function () { setToast(null); }} />
      {/* Email-BOL picker — modal list of the shipper's embarques.
          Sorted by the first proforma's departure date desc so the
          most recent shipments appear first. Click a row → close the
          picker and set emailingEmbarque, which mounts the EmailDialog
          below. */}
      {showEmailPicker && (function () {
        var ordered = myEmbarques.slice().sort(function (a, b) {
          var ad = (a.proformas && a.proformas[0] && a.proformas[0].dateDeparture) || "";
          var bd = (b.proformas && b.proformas[0] && b.proformas[0].dateDeparture) || "";
          if (ad === bd) return 0;
          return ad < bd ? 1 : -1;
        });
        return (
          <DraggableModal title={t.emailBolPickerTitle} onClose={function () { setShowEmailPicker(false); }} initialWidth={680} initialHeight={Math.min(560, window.innerHeight - 60)} zIndex={10003}>
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
              {ordered.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 10px", color: "var(--color-text-muted)", fontSize: 12, fontStyle: "italic" }}>{t.emailBolPickerEmpty}</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {ordered.map(function (emb) {
                    var pros = emb.proformas || [];
                    var consecs = pros.map(function (p) { return p.consec || "—"; }).join(", ") || "—";
                    var firstDep = pros[0] && pros[0].dateDeparture ? new Date(pros[0].dateDeparture + "T00:00:00").toLocaleDateString("en-US") : "—";
                    return (
                      <button
                        key={emb.id}
                        type="button"
                        onClick={function () { setEmailingEmbarque(emb); setShowEmailPicker(false); }}
                        style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 8, background: "var(--color-bg-card)", color: "var(--color-text)", cursor: "pointer", fontSize: 12 }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{emb.shipperInvoice || ("(" + t.incomplete + ")")}</span>
                          <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{firstDep}</span>
                        </div>
                        <div style={{ marginTop: 4, fontSize: 11, color: "var(--color-text-secondary)" }}>
                          <span style={{ color: "var(--color-text-muted)" }}>{t.colProforma}: </span>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)", fontWeight: 700 }}>{consecs}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: "8px 14px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", background: "var(--color-bg)" }}>
              <button onClick={function () { setShowEmailPicker(false); }} style={{ padding: "6px 14px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.emailBolPickerCancel}</button>
            </div>
          </DraggableModal>
        );
      })()}
      {/* Email-BOL dialog — opens AFTER the shipper picks an embarque
          from the picker above. Generates one BOL PDF per proforma in
          the embarque (so a shipper-invoice with 3 proformas results
          in 3 attachments). Recipients are derived from the embarque's
          first proforma (the MX broker is selected per proforma but
          typically the same across the embarque). */}
      {emailingEmbarque && (function () {
        var emb = emailingEmbarque;
        var allPros = (emb.proformas || []).filter(function (p) { return proformaReadyToPrint(emb, p); });
        if (allPros.length === 0) {
          /* Defer the alert + close to a microtask so we don't call
             setState during render. */
          Promise.resolve().then(function () {
            alert(t.emailBolNoReady);
            setEmailingEmbarque(null);
          });
          return null;
        }
        var ctx = {
          suppliers: loadLS("gfi-suppliers", []),
          warehouses: getWarehouses(),
          brokers: getBrokers(),
          commodities: loadLS("gfi-commodities", []),
          seasons: getSeasons(),
          lang: lang,
        };
        /* Recipients use the first ready proforma (MX broker on most
           embarques is shared across proformas; the picker is per
           embarque so we can't show one MX broker per proforma here). */
        var recipients = buildBolRecipients(emb, allPros[0], shipper, lang, t);
        /* One doc option per proforma — checked by default so all
           BOLs are attached. The shipper can uncheck any. Plus two
           optional embarque-level attachments (shipper invoice +
           export certificate) when present on the embarque record;
           both default to checked so AGROCAP / GFI staff receive
           the full bundle without extra clicks. */
        var docOptions = allPros.map(function (p) {
          var pPallets = (p.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0);
          var pCases = (p.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0);
          var pSummary = proProductSummary(p);
          return {
            key: "carta-porte::" + p.id,
            label: t.emailBolDocLabel + " " + (p.consec || "") + " · " + pSummary + " · " + pPallets + " " + t.emailBolPallets + ", " + pCases + " " + t.emailBolCases,
            checked: true,
          };
        });
        if (emb.invoiceFile && (emb.invoiceFile.url || emb.invoiceFile.data)) {
          docOptions.push({ key: "shipper-invoice", label: (lang === "es" ? "Factura del Exportador" : "Shipper Invoice") + " — " + (emb.invoiceFile.name || "invoice.pdf"), checked: true });
        }
        if (emb.exportCertFile && (emb.exportCertFile.url || emb.exportCertFile.data)) {
          docOptions.push({ key: "export-cert", label: (lang === "es" ? "Certificado de Exportación" : "Export Certificate") + " — " + (emb.exportCertFile.name || "cert.pdf"), checked: true });
        }
        (emb.otherDocs || []).forEach(function (d) {
          if (d && (d.url || d.data)) docOptions.push({ key: "otherdoc::emb::" + (d.id || d.name), label: (lang === "es" ? "Documento" : "Document") + " — " + (d.name || "doc"), checked: true });
        });
        (emb.proformas || []).forEach(function (pp) {
          (pp.otherDocs || []).forEach(function (d) {
            if (d && (d.url || d.data)) docOptions.push({ key: "otherdoc::" + pp.id + "::" + (d.id || d.name), label: (lang === "es" ? "Documento" : "Document") + " — " + (d.name || "doc"), checked: true });
          });
        });
        /* Subject:
             "Embarque Ground-Fresh {shipperInvoice} MM/DD/YYYY {consec1, consec2}"
           No GFI lot — lot is assigned by GFI after acceptance and
           may not exist when the shipper sends the BOL. */
        var firstDep = "";
        for (var iD = 0; iD < allPros.length; iD++) {
          if (allPros[iD].dateDeparture) { firstDep = allPros[iD].dateDeparture; break; }
        }
        var depFmt = firstDep ? new Date(firstDep + "T00:00:00").toLocaleDateString("en-US") : "";
        var consList = allPros.map(function (p) { return p.consec || "—"; }).join(", ");
        var subject = t.emailBolSubjectShipmentPrefix
          + " " + (emb.shipperInvoice || "—")
          + (depFmt ? " " + depFmt : "")
          + " " + consList;
        /* Body (default language). Bullet line per proforma with
           a short product summary + total pallets + total cases. */
        var bodyLines = [];
        bodyLines.push(t.emailBolGreeting + " " + t.emailBolIntro + " " + (emb.shipperInvoice || "—") + ":");
        bodyLines.push("");
        allPros.forEach(function (p) {
          var pallets = (p.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0);
          var cases = (p.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0);
          var prodSummary = proProductSummary(p);
          bodyLines.push("- " + t.colProforma + " " + (p.consec || "—") + ": " + prodSummary + " · " + pallets + " " + t.emailBolPallets + ", " + cases + " " + t.emailBolCases);
        });
        bodyLines.push("");
        bodyLines.push(t.emailBolSignoff);
        var body = bodyLines.join("\n");
        /* Build the email account from the logged-in shipper user.
           The masked alias (e.g. paulogonzalez.gomez_berries@gfinexus.com)
           is the actual From; the real personal email is the Reply-To.
           This replaces the old "first contact on the supplier record"
           approach, which was a Gmail-era hack (Gmail sent as the admin
           OAuth account, fromEmail was cosmetic). With Resend, the
           From line must be an @gfinexus.com address that belongs to
           the actual sender. */
        var shipperEmailAccount = profile ? {
          id: profile.id || "logged-in-shipper",
          name: ((profile.first_name || "") + " " + (profile.last_name || "")).trim() || profile.username || "Shipper",
          email: profile.email || "",
          maskedFrom: profile.masked_email || "",
        } : null;
        var shipperEmail = shipperEmailAccount ? shipperEmailAccount.email : "";
        var shipperEmailAccounts = shipperEmailAccount ? [shipperEmailAccount] : [];
        var company = loadCompanyProfile();
        /* Shipper's own signature: logged-in user name + shipper logo.
           Never uses Ground-Fresh / SweetLeaf branding. */
        var shipperSigName = ((profile && ((profile.first_name || "") + " " + (profile.last_name || "")).trim()) || (profile && profile.username) || (shipper && (shipper.shortName || shipper.fullName)) || "");
        var shipperSigCompany = (shipper && (shipper.fullName || shipper.shortName)) || "";
        var shipperLogo = (shipper && (shipper.logoUrl || (shipper.extra && shipper.extra.logoUrl))) || "";
        var shipperSignatureHtml = '<div style="font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#333;margin-top:12px">'
          + (shipperLogo ? '<img src="' + shipperLogo + '" alt="" style="max-height:60px;max-width:200px;display:block;margin-bottom:6px" />' : '')
          + (shipperSigName ? '<div style="font-weight:700">' + shipperSigName + '</div>' : '')
          + (shipperSigCompany ? '<div style="color:#666">' + shipperSigCompany + '</div>' : '')
          + '</div>';
        var shipperSignatures = shipperSigName || shipperLogo ? [{ name: shipperSigName || shipperSigCompany || "Shipper", html: shipperSignatureHtml }] : [];
        /* buildAttachments: turn each checked doc key back into the
           matching proforma, build a synthetic shipment, and produce
           one PDF per proforma. */
        var buildAttachments = async function (checkedKeys) {
          var out = [];
          for (var i = 0; i < checkedKeys.length; i++) {
            var key = checkedKeys[i];
            if (key.indexOf("carta-porte::") === 0) {
              var proId = key.replace(/^carta-porte::/, "");
              var pro = allPros.find(function (p) { return String(p.id) === String(proId); });
              if (!pro) continue;
              var synth = buildSyntheticShipment(emb, pro);
              var att = await buildShipmentDocPdf(synth, "carta-porte", ctx);
              out.push(att);
            } else if (key === "shipper-invoice" && emb.invoiceFile) {
              var src = emb.invoiceFile.url || emb.invoiceFile.data || "";
              if (src) {
                try {
                  var resp = await fetch(src);
                  var blob = await resp.blob();
                  out.push({ filename: emb.invoiceFile.name || "shipper-invoice.pdf", mimeType: blob.type || "application/pdf", blob: blob });
                } catch (e) { console.warn("[email-bol] failed to fetch invoiceFile:", e); }
              }
            } else if (key === "export-cert" && emb.exportCertFile) {
              var certSrc = emb.exportCertFile.url || emb.exportCertFile.data || "";
              if (certSrc) {
                try {
                  var certResp = await fetch(certSrc);
                  var certBlob = await certResp.blob();
                  out.push({ filename: emb.exportCertFile.name || "export-certificate.pdf", mimeType: certBlob.type || "application/pdf", blob: certBlob });
                } catch (e) { console.warn("[email-bol] failed to fetch exportCertFile:", e); }
              }
            } else if (key.indexOf("otherdoc::") === 0) {
              var allDocs = [];
              (emb.otherDocs || []).forEach(function (d) { allDocs.push({ k: "otherdoc::emb::" + (d.id || d.name), d: d }); });
              (emb.proformas || []).forEach(function (pp) {
                (pp.otherDocs || []).forEach(function (d) { allDocs.push({ k: "otherdoc::" + pp.id + "::" + (d.id || d.name), d: d }); });
              });
              var hit = allDocs.find(function (x) { return x.k === key; });
              if (hit && hit.d) {
                var docSrc = hit.d.url || hit.d.data || "";
                if (docSrc) {
                  try {
                    var docResp = await fetch(docSrc);
                    var docBlob = await docResp.blob();
                    out.push({ filename: hit.d.name || "document", mimeType: docBlob.type || hit.d.type || "application/octet-stream", blob: docBlob });
                  } catch (e) { console.warn("[email-bol] failed to fetch otherDoc:", e); }
                }
              }
            }
          }
          return out;
        };
        return (
          <EmailDialog
            lang={lang}
            title={t.emailBolTitle}
            recipients={recipients}
            docOptions={docOptions}
            defaultSubject={subject}
            defaultBody={body}
            fromEmail={shipperEmail}
            emailAccounts={shipperEmailAccounts}
            signatures={shipperSignatures}
            buildAttachments={buildAttachments}
            onClose={function () { setEmailingEmbarque(null); }}
            onSent={async function () {
              /* Record the send on the embarque so the list can mark it
                 (green customs icon, Bloque 2) and the detail can show
                 recipients + timestamp. Plain short strings only (no
                 base64) so stripBlobs leaves them; lands in
                 shipper_shipments.extra automatically. */
              try {
                var updated = Object.assign({}, emb, {
                  bolSentAt: new Date().toISOString(),
                  bolSentTo: recipients.map(function (rr) { return { name: rr.name || "", email: rr.email || "" }; }),
                });
                await persistSingle(updated);
              } catch (e) { console.error("[email-bol] could not record send:", e && e.message); }
              setToast(lang === "es" ? "BOL enviado" : "BOL sent");
              setEmailingEmbarque(null);
            }}
          />
        );
      })()}
      {listTrackingOpen && (
        <TrackingLinkModal
          lang={lang}
          payload={listTrackingOpen}
          onClose={function () { setListTrackingOpen(null); }}
        />
      )}
    </div>
  );
}
